import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Timestamp } from 'firebase/firestore';
import styled, { css } from 'styled-components';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Cropper from 'react-easy-crop';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import Header from '../components/Header';
import { useGlobalAlarmService } from '../hooks/useGlobalAlarmService';
import { User, Class, ClassMember } from '../types/alarm';
import { userDataService } from '../services/userDataService';
import CachedProfileImage from '../components/CachedProfileImage';
import { chatQuizService } from '../services/chatQuizService';
import { ChatQuiz, StudentQuizResponse, ConnectedStudentInfo, ChatQuizSet } from '../types/chatQuiz';
import { storage } from '../firebase/config';
import ConfirmModal from '../components/ConfirmModal';
import { checkChatAccess, checkQuizSetLimit, checkQuizCardLimit } from '../utils/licenseLimits';
import LicenseLimitModal from '../components/LicenseLimitModal';
import { StorageQuotaExceededError, userStorageQuotaService } from '../services/userStorageQuotaService';
import {
  cropToWebpBlob,
  IMAGE_WEBP_EXT,
  IMAGE_WEBP_MIME,
  readFileAsDataURL
} from '../utils/imageCompress';
import { buildQuizQuestionImagePath } from '../utils/userStoragePaths';
import {
  MemberProfileDisplayMode,
  readMemberProfileDisplayMode,
  resolveMemberProfileSource
} from '../utils/memberProfileDisplay';

// 레벨 색상 정의 (레벨1~레벨8)
const levelColors = [
  '#FC5555', // 레벨1
  '#FF9933', // 레벨2
  '#669900', // 레벨3
  '#119473', // 레벨4
  '#0066CC', // 레벨5
  '#9900CC', // 레벨6
  '#D60093', // 레벨7
  '#920049', // 레벨8
];

// Hex 색상을 rgba로 변환 (alpha: 0.2)
const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

type CropAreaPixels = { x: number; y: number; width: number; height: number };

const MAX_COORD_QUIZ_MULTI_RESPONSES = 5;

function listSubmittedQuizResponses(
  raw: StudentQuizResponse | StudentQuizResponse[] | undefined
): StudentQuizResponse[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.filter((r): r is StudentQuizResponse => !!r && !!r.submittedAt);
  }
  return raw.submittedAt ? [raw] : [];
}

function responseAnswerHasContent(resp: StudentQuizResponse): boolean {
  const a = resp.answer;
  if (a === undefined || a === null) return false;
  if (typeof a === 'string') return a.trim().length > 0;
  if (Array.isArray(a)) return a.length > 0;
  return true;
}

function hasNoStoredQuizResponse(
  raw: StudentQuizResponse | StudentQuizResponse[] | undefined
): boolean {
  return listSubmittedQuizResponses(raw).length === 0;
}

function canMemberStillSubmitQuizResponse(
  isMultiAnswerMode: boolean,
  quizType: ChatQuiz['type'] | undefined,
  raw: StudentQuizResponse | StudentQuizResponse[] | undefined
): boolean {
  const entries = listSubmittedQuizResponses(raw);
  if (isMultiAnswerMode) {
    return entries.length < MAX_COORD_QUIZ_MULTI_RESPONSES;
  }
  if (entries.length === 0) return true;
  if (quizType === 'essay') {
    return !entries.some(responseAnswerHasContent);
  }
  return false;
}

/** 단일 퀴즈 재생 중: 제출 가능·아직 한 건도 저장된 응답이 없을 때만 … 대기 표시 */
function shouldShowMemberQuizTypingWait(args: {
  isQuizPlaying: boolean;
  isConnected: boolean;
  isStudentCreateMode: boolean;
  isSetMode: boolean;
  isMultiAnswerMode: boolean;
  activeQuizType: ChatQuiz['type'] | undefined;
  rawResponse: StudentQuizResponse | StudentQuizResponse[] | undefined;
}): boolean {
  const {
    isQuizPlaying,
    isConnected,
    isStudentCreateMode,
    isSetMode,
    isMultiAnswerMode,
    activeQuizType,
    rawResponse,
  } = args;
  if (!isQuizPlaying || !isConnected) return false;
  if (isStudentCreateMode || isSetMode) return false;
  if (!hasNoStoredQuizResponse(rawResponse)) return false;
  return canMemberStillSubmitQuizResponse(isMultiAnswerMode, activeQuizType, rawResponse);
}

interface ChatPageProps {
  user: User;
  currentClass: Class | null;
  onNavigate: (page: 'home' | 'alarm' | 'admin' | 'assignments' | 'points-table' | 'chat' | 'board' | 'license' | 'terms-of-service' | 'privacy-policy') => void;
  onLogout: () => void;
  onUpdateUser: (updatedUser: User) => void;
  onProfileModalOpen: () => void;
  onClassManageModalOpen: () => void;
  classes: Class[];
  memberGroups?: string[];
  currentMemberGroup?: string;
  onMemberGroupChange?: (group: string) => void;
  onSeatAssignmentClick?: () => void;
  onLevelSettingsClick?: () => void;
  onPointManagementClick?: () => void;
  onItemCardSettingsClick?: () => void;
  onAssignmentSettingsClick?: () => void;
  themeMode: 'dark' | 'light';
  onToggleTheme: () => void;
  onLicenseClick?: () => void; // 이용권 구매 모달 열기
  onChatPlayingChange?: (playing: boolean) => void; // 재생 중 여부 (다른 페이지 이동 차단용)
}

// 스타일 컴포넌트
const Container = styled.div`
  min-height: 100vh;
  background-color: var(--color-bg);
  padding: 36px 0 20px 0;
`;

const Content = styled.div`
  padding: 0 20px 80px 20px;
`;

// 문제 생성 UI 내부 콘텐츠 컨테이너 (카드 스타일)
const QuizEditorContent = styled.div<{ $isLocked: boolean }>`
  width: 100%;
  padding: 20px;
  background: var(--color-surface);
  border-radius: 8px;
  box-shadow: var(--modal-shadow);
  display: flex;
  flex-direction: column;
  pointer-events: ${(props) => (props.$isLocked ? 'none' : 'auto')};
  opacity: ${(props) => (props.$isLocked ? 0.9 : 1)};
  transition: opacity 0.2s ease;
  
  /* 라이트 모드: 기본 테두리, 다크 모드: 흰색 점선 테두리 */
  :root[data-theme='light'] & {
    border: 3px dashed var(--color-border);
  }
  
  :root:not([data-theme='light']) & {
    border: 3px dashed #ffffff;
  }
`;

const FirstRowContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0;
  background: transparent;
  border: none;
  gap: 12px;
  align-items: center;
`;

const FirstRowFirstCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  justify-content: center;
`;

const QuizEditorOptionRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: center;
`;

const QuestionTypeButtonsRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  width: 100%;
  
  @media (max-width: 420px) {
    gap: 6px;
  }
`;

const FirstRowSecondCol = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
  width: 100%;
`;

const FirstRowThirdCol = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-width: 0; /* flex 아이템이 계속 축소될 수 있도록 */
`;

// 재생/정지 토글 버튼
const PlayStopToggleButton = styled.button<{ $isPlaying: boolean }>`
  width: 36px;
  height: 36px;
  min-width: 36px;
  min-height: 36px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  padding: 0;
  -webkit-appearance: none;
  appearance: none;
  touch-action: manipulation;
  user-select: none;
  
  background: ${props => props.$isPlaying ? '#ff6b6b' : 'var(--color-accent-strong)'}; /* 정지 버튼은 코인 음수 색상 */
  
  &:hover {
    transform: scale(1.05);
    box-shadow: var(--modal-shadow);
  }
  
  &:active {
    transform: scale(0.95);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  @media (max-width: 768px) {
    min-width: 36px;
    min-height: 36px;
    width: 36px;
    height: 36px;
  }
`;

// 재생 삼각형 아이콘
const PlayIcon = styled.svg`
  width: 15px;
  height: 15px;
  fill: white;
  margin-left: 1.5px; /* 삼각형을 중앙으로 보정 */
`;

// 정지 네모 아이콘
const StopIcon = styled.svg`
  width: 14px;
  height: 14px;
  fill: white;
`;

// 세트 재생 순서 토글 (순차/랜덤) - 단일 퀴즈 단일/멀티 토글과 동일 스타일
const SetOrderToggleButton = styled.button<{ $isRandom: boolean }>`
  width: 36px;
  height: 36px;
  min-width: 36px;
  min-height: 36px;
  border-radius: 50%;
  border: 2px solid var(--color-border);
  background: var(--color-panel);
  color: var(--color-accent-strong);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  padding: 0;
  -webkit-appearance: none;
  appearance: none;
  touch-action: manipulation;
  user-select: none;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  :root[data-theme='dark'] & {
    color: #fff;
  }

  @media (max-width: 768px) {
    min-width: 36px;
    min-height: 36px;
    width: 36px;
    height: 36px;
  }
`;

// 문제 만들기 제출 완료 말풍선: 원형+V 아이콘 + "제출완료" (라이트=초록, 다크=흰색)
const StudentCreateSubmittedBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: var(--color-accent-strong);

  :root[data-theme='dark'] & {
    color: #fff;
  }
`;

const StudentCreateSubmittedIcon = styled.svg`
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  stroke: currentColor;
  fill: none;
  stroke-width: 2.5;
  stroke-linecap: round;
  stroke-linejoin: round;
  color: inherit;
`;

const StudentCreateSubmittedLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
`;

const QuestionTypeButton = styled.button<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  background: var(--color-surface);
  color: ${props => props.$isActive ? 'var(--color-text-primary)' : 'var(--color-text-muted)'};
  border: 1px solid var(--color-border);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  min-height: 36px;
  min-width: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  font-size: 14px;
  font-weight: ${props => props.$isActive ? '600' : '400'};
  
  &:hover {
    background-color: var(--color-panel);
    border-color: var(--color-text-muted);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  @media (max-width: 420px) {
    padding: 6px 8px;
    min-height: 32px;
    font-size: 12px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    color: #cbd5e1;
  }
`;

const BottomButtonRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
  width: 100%;
`;

const SaveButton = styled.button`
  flex: 1;
  padding: 12px 16px;
  background: var(--color-accent-strong);
  color: white;
  border: 2px solid var(--color-accent-strong);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: rgba(var(--color-accent-rgb), 0.9);
    border-color: rgba(var(--color-accent-rgb), 0.9);
  }
  
  &:disabled {
    background: var(--color-text-muted);
    border-color: var(--color-text-muted);
    cursor: not-allowed;
    opacity: 0.6;
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 12px 16px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border: 2px solid var(--color-text-muted);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: var(--color-panel);
    border-color: #6c757d;
  }
  
  &:disabled {
    background: var(--color-panel);
    border-color: var(--color-border);
    cursor: not-allowed;
    opacity: 0.6;
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const GuideMessageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const GuideMessageInput = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 12px 16px;
  font-size: 14px;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  font-family: inherit;
  resize: vertical;
  background: var(--color-category-input-bg);
  color: var(--color-text-primary);
  
  &:focus {
    outline: none;
    border-color: var(--color-accent-strong);
  }
  
  &::placeholder {
    color: var(--color-text-muted);
  }

  &:disabled {
    background: var(--color-panel);
    cursor: not-allowed;
  }
`;

const ChoiceButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  min-width: 0; /* flex 아이템이 계속 축소될 수 있도록 */
`;

const ChoiceOptionRow = styled.div<{ $isDragging?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0; /* flex 아이템이 계속 축소될 수 있도록 */
  opacity: ${props => props.$isDragging ? 0.5 : 1};
  transition: opacity 0.2s ease;
`;

const ChoiceNumberButton = styled.button<{ $levelIndex?: number }>`
  padding: 6px 12px;
  background: ${props => {
    if (props.$levelIndex !== undefined && props.$levelIndex >= 0 && props.$levelIndex < 6) {
      return hexToRgba(levelColors[props.$levelIndex], 0.2);
    }
    return 'white';
  }};
  color: var(--color-text-secondary);
  border: 2px solid var(--color-border);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 42px;
  height: 36px;
  flex-shrink: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover:not(:disabled) {
    border-color: var(--color-accent-strong);
    background: rgba(var(--color-accent-rgb), 0.1);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const CorrectAnswerBadge = styled.div`
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  background: var(--color-accent-strong);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  font-weight: bold;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 1;
  
  svg {
    width: 12px;
    height: 12px;
    stroke-width: 3;
  }
`;

const ChoiceInputWrapper = styled.div`
  display: flex;
  flex: 1;
  min-width: 0; /* flex 아이템이 계속 축소될 수 있도록 */
  align-items: center;
  position: relative;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-category-input-bg);
  height: 36px;
  
  &:focus-within {
    border-color: var(--color-border);
    outline: none;
    box-shadow: none;
  }
`;

const ChoiceInputField = styled.input`
  flex: 1;
  min-width: 0; /* flex 아이템이 계속 축소될 수 있도록 */
  height: 36px;
  padding: 6px 10px 6px 10px;
  font-size: 13px;
  border: none;
  outline: none;
  border-radius: 8px;
  font-family: inherit;
  background: transparent;
  color: var(--color-text-primary);
  
  &:focus {
    outline: none !important;
    border: none !important;
    box-shadow: none !important;
  }
  
  &:active {
    outline: none !important;
    border: none !important;
  }
  
  &::placeholder {
    color: var(--color-text-muted);
  }

  &:disabled {
    color: var(--color-text-muted);
    background: transparent;
  }
`;

const ChoiceInputIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 0 6px;
  height: 100%;
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 36px;
  cursor: grab;
  color: var(--color-text-muted);
  flex-shrink: 0;
  border-radius: 4px;
  transition: all 0.2s ease;
  
  &:hover {
    color: var(--color-text-secondary);
    background: var(--color-panel);
  }
  
  &:active {
    cursor: grabbing;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const AddChoiceButton = styled.button`
  width: 100%;
  padding: 10px 12px;
  background: var(--color-panel);
  color: var(--color-text-primary);
  border: 2px dashed var(--color-text-muted);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);

  :root:not([data-theme='light']) & {
    color: #ffffff;
    border-color: rgba(255, 255, 255, 0.6);
    box-shadow: 0 4px 10px rgba(255, 255, 255, 0.18);
  }
  
  &:hover {
    border-color: var(--color-text-muted);
    background: #f1f3f5;
  }

  :root:not([data-theme='light']) & {
    &:hover {
      background: rgba(255, 255, 255, 0.12);
      border-color: rgba(255, 255, 255, 0.6);
    }
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const AddChoiceButtonIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 600;
  color: var(--color-accent-strong);

  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
`;


const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--color-text-secondary);
  transition: all 0.2s ease;
  border-radius: 4px;
  box-sizing: border-box;

  :root[data-theme='light'] & {
    color: #4a4a4a;
  }

  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
  
  &:hover:not(:disabled) {
    background: var(--color-panel);
    color: inherit;
  }
  
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    color: var(--color-text-muted);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  svg {
    width: 14px;
    height: 14px;
    stroke: currentColor;
    fill: none;
  }
`;

const RefreshIcon = styled.svg`
  width: 14px;
  height: 14px;
  stroke: currentColor;
  fill: none;
`;

const TrashIcon = styled.svg`
  width: 14px;
  height: 14px;
  stroke: currentColor;
  fill: none;
`;


const SecondRowContainer = styled.div<{ $isSeatMode?: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 4px 20px 10px 20px;
  background: var(--color-bg);
  gap: 8px;
  align-items: center;
  border-top: 1px solid var(--color-border);
  margin-top: 20px;
`;

const SectionTitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: 8px 0 8px 0;
`;

const SectionTitle = styled.h4`
  margin: 0;
  font-size: 1.4rem;
  color: var(--color-text-primary);
  font-weight: 600;
  background: var(--color-surface);
  padding: 3px 14px;
  border: 2px solid var(--color-border);
  border-radius: 20px;
  display: inline-block;
`;

const MonitoringControlsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  justify-content: center;
  width: 100%;
  
  @media (min-width: 801px) {
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: center;
    align-items: center;
  }
`;

const MonitoringControlsFirstRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  width: 100%;
  
  @media (min-width: 801px) {
    width: auto;
    order: 1;
  }
`;

const MonitoringControlsSecondRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  width: 100%;
  
  @media (min-width: 801px) {
    width: auto;
    order: 2; /* 정답자선택, 전체선택, 선택취소 - 진행 새로고침 왼쪽 */
  }
`;

const MonitoringControlsThirdRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  width: 100%;
  
  @media (min-width: 801px) {
    width: auto;
    order: 3; /* 진행 새로고침, 프레젠테이션 */
  }
  
  @media (max-width: 800px) {
    width: auto; /* 작은 화면에서도 버튼 크기 유지 */
  }
`;

// 번호/자리 토글 버튼 (PointsTablePage에서 복사)
const ViewModeToggleButton = styled.div<{ $isSeatMode: boolean; $isDisabled?: boolean; $isInitialLoad?: boolean }>`
  display: flex;
  background: ${props => props.$isDisabled ? 'rgba(var(--color-accent-rgb), 0.3)' : 'var(--color-accent-strong)'};
  border-radius: 13px;
  padding: 2px;
  position: relative;
  cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
  transition: ${props => props.$isInitialLoad ? 'none' : 'all 0.3s ease'};
  height: 26px;
  min-width: 120px;
  width: auto;
  
  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.$isSeatMode ? '50%' : '2px'};
    width: calc(50% - 2px);
    height: calc(100% - 4px);
    background: ${props => props.$isDisabled ? 'rgba(255, 255, 255, 0.4)' : 'var(--color-surface)'};
    border-radius: 11px;
    transition: ${props => props.$isInitialLoad ? 'none' : 'left 0.3s ease'};
    z-index: 1;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ViewModeToggleOption = styled.div<{ $isActive: boolean; $isDisabled?: boolean }>`
  flex: 1;
  padding: 2px 12px;
  font-size: 11px;
  font-weight: 600;
  color: ${props => {
    if (props.$isDisabled) {
      return props.$isActive ? 'rgba(var(--color-accent-rgb), 0.4)' : 'rgba(255, 255, 255, 0.6)';
    }
    return props.$isActive ? 'var(--color-toggle-active)' : 'var(--color-toggle-inactive)';
  }};
  text-align: center;
  position: relative;
  z-index: 2;
  transition: color 0.3s ease;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  white-space: nowrap;
  
  @media (max-width: 600px) {
    padding: 2px 10px;
    font-size: 10px;
  }
`;

// 그리드 정렬 컨테이너
const GridAlignmentContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 12px;
`;

// 그리드 정렬 토글 버튼
const GridAlignmentToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 2px;
  height: 26px;
  min-height: 26px;
  max-height: 26px;
  min-width: 60px;
  box-sizing: border-box;
  line-height: 1;
  background: var(--color-grid-alignment-container);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  
  &:hover {
    border-color: var(--color-border);
    background: var(--color-grid-alignment-container);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const GridAlignmentOption = styled.div<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 20px;
  border-radius: 4px;
  background: ${props => props.$isActive ? 'var(--color-grid-alignment-active-bg)' : 'transparent'};
  color: ${props => props.$isActive ? 'var(--color-grid-alignment-active-icon)' : 'var(--color-grid-alignment-icon)'};
  box-shadow: ${props => props.$isActive ? '0 1px 2px rgba(0, 0, 0, 0.1)' : 'none'};
  transition: all 0.2s ease;
`;

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

// 텍스트 입력 영역 컨테이너 (제거됨 - FirstRowContainer로 이동)

const SectionTitleNavigationButton = styled.button`
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-surface);
  color: var(--color-text-primary);
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
  -webkit-appearance: none;
  appearance: none;
  touch-action: manipulation;
  user-select: none;
  
  &:hover:not(:disabled) {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
  }
  
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
  
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const NavigationButton = styled.button`
  width: 36px;
  height: 36px;
  min-width: 36px;
  min-height: 36px;
  border-radius: 50%;
  background: var(--color-surface);
  border: 2px solid var(--color-border);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: bold;
  color: var(--color-accent-strong);
  transition: all 0.3s ease;
  flex-shrink: 0;
  margin: 0;
  padding: 0;
  -webkit-appearance: none;
  appearance: none;
  touch-action: manipulation;
  user-select: none;
  
  /* 라이트 모드: 메인 테마 초록색, 다크 모드: 흰색 */
  :root[data-theme='light'] & {
    color: var(--color-accent-strong);
    border-color: var(--color-accent-strong);
  }

  :root:not([data-theme='light']) & {
    color: #ffffff;
    border-color: #ffffff;
  }
  
  :root[data-theme='light'] &:hover {
    background: var(--color-accent-soft);
    border-color: var(--color-accent-strong);
  }

  :root:not([data-theme='light']) &:hover {
    border-color: #ffffff;
    background: rgba(255, 255, 255, 0.1);
  }
  
  &:active {
    transform: scale(0.95);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    min-width: 36px;
    min-height: 36px;
    width: 36px;
    height: 36px;
    font-size: 18px;
  }
`;

const LargeTextInput = styled.input`
  flex: 1;
  width: 100%;
  height: 40px;
  padding: 8px 16px;
  font-size: 16px;
  line-height: 1.5;
  border: 2px solid var(--color-border);
  border-radius: 12px;
  font-family: inherit;
  color: var(--color-text-primary);
  
  /* 라이트 모드: 흰색 배경, 다크 모드: #4a4a4a (조금 더 밝게) */
  :root[data-theme='light'] & {
    background: #ffffff;
  }
  
  :root:not([data-theme='light']) & {
    background: #4a4a4a;
  }
  
  &:focus {
    outline: none;
    border-color: var(--color-accent-strong);
  }
  
  &::placeholder {
    color: var(--color-text-muted);
  }

  &[readonly] {
    background: var(--color-panel);
    cursor: not-allowed;
  }
`;

const QuestionMediaToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;
`;

const QuestionImageDeleteButton = styled.button`
  width: 36px;
  height: 36px;
  min-width: 36px;
  min-height: 36px;
  border-radius: 50%;
  border: 2px solid var(--color-border);
  background: var(--color-panel);
  color: var(--color-text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
  -webkit-appearance: none;
  appearance: none;
  touch-action: manipulation;
  user-select: none;

  &:hover:not(:disabled) {
    background: var(--color-surface);
    border-color: var(--color-text-muted);
    color: var(--color-text-primary);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  svg {
    width: 18px;
    height: 18px;
    stroke: currentColor;
    fill: none;
  }
`;

const QuestionMediaToggleButton = styled.button<{ $isActive?: boolean }>`
  width: 36px;
  height: 36px;
  min-width: 36px;
  min-height: 36px;
  border-radius: 50%;
  border: 2px solid var(--color-border);
  background: var(--color-panel);
  color: ${({ $isActive }) => ($isActive ? 'var(--color-accent-strong)' : 'var(--color-text-secondary)')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
  -webkit-appearance: none;
  appearance: none;
  touch-action: manipulation;
  user-select: none;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const QuestionImagePlaceholder = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  border: 2px dashed var(--color-border);
  border-radius: 12px;
  background: var(--color-panel);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-muted);
  font-size: 13px;
  font-weight: 700;
  user-select: none;
`;

const QuestionImageBox = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  border: 2px dashed var(--color-border);
  border-radius: 12px;
  background: var(--color-panel);
  overflow: hidden;
  position: relative;
`;

const QuestionImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const MediaModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 2200;
`;

const MediaModalContainer = styled.div`
  width: min(520px, 100%);
  background: var(--color-surface);
  border-radius: 16px;
  border: var(--modal-shell-border);
  box-shadow: var(--modal-shadow-xlarge);
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MediaModalTitle = styled.div`
  font-size: 16px;
  font-weight: 800;
  color: var(--color-text-primary);
  text-align: center;
`;

const MediaModalActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
`;

const MediaPrimaryButton = styled.button`
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid var(--color-accent-strong);
  background: var(--color-accent-strong);
  color: #fff;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MediaSecondaryButton = styled.button`
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// 멤버 블럭 컨테이너
const StudentsContainer = styled.div<{ $alignment?: 'left' | 'center' }>`
  padding: 16px;
  margin-top: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  background: transparent;
  border: 2px dashed var(--color-border);
  border-radius: 8px;
  box-shadow: none;
  justify-content: ${props => props.$alignment === 'center' ? 'center' : 'flex-start'};
  
  & > * {
    flex: 0 0 calc((100% - 32px - 64px) / 5);
    min-width: 0;
    box-sizing: border-box;
  }

  @media (max-width: 1500px) {
    & > * {
      flex: 0 0 calc((100% - 32px - 48px) / 4);
    }
  }

  @media (max-width: 1220px) {
    & > * {
      flex: 0 0 calc((100% - 32px - 32px) / 3);
    }
  }

  @media (max-width: 930px) {
    & > * {
      flex: 0 0 calc((100% - 32px - 16px) / 2);
    }
  }

  @media (max-width: 670px) {
    & > * {
      flex: 0 0 calc(100% - 32px);
    }
  }
`;

const GuestSectionWrapper = styled.div<{ $fullWidth?: boolean }>`
  margin: ${(props) => (props.$fullWidth ? '16px 0 0 0' : '16px 20px 0 20px')};
  width: ${(props) => (props.$fullWidth ? '100%' : 'auto')};
`;

const GuestSectionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-secondary);
  margin-bottom: 8px;
`;

// 멤버 프로필 블럭 (포인트 조회 페이지와 동일한 테두리/호버)
const StudentBlock = styled.div<{ $isActive?: boolean; $isSelected?: boolean }>`
  border: 2px solid ${props => {
    if (props.$isSelected) return 'var(--color-accent-strong)';
    return 'var(--color-border)';
  }};
  border-radius: 12px;
  padding: 4px;
  height: auto;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 12px;
  position: relative;
  filter: ${({ $isActive }) => ($isActive ? 'none' : 'grayscale(0.9)')};
  opacity: ${({ $isActive }) => ($isActive ? 1 : 0.55)};
  min-height: 104px;
  
  :root[data-theme='light'] & {
    background: rgba(255, 255, 255, 0.6);
    border-color: ${props => props.$isSelected ? 'var(--color-accent-strong)' : 'var(--color-block-border)'};
  }
  
  :root:not([data-theme='light']) & {
    background: var(--color-surface);
    border-color: ${props => props.$isSelected ? 'rgba(255, 255, 255, 0.8)' : 'var(--color-block-border)'};
  }
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  :root[data-theme='light'] &:hover {
    border-color: var(--color-accent-strong);
  }
  :root:not([data-theme='light']) &:hover {
    border-color: rgba(255, 255, 255, 0.8);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const MessageBubble = styled.div`
  flex: 1;
  width: 100%;
  max-width: 100%;
  position: relative;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 8px 12px 26px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-height: 74px;
  display: flex;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  overflow: visible;
  box-sizing: border-box;
  
  /* 라이트 모드: 흰색, 다크 모드: #4a4a4a (조금 더 밝게) */
  :root[data-theme='light'] & {
    background: #ffffff;
    
    &::after {
      border-left-color: #ffffff;
    }
  }
  
  :root:not([data-theme='light']) & {
    background: #4a4a4a;
    
    &::after {
      border-left-color: #4a4a4a;
    }
  }
  
  /* 말풍선 꼬리 - 오른쪽 하단 */
  &::after {
    content: '';
    position: absolute;
    bottom: 10px;
    right: -6px;
    width: 0;
    height: 0;
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
    border-left: 8px solid;
  }
  
  &::before {
    content: '';
    position: absolute;
    bottom: 10px;
    right: -8px;
    width: 0;
    height: 0;
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
    border-left: 9px solid var(--color-border);
    z-index: -1;
  }
`;

const ProfileImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex-shrink: 0;
  gap: 4px;
`;

const ProfileImageWrapper = styled.div`
  width: 52px;
  height: 52px;
  border: 2px solid #000000;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
`;

const ProfileTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 52px;
  max-width: 52px;
`;

const SelectionIndicator = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  padding: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
`;

const CompactButton = styled.button<{ disabled?: boolean }>`
  padding: 4px 8px;
  height: 26px;
  min-height: 26px;
  background: var(--color-date-block-bg);
  color: ${props => props.disabled ? 'var(--color-text-muted)' : 'var(--color-text-primary)'};
  border: 1px solid var(--color-border);
  border-radius: 8px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
  opacity: ${props => props.disabled ? 0.5 : 1};

  @media (max-width: 768px) {
    padding: 4px 6px;
  }

  @media (max-width: 600px) {
    padding: 4px 5px;
  }

  @media (max-width: 480px) {
    padding: 4px 4px;
  }

  &:hover:not(:disabled) {
    background: rgba(var(--color-accent-rgb), 0.1);
    border-color: var(--color-text-muted);
  }
`;

const TypingIndicator = styled.div<{ $isOverlay?: boolean; $isSmall?: boolean }>`
  display: flex;
  gap: 6px;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: ${({ $isOverlay }) => ($isOverlay ? '10' : '1')};
  opacity: 1;
  background: transparent;
  
  span {
    width: ${({ $isSmall }) => ($isSmall ? '5px' : '8px')};
    height: ${({ $isSmall }) => ($isSmall ? '5px' : '8px')};
    border-radius: 50%;
    background: var(--typing-indicator-color);
    box-shadow: 0 0 6px var(--typing-indicator-shadow);
    animation: typing 1.4s infinite;
    
    &:nth-child(2) {
      animation-delay: 0.2s;
    }
    
    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
  
  @keyframes typing {
    0%, 60%, 100% {
      transform: translateY(0);
    }
    30% {
      transform: translateY(-10px);
    }
  }
`;

const ResultBadge = styled.div<{ $status: 'correct' | 'wrong' | 'neutral' }>`
  position: absolute;
  top: -10px;
  right: -10px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $status }) =>
    $status === 'correct' ? '#10504a' : $status === 'wrong' ? '#ff6b6b' : '#777777'};
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 1;
  overflow: visible;
  padding: 2px;
  
  svg {
    width: 14px;
    height: 14px;
    color: white;
    overflow: visible;
  }
`;

// 퀴즈세트 가로 막대 (루틴 과제 도달률 모달과 동일 스타일): 전체 트랙 / 응답(회색) / 정답(진한 초록) / 서술형(연한 초록)
const QuizSetBarWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  gap: 6px;
`;
const QuizSetBar = styled.div`
  width: 100%;
  height: 10px;
  border-radius: 6px;
  background: var(--color-border);
  overflow: hidden;
  position: relative;

  :root:not([data-theme='light']) & {
    background: #6e6e6e;
  }
`;
const QuizSetBarSegment = styled.div<{ $widthPct: number; $leftPct?: number }>`
  height: 100%;
  position: absolute;
  left: ${p => (p.$leftPct ?? 0)}%;
  top: 0;
  width: ${p => p.$widthPct}%;
  transition: width 0.2s ease;
`;
const QuizSetBarResponse = styled(QuizSetBarSegment)`
  background: #d4d6dc;
  :root:not([data-theme='light']) & {
    background: rgba(255, 255, 255, 0.25);
  }
`;
const QuizSetBarCorrect = styled(QuizSetBarSegment)`
  background: var(--color-accent-strong);
  :root:not([data-theme='light']) & {
    background: #ffffff;
  }
`;
const QuizSetBarEssay = styled(QuizSetBarSegment)`
  background: rgba(16, 80, 74, 0.75);
  :root:not([data-theme='light']) & {
    background: rgba(255, 255, 255, 0.75);
  }
`;
const QuizSetBarTextRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 2px;
  font-weight: 700;
  font-size: 12px;
  line-height: 1.2;
  text-align: center;
  max-width: 100%;
  margin-top: 4px;
`;
const QuizSetTextCorrect = styled.span`
  color: var(--color-accent-strong);
  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
`;
const QuizSetTextEssay = styled.span`
  color: rgba(16, 80, 74, 0.85);
  :root:not([data-theme='light']) & {
    color: rgba(255, 255, 255, 0.75);
  }
`;
const QuizSetTextAnswered = styled.span`
  color: #d4d6dc;
  :root:not([data-theme='light']) & {
    color: rgba(255, 255, 255, 0.25);
  }
`;
const QuizSetTextTotal = styled.span`
  color: var(--color-border);
  :root:not([data-theme='light']) & {
    color: rgba(255, 255, 255, 0.2);
  }
`;

// 선택형 선택지 표시용 네모 도형
const ChoiceBadge = styled.div<{ $levelIndex?: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: ${props => {
    // levelIndex는 1부터 시작 (1번 버튼 = levelColors[0], 2번 버튼 = levelColors[1])
    // BoardPage에서는 index를 그대로 사용하므로 levelIndex = index + 1
    if (props.$levelIndex !== undefined && props.$levelIndex >= 1 && props.$levelIndex <= 6) {
      const colors = [
        '#FC5555', // 레벨1 - 빨강
        '#FF9933', // 레벨2 - 노랑
        '#669900', // 레벨3 - 초록
        '#119473', // 레벨4 - 파랑
        '#0066CC', // 레벨5
        '#9900CC', // 레벨6
      ];
      return hexToRgba(colors[props.$levelIndex - 1], 0.2);
    }
    return 'rgba(var(--color-accent-rgb), 0.2)';
  }};
  color: var(--color-text-secondary); /* 검정 텍스트 */
  border: 2px solid var(--color-border); /* 회색 테두리 */
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  margin: 0 2px;
  padding: 4px;
`;

// 선택형 선택지 컨테이너
const ChoiceBadgesContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px;
  width: 100%;
`;

const MessageContent = styled.div<{ $isWaiting?: boolean }>`
  flex: 1 1 auto;
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  text-align: center;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
  opacity: ${({ $isWaiting }) => ($isWaiting ? 0.65 : 1)};
  color: ${({ $isWaiting }) => ($isWaiting ? 'var(--color-text-muted)' : 'var(--color-text-primary)')};

  ${ChoiceBadgesContainer} {
    filter: ${({ $isWaiting }) => ($isWaiting ? 'grayscale(0.8)' : 'none')};
    opacity: ${({ $isWaiting }) => ($isWaiting ? 0.7 : 1)};
  }
`;

// 긴 텍스트를 말풍선 너비에 맞춰 줄바꿈하고 일정 높이를 넘기면 말줄임 처리
const MessageText = styled.span`
  display: -webkit-box;
  width: 100%;
  max-width: 100%;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.45;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 6;
  -webkit-box-orient: vertical;
`;

// 퀴즈 제어 섹션 (재생/정지 버튼)
const QuizControlSection = styled.div<{ $tightBottom?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 0 20px 0 20px;
  margin-bottom: ${props => (props.$tightBottom ? -15 : 8)}px;
  width: 100%;
`;

const QuizControlWrapper = styled.div<{ $isSetMode?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 8px 28px;
  min-height: 52px;
  background: var(--color-panel);
  border: 1px solid var(--color-border);
  border-radius: 999px;
  box-shadow: 0 10px 24px rgba(var(--color-accent-rgb), 0.08);
  /* 화면이 좁아져도 pill 자체는 절대 줄어들지 않게 고정 */
  width: ${props => (props.$isSetMode ? '320px' : '250px')};
  min-width: ${props => (props.$isSetMode ? '320px' : '250px')};
  flex-shrink: 0;
  box-sizing: border-box;
`;

const ControlTooltipWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &[data-tooltip]::after,
  &[data-tooltip]::before {
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.1s ease;
  }

  &[data-tooltip]::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: calc(100% + 4px);
    left: 50%;
    transform: translateX(-50%);
    background: rgba(var(--color-accent-rgb), 0.92);
    color: #fff;
    font-size: 11px;
    font-weight: 500;
    padding: 4px 8px;
    border-radius: 6px;
    white-space: nowrap;
    box-shadow: var(--modal-shadow);
    z-index: 10;
  }

  &[data-tooltip]::before {
    content: '';
    position: absolute;
    bottom: calc(100% + 3px);
    left: 50%;
    transform: translateX(-50%);
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid rgba(var(--color-accent-rgb), 0.92);
    z-index: 9;
  }

  &:hover::after,
  &:hover::before {
    opacity: 1;
  }
`;
const ChatStartButton = styled(QuizControlWrapper).attrs({ as: 'button' })`
  cursor: pointer;
  gap: 12px;
  font-size: 16px;
  font-weight: 600;
  min-height: 52px;
  /* QuizControlWrapper의 고정 폭을 그대로 사용 */
  max-width: none;
  width: auto;
  flex-shrink: 0;
  border: 1px solid var(--color-border);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  /* 라이트 모드: 메인 테마 초록색, 다크 모드: 흰색 */
  :root[data-theme='light'] & {
    color: var(--color-accent-strong);
    border-color: var(--color-accent-strong);
  }

  :root:not([data-theme='light']) & {
    color: #ffffff;
    border-color: #ffffff;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 14px 30px rgba(var(--color-accent-rgb), 0.12);
  }

  :root:not([data-theme='light']) &:hover:not(:disabled) {
    box-shadow: 0 14px 30px rgba(255, 255, 255, 0.2);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const StudentCreateToggleButton = styled(ChatStartButton)<{ $isActive?: boolean }>`
  padding: 0 12px;
  min-width: 120px;
  height: 36px;
  min-height: 36px;
  font-size: 14px;
  font-weight: 700;
  gap: 6px;
  line-height: 1.2;
  color: #ffffff !important;
  border-radius: 999px;
  align-items: center;
  margin-top: 0;
  margin-bottom: 0;
  border: none;

  background: ${({ $isActive }) => ($isActive ? '#FD6B6B' : 'var(--color-accent-strong)')};
  color: #ffffff;
  svg {
    color: #ffffff;
  }
  & > span {
    color: #ffffff !important;
  }

  :root:not([data-theme='light']) & {
    background: ${({ $isActive }) => ($isActive ? '#FD6B6B' : '#404040')};
    color: #ffffff;
  }

  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: var(--modal-shadow);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  :root:not([data-theme='light']) & {
    &:hover:not(:disabled) {
      box-shadow: none;
    }
  }
`;

const CloseChatButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;

  /* 라이트 모드: 메인 테마 초록색, 다크 모드: 흰색 */
  :root[data-theme='light'] & {
    color: var(--color-accent-strong);
    
    &:hover {
      background: rgba(var(--color-accent-rgb), 0.08);
      color: var(--color-accent-strong);
    }
  }
  
  :root:not([data-theme='light']) & {
    color: #ffffff;
    
    &:hover {
      background: rgba(255, 255, 255, 0.1);
      color: #ffffff;
    }
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;


// 공개/비공개 토글 버튼 (멀티답변 토글과 동일한 스타일)
const PublicModeToggleButton = styled.button<{ $isPublicMode: boolean; $isDisabled?: boolean }>`
  width: 36px;
  height: 36px;
  min-width: 36px;
  min-height: 36px;
  border-radius: 50%;
  border: 2px solid var(--color-border);
  background: var(--color-panel);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => (props.$isDisabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  padding: 0;
  -webkit-appearance: none;
  appearance: none;
  touch-action: manipulation;
  user-select: none;

  &:hover {
    transform: ${props => (props.$isDisabled ? 'none' : 'scale(1.05)')};
    box-shadow: ${props => (props.$isDisabled ? 'none' : '0 4px 12px rgba(0, 0, 0, 0.12)')};
  }

  &:active {
    transform: ${props => (props.$isDisabled ? 'none' : 'scale(0.95)')};
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
  }
`;

const PublicModeToggleIcon = styled.img`
  width: 20px;
  height: 20px;
  pointer-events: none;
  
  /* 라이트 모드: 메인 테마 초록색 (#10504A), 다크 모드: 흰색 */
  :root[data-theme='light'] & {
    filter: brightness(0) saturate(100%) invert(17%) sepia(61%) saturate(1234%) hue-rotate(141deg) brightness(94%) contrast(99%);
  }
  
  :root:not([data-theme='light']) & {
    filter: brightness(0) invert(1);
  }
`;

// 멀티답변 토글 버튼 (번호/자리 토글과 동일한 스타일)
const MultiAnswerToggleButton = styled.button<{ $isMultiMode: boolean; $isDisabled?: boolean }>`
  width: 36px;
  height: 36px;
  min-width: 36px;
  min-height: 36px;
  border-radius: 50%;
  border: 2px solid var(--color-border);
  background: var(--color-panel);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => (props.$isDisabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  padding: 0;
  -webkit-appearance: none;
  appearance: none;
  touch-action: manipulation;
  user-select: none;

  &:hover {
    transform: ${props => (props.$isDisabled ? 'none' : 'scale(1.05)')};
    box-shadow: ${props => (props.$isDisabled ? 'none' : '0 4px 12px rgba(0, 0, 0, 0.12)')};
  }

  &:active {
    transform: ${props => (props.$isDisabled ? 'none' : 'scale(0.95)')};
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
  }
`;

const MultiAnswerToggleIcon = styled.svg`
  width: 20px;
  height: 20px;
  fill: #ffffff;
  stroke: var(--color-accent-strong);
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
`;

// 멤버 답변 네비게이션 버튼
const AnswerNavigationButton = styled.button`
  width: 18px;
  height: 18px;
  min-width: 18px;
  min-height: 18px;
  border-radius: 3px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  padding: 0;
  
  &:hover:not(:disabled) {
    border-color: var(--color-accent-strong);
    background: rgba(var(--color-accent-rgb), 0.1);
    color: var(--color-accent-strong);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

// 멤버 답변 네비게이션 컨테이너
const AnswerNavigationContainer = styled.div`
  position: absolute;
  bottom: 4px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0;
  background: transparent;
  border-radius: 0;
  box-shadow: none;
  z-index: 5;
`;

// 답변 인덱스 텍스트 (0/0 표시)
const AnswerNavigationText = styled.span`
  font-size: 9px;
  color: #666;
  min-width: 28px;
  text-align: center;
  
  /* 다크모드: 더 밝은 회색으로 */
  :root:not([data-theme='light']) & {
    color: #999999;
  }
`;

// 문제카드 섹션 컨테이너 (중앙정렬, 문제 생성 섹션과 같은 너비)
const QuizCardSectionContainer = styled.div`
  margin: 0 auto;
  width: 100%;
  max-width: 640px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0px;
`;

// 문제카드 네비게이션 컨테이너
const QuizCardNavigationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  width: 100%;
  position: relative;
  overflow: hidden; /* 카드가 버튼 영역을 벗어나지 않도록 */
  margin: 8px 0 8px 0;
  padding: 0;
  
  @media (max-width: 768px) {
    margin: 8px 0 8px 0;
  }
`;

// 문제카드 섹션 (스태킹 효과)
const QuizCardSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 260px;
  position: relative;
  perspective: 1000px;
  overflow: visible; /* 스택 카드가 잘리지 않도록 */
  margin: 0;
  padding: 0 0 32px 0;
  
  /* 양쪽 끝 그라데이션 효과 */
  mask-image: linear-gradient(
    to right,
    transparent 0%,
    black 20%,
    black 80%,
    transparent 100%
  );
  -webkit-mask-image: linear-gradient(
    to right,
    transparent 0%,
    black 20%,
    black 80%,
    transparent 100%
  );
  
  @media (max-width: 768px) {
    min-height: 170px;
  }
`;

// 문제카드 래퍼 (스태킹을 위한 개별 컨테이너)
const QuizCardWrapper = styled.div<{ $index: number; $displayedIndex: number; $totalCards: number; $isCreateButton?: boolean }>`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%) translateX(${props => {
    const offset = props.$index - props.$displayedIndex;
    if (offset === 0) return '0px'; // 중앙 카드
    // 양쪽으로 스태킹 (왼쪽은 음수, 오른쪽은 양수)
    // 뒤에 있는 카드일수록 더 많이 겹치도록 비선형적으로 조정
    const absOffset = Math.abs(offset);
    let translateX: number;
    if (absOffset === 1) {
      translateX = 80; // 첫 번째 뒤 카드: 약 40% 겹침
    } else if (absOffset === 2) {
      translateX = 120; // 두 번째 뒤 카드: 120px 이동
    } else {
      // 세 번째 이후: 더 많이 겹치도록 (약 60% 이상)
      translateX = 120 + (absOffset - 2) * 20;
    }
    return `${offset < 0 ? -translateX : translateX}px`;
  }}) 
  scale(${props => {
    const offset = Math.abs(props.$index - props.$displayedIndex);
    if (offset === 0) return '1'; // 중앙 카드는 100%
    if (offset === 1) return '0.85'; // 첫 번째 옆 카드는 85%
    return '0.7'; // 나머지는 70%
  }});
  z-index: ${props => {
    // 문제 생성 버튼도 일반 카드와 동일하게 위치에 따라 z-index 조정
    const offset = Math.abs(props.$index - props.$displayedIndex);
    return props.$totalCards - offset;
  }};
  transition: transform 0.3s ease, opacity 0.3s ease;
  opacity: ${props => {
    // 빈 카드(문제 생성 버튼)는 항상 불투명하게
    if (props.$isCreateButton) return '1';
    const offset = Math.abs(props.$index - props.$displayedIndex);
    if (offset === 0) return '1';
    if (offset === 1) return '0.9';
    if (offset === 2) return '0.7';
    return '0.5';
  }};
  cursor: pointer;
  pointer-events: auto; /* 모든 카드를 클릭 가능하게 */
  
  /* 카드가 중앙에 있을 때만 호버 효과 */
  ${props => props.$index === props.$displayedIndex && `
    &:hover > * {
      cursor: pointer;
    }
  `}
  
  /* 모바일에서 카드 크기가 작아질 때 이동 거리도 비율적으로 조정 (1.2배 확대) */
  @media (max-width: 768px) {
    transform: translate(-50%, -50%) translateX(${props => {
      const offset = props.$index - props.$displayedIndex;
      if (offset === 0) return '0px'; // 중앙 카드
      const absOffset = Math.abs(offset);
      let translateX: number;
      if (absOffset === 1) {
        translateX = 77; // 64 * 1.2 = 76.8px ≈ 77px
      } else if (absOffset === 2) {
        translateX = 115; // 96 * 1.2 = 115.2px ≈ 115px
      } else {
        translateX = 115 + (absOffset - 2) * 19; // (96 + (absOffset - 2) * 16) * 1.2
      }
      return `${offset < 0 ? -translateX : translateX}px`;
    }}) 
    scale(${props => {
      const offset = Math.abs(props.$index - props.$displayedIndex);
      if (offset === 0) return '1';
      if (offset === 1) return '0.85';
      return '0.7';
    }});
  }
`;

// 문제카드 (클릭 가능)
const QuizCard = styled.div<{ $isSelected?: boolean; $isSetCard?: boolean; $hasImage?: boolean }>`
  width: 340px;
  height: 190px;
  border: 2px solid ${props => props.$isSelected ? 'var(--color-accent-strong)' : 'var(--color-border)'};
  border-radius: ${({ $isSetCard }) => ($isSetCard ? '0 8px 16px 16px' : '16px')};
  background: var(--color-surface);
  display: grid;
  grid-template-columns: ${({ $isSetCard }) => ($isSetCard ? '1fr' : 'minmax(0, 1fr) 32px')};
  align-items: stretch;
  justify-content: stretch;
  padding: ${({ $isSetCard }) => ($isSetCard ? '12px' : '10px 8px 10px 10px')};
  column-gap: ${({ $isSetCard }) => ($isSetCard ? '0px' : '8px')};
  row-gap: 4px;
  box-shadow: ${props => props.$isSelected ? '0 6px 14px rgba(var(--color-accent-rgb), 0.24)' : '0 3px 10px rgba(0, 0, 0, 0.1)'};
  box-sizing: border-box;
  overflow-x: visible;
  overflow-y: ${({ $isSetCard }) => ($isSetCard ? 'visible' : 'auto')};
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;
  position: relative;

  /* 이미지가 있을 때 워터마크 배경 아이콘 (카드 전체의 정중앙) */
  ${({ $hasImage, $isSetCard }) => $hasImage && !$isSetCard && css`
    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 80px;
      height: 80px;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='1.5'%3E%3Crect x='3' y='3' width='18' height='18' rx='2' ry='2'/%3E%3Ccircle cx='8.5' cy='8.5' r='1.5'/%3E%3Cpath d='M21 15l-5-5L5 21'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: center;
      background-size: contain;
      pointer-events: none;
      z-index: 0;
      opacity: 0.15;
    }
  `}

  ${({ $isSetCard }) =>
    $isSetCard &&
    css`
      /* 폴더 뚜껑 (상단 탭) */
      &::before {
        content: '';
        position: absolute;
        top: -13px;
        left: -2px;
        width: 60px;
        height: 12px;
        background: var(--color-surface);
        border: 2px solid var(--color-border);
        border-bottom: none;
        border-radius: 8px 8px 0 0;
        z-index: 2;
      }

      /* 폴더 오른쪽 탭 (뒤로 가는 탭) */
      &::after {
        content: '';
        position: absolute;
        top: -13px;
        left: 58px;
        width: 60px;
        height: 12px;
        background: rgba(var(--color-accent-rgb), 0.1);
        border: 2px solid var(--color-border);
        border-bottom: none;
        border-radius: 8px 8px 0 0;
        z-index: -1;
      }
    `}
  
  ${({ $isSelected, $isSetCard }) =>
    $isSelected && $isSetCard &&
    css`
      &::before {
        border-color: var(--color-accent-strong);
      }
      &::after {
        border-color: var(--color-accent-strong);
      }
    `}
  
  &:hover {
    border-color: var(--color-accent-strong);
    box-shadow: 0 6px 14px rgba(0, 0, 0, 0.14);
    
    ${({ $isSetCard }) =>
      $isSetCard &&
      css`
        &::before {
          border-color: inherit;
        }
        &::after {
          border-color: inherit;
        }
      `}
  }
  
  @media (max-width: 768px) {
    width: 192px;
    height: 125px;
    grid-template-columns: ${({ $isSetCard }) => ($isSetCard ? '1fr' : 'minmax(0, 1fr) 28px')};
    padding: ${({ $isSetCard }) => ($isSetCard ? '8px' : '6px 4px 6px 6px')};
    column-gap: ${({ $isSetCard }) => ($isSetCard ? '0px' : '6px')};
    border-radius: ${({ $isSetCard }) => ($isSetCard ? '0 6px 12px 12px' : '16px')};
    
    /* 모바일에서 배경 아이콘 크기 조정 */
    ${({ $hasImage, $isSetCard }) => $hasImage && !$isSetCard && css`
      &::before {
        width: 50px;
        height: 50px;
      }
    `}
    
    ${({ $isSetCard }) =>
      $isSetCard &&
      css`
        &::before {
          width: 40px;
          height: 8px;
          top: -9px;
          border-radius: 6px 6px 0 0;
        }
        
        &::after {
          top: -9px;
          left: 38px;
          width: 40px;
          height: 8px;
          background: rgba(var(--color-accent-rgb), 0.1);
          border: 2px solid var(--color-border);
          border-bottom: none;
          border-radius: 6px 6px 0 0;
          z-index: -1;
        }
      `}
    
    ${({ $isSelected, $isSetCard }) =>
      $isSelected && $isSetCard &&
      css`
        &::before {
          border-color: var(--color-accent-strong);
        }
        &::after {
          border-color: var(--color-accent-strong);
        }
      `}
    
    &:hover {
      ${({ $isSetCard }) =>
        $isSetCard &&
        css`
          &::before {
            border-color: inherit;
          }
          &::after {
            border-color: inherit;
          }
        `}
    }
  }
`;

const SetCardTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SetCardActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
  width: 100%;
  margin-top: auto;
  box-sizing: border-box;
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.12);

  @media (max-width: 768px) {
    gap: 4px;
  }
`;

const SetActionButton = styled.button`
  width: 100%;
  min-height: 32px;
  border-radius: 10px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-sizing: border-box;
  color: var(--color-text-primary);

  @media (max-width: 768px) {
    min-height: 28px;
    padding: 4px 8px;
    font-size: 12px;
    gap: 4px;
  }

  :root:not([data-theme='light']) & {
    color: #ffffff;
  }

  &:hover {
    border-color: var(--color-accent-strong);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

// 문제카드 질문 표시
const QuizCardQuestion = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: var(--color-text-primary);
  text-align: left;
  line-height: 1.4;
  word-break: break-word;
  width: 100%;
  overflow-wrap: break-word;
  margin-bottom: 0;
  
  @media (max-width: 768px) {
    font-size: 12px;
    margin-bottom: 0;
  }
`;

// 문제카드 선택지 컨테이너
const QuizCardChoices = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  margin-top: 0;
  align-items: flex-start;
`;

// 문제카드 선택지 항목
const QuizCardChoiceItem = styled.div`
  font-size: 11px;
  color: #6c757d;
  line-height: 1.2;
  word-break: break-word;
  overflow-wrap: break-word;
  
  @media (max-width: 768px) {
    font-size: 8px;
  }
`;

// 문제카드 서술형 안내 메시지
const QuizCardGuide = styled.div`
  font-size: 11px;
  color: #6c757d;
  line-height: 1.2;
  word-break: break-word;
  overflow-wrap: break-word;
  margin-top: 0;
  font-style: italic;
  text-align: left;
  width: 100%;
  
  @media (max-width: 768px) {
    font-size: 8px;
    margin-top: 0;
  }
`;

const CreateQuizButtonIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--color-border);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-muted);
  font-size: 24px;
  font-weight: bold;
  line-height: 1;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
    font-size: 23px;
  }
`;

const CreateQuizButtonText = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-muted);
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 12px; /* 비율에 맞게 축소, 1.2배 확대 */
  }
`;

const ProfileNumber = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
  line-height: 1.2;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ProfileName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #666;
  line-height: 1.2;
  max-width: 100%;
  
  /* 다크모드: 조금 더 밝게 */
  :root:not([data-theme='light']) & {
    color: #777777;
  }
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 1px;
`;

// 자리 모드 컨테이너
const SeatModeContainer = styled.div`
  padding: 0 20px 80px;
  margin-top: 0;
  width: 100%;
`;

const SeatModeScrollArea = styled.div`
  width: 100%;
  min-width: 0;
  background: transparent;
  border: 2px dashed var(--color-border);
  border-radius: 8px;
  /* 상/하단 빈 행 trim 이후에도 시각적 여백을 유지 */
  padding: 16px 2px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  box-shadow: none;

  /* 가로 스크롤바를 아주 컴팩트하게 */
  scrollbar-width: thin; /* Firefox */
  :root[data-theme='light'] & {
    scrollbar-color: rgba(0, 0, 0, 0.18) transparent;
  }
  :root:not([data-theme='light']) & {
    scrollbar-color: rgba(255, 255, 255, 0.25) transparent;
  }

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.16);
    border-radius: 999px;
  }

  :root:not([data-theme='light']) &::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.22);
  }

  &:hover::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.26);
  }

  :root:not([data-theme='light']) &:hover::-webkit-scrollbar-thumb {
    background-color: var(--color-block-border);
  }
`;

const SeatModeGridWrapper = styled.div`
  min-width: 1245px;
  width: 100%;
  display: flex;
  justify-content: flex-start;
`;

const SeatGridContent = styled.div`
  display: grid;
  gap: 4px;
  width: 100%;
  min-width: 1245px;
  align-content: start;
  height: fit-content;
`;

const RotateButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 16px;
  margin-bottom: 8px;
`;

const RotateButton = styled.button`
  width: 60px;
  height: 26px;
  min-height: 26px;
  max-height: 26px;
  min-width: 60px;
  box-sizing: border-box;
  line-height: 1;
  background: var(--color-grid-alignment-container);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 2px;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  &:hover {
    border-color: var(--color-border);
    background: var(--color-grid-alignment-container);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  &:active {
    transform: none;
  }
`;

const RotateIconWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  background: var(--color-grid-alignment-active-bg);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
`;

const RotateArrowIcon = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 1;
  transform: scale(1);
  transition: opacity 0.3s ease, transform 0.3s ease;

  svg {
    width: 22px;
    height: 22px;
  }

  /* 라이트 모드: 아이콘 색상 */
  :root[data-theme='light'] & svg {
    stroke: #717785;
  }

  ${RotateButton}:hover & {
    opacity: 0;
    transform: scale(0.8);
  }
`;

const RotateNumberIcon = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: currentColor;
  opacity: 0;
  transform: scale(0.8);
  transition: opacity 0.3s ease, transform 0.3s ease;

  /* 라이트 모드: 180 텍스트 색상 */
  :root[data-theme='light'] & {
    color: #717785;
  }

  ${RotateButton}:hover & {
    opacity: 1;
    transform: scale(1);
  }
`;

// 자리 모드 학생 블럭 (포인트 조회 페이지와 동일한 테두리/호버)
const SeatStudentBlockWrapper = styled.div<{ $isActive: boolean; $isSelected?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 12px;
  border: 2px solid ${props => {
    if (props.$isSelected) return 'var(--color-accent-strong)';
    return 'var(--color-border)';
  }};
  border-radius: 12px;
  padding: 2px;
  height: 100%;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  filter: ${({ $isActive }) => ($isActive ? 'none' : 'grayscale(0.9)')};
  opacity: ${({ $isActive }) => ($isActive ? 1 : 0.55)};
  
  :root[data-theme='light'] & {
    background: rgba(255, 255, 255, 0.6);
    border-color: ${props => props.$isSelected ? 'var(--color-accent-strong)' : 'var(--color-block-border)'};
  }
  
  :root:not([data-theme='light']) & {
    background: var(--color-surface);
    border-color: ${props => props.$isSelected ? 'rgba(255, 255, 255, 0.8)' : 'var(--color-block-border)'};
  }
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  :root[data-theme='light'] &:hover {
    border-color: var(--color-accent-strong);
  }
  :root:not([data-theme='light']) &:hover {
    border-color: rgba(255, 255, 255, 0.8);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ResetResponsesButton = styled.button<{ disabled?: boolean }>`
  padding: 4px 16px;
  height: 26px;
  min-height: 26px;
  background: var(--color-surface);
  color: ${props => props.disabled ? 'var(--color-text-muted)' : 'var(--color-accent-strong)'};
  border: 2px solid ${props => props.disabled ? 'var(--color-border)' : 'var(--color-accent-strong)'};
  border-radius: 8px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  white-space: nowrap;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  opacity: ${props => props.disabled ? 0.5 : 1};

  /* 라이트 모드: 메인 테마 초록색, 다크 모드: 흰색 */
  :root[data-theme='light'] & {
    color: ${props => props.disabled ? 'var(--color-text-muted)' : 'var(--color-accent-strong)'};
    border-color: ${props => props.disabled ? 'var(--color-border)' : 'var(--color-accent-strong)'};
  }

  :root:not([data-theme='light']) & {
    color: ${props => props.disabled ? 'var(--color-text-muted)' : '#ffffff'};
    border-color: ${props => props.disabled ? 'var(--color-border)' : '#ffffff'};
  }

  /* 아이콘 색상도 버튼 색상과 동일하게 */
  img {
    :root[data-theme='light'] & {
      filter: brightness(0) invert(0);
    }

    :root:not([data-theme='light']) & {
      filter: brightness(0) invert(1);
    }
  }

  /* 비활성화 시 아이콘도 투명도 적용 */
  &:disabled img {
    opacity: 0.5;
  }

  &:hover:not(:disabled) {
    background: var(--color-accent-soft);
    border-color: rgba(var(--color-accent-rgb), 0.8);
  }

  :root:not([data-theme='light']) &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.1);
    border-color: #ffffff;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  @media (max-width: 768px) {
    flex: 1;
    min-width: 0;
  }
`;

const PresentationButton = styled(ResetResponsesButton)`
  min-width: 148px;
  justify-content: center;
`;

const StudentResetButton = styled.button`
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  min-width: 18px;
  min-height: 18px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--color-text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 5;

  &:hover:not(:disabled) {
    color: var(--color-accent-strong);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const QuizCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
  position: relative;
  z-index: 1; /* 워터마크 위에 표시 */
`;

const QuizCardActionColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;

  @media (max-width: 768px) {
    gap: 5px;
  }
`;

const QuizCardActionButton = styled.button<{ $isEnabled?: boolean }>`
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  cursor: pointer;
  opacity: ${props => props.$isEnabled ? 1 : 0.3};
  pointer-events: ${props => props.$isEnabled ? 'auto' : 'none'};
  transition: transform 0.2s ease, opacity 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    pointer-events: none;
  }
`;

const QuizCardNumberBadge = styled.div`
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;
  border-radius: 50%;
  background: var(--color-text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 10px;
  font-weight: 600;

  @media (max-width: 768px) {
    width: 18px;
    height: 18px;
    min-width: 18px;
    min-height: 18px;
    font-size: 9px;
  }
`;

const QuizCardEditButton = styled(QuizCardActionButton)`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid transparent;

  /* 라이트 모드: 검정색, 다크 모드: 흰색 */
  :root[data-theme='light'] & {
    color: #000000;
    background: rgba(0, 0, 0, 0.05);
    
    svg {
      stroke: #000000;
    }
  }

  :root:not([data-theme='light']) & {
    color: #ffffff;
    
    svg {
      stroke: #ffffff;
    }
  }

  svg {
    width: 12px;
    height: 12px;
    fill: none;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  :root[data-theme='light'] &:hover {
    background: rgba(0, 0, 0, 0.1);
  }
`;

const QuizCardDeleteButton = styled(QuizCardActionButton)`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid transparent;

  /* 라이트 모드: 검정색, 다크 모드: 흰색 */
  :root[data-theme='light'] & {
    color: #000000;
    background: rgba(0, 0, 0, 0.05);
    
    svg {
      stroke: #000000;
    }
  }

  :root:not([data-theme='light']) & {
    color: #ffffff;
    
    svg {
      stroke: #ffffff;
    }
  }

  svg {
    width: 12px;
    height: 12px;
    fill: none;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  :root[data-theme='light'] &:hover {
    background: rgba(0, 0, 0, 0.1);
  }

  :root[data-theme='light'] &:hover svg {
    stroke: #000000;
  }

  :root:not([data-theme='light']) &:hover svg {
    stroke: #ffffff;
  }
`;

const QuizCardResetButton = styled(QuizCardActionButton)`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid transparent;

  /* 라이트 모드: 검정색 배경, 다크 모드: 흰색 배경 */
  :root[data-theme='light'] & {
    background: rgba(0, 0, 0, 0.05);
  }

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  :root[data-theme='light'] &:hover {
    background: rgba(0, 0, 0, 0.1);
  }
`;

const QuizCardResetIcon = styled.img`
  width: 11px;
  height: 11px;
  pointer-events: none;
  
  /* 라이트 모드: 검정색, 다크 모드: 흰색 */
  :root[data-theme='light'] & {
    filter: brightness(0) invert(0);
  }

  :root:not([data-theme='light']) & {
    filter: brightness(0) invert(1);
  }
`;

const QuizModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 0 16px;
  overflow-y: auto;
  z-index: 2000;
`;

const QuizModalContainer = styled.div`
  width: min(720px, 100%);
  background: var(--color-surface);
  border-radius: 16px;
  box-shadow: var(--modal-shadow-xlarge);
  border: var(--modal-border);
  display: flex;
  flex-direction: column;
  margin: 32px auto;
  min-height: fit-content;
`;

const QuizModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-border);
`;

const QuizModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  margin: 0;
  
  /* 라이트 모드: 메인 테마 초록색, 다크 모드: 흰색 */
  :root[data-theme='light'] & {
    color: var(--color-accent-strong);
  }
  
  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
`;

const QuizModalCloseButton = styled.button`
  border: none;
  background: none;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  color: #6c757d;
  padding: 4px;

  &:hover {
    color: #343a40;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const QuizModalBody = styled.div`
  padding: 24px;
`;

const QuizModalFooter = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: 16px 24px 20px 24px;
  border-top: 1px solid var(--color-border);
`;

// 세트 문항 블록 (점선 테두리) 그림자 포함
const SetSectionCard = styled.div`
  border: 2px dashed var(--color-border);
  border-radius: 12px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: var(--color-panel);
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.12);

  :root:not([data-theme='light']) & {
    box-shadow: 0 6px 14px rgba(255, 255, 255, 0.18);
  }
`;

// 세트 모달: + 문항 추가 버튼
const AddSectionButton = styled.button`
  width: 100%;
  padding: 16px 16px;
  border-radius: 10px;
  border: 2px dashed var(--color-border);
  background: #ffffff;
  color: var(--color-text-primary);
  cursor: pointer;
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.12);

  :root:not([data-theme='light']) & {
    background: var(--color-panel);
    color: var(--color-text-primary);
    box-shadow: 0 6px 14px rgba(255, 255, 255, 0.18);
  }
`;

const QuizModalButton = styled.button`
  width: 100%;
  padding: 10px 16px;
  border-radius: 10px;
  border: 1px solid var(--color-border);
  background: var(--color-panel);
  color: var(--color-text-primary);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--color-accent-strong);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const QuizModalButtonPrimary = styled(QuizModalButton)`
  background: var(--color-accent-strong);
  color: #fff;
  border-color: var(--color-accent-strong);

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    background: #ccc;
    border-color: var(--color-border);
    color: #fff;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const FloatingSaveButton = styled.button<{ $isDisabled?: boolean }>`
  position: fixed;
  bottom: calc(env(safe-area-inset-bottom, 0px) + 60px);
  right: 24px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: ${props => props.$isDisabled ? '#ccc' : '#10504A'};
  color: white;
  cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
  box-shadow: ${props => props.$isDisabled ? '0 2px 8px rgba(0, 0, 0, 0.1)' : '0 4px 16px rgba(var(--color-accent-rgb), 0.3)'};
  transition: all 0.3s ease;
  z-index: 2001;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${props => props.$isDisabled ? 0.6 : 1};
  
  &:hover {
    background: ${props => props.$isDisabled ? '#ccc' : '#0D4039'};
    transform: ${props => props.$isDisabled ? 'none' : 'scale(1.05)'};
    box-shadow: ${props => props.$isDisabled ? '0 2px 8px rgba(0, 0, 0, 0.1)' : '0 6px 20px rgba(var(--color-accent-rgb), 0.4)'};
  }
  
  &:active {
    transform: ${props => props.$isDisabled ? 'none' : 'scale(0.95)'};
  }
`;

const SaveSuccessMessage = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: var(--color-surface);
  border: var(--modal-shell-border);
  border-radius: 16px;
  padding: 24px 32px;
  box-shadow: var(--modal-shadow-large);
  z-index: 2002;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  min-width: 280px;
`;

const SaveSuccessText = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: var(--color-text-primary);
  text-align: center;
`;

const SaveSuccessButtons = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
`;

const SaveSuccessButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  border-radius: 8px;
  border: 2px solid var(--color-accent-strong);
  background: var(--color-accent-strong);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &:first-child {
    background: var(--color-surface);
    color: var(--color-accent-strong);
    border-color: var(--color-accent-strong);

    &:hover {
      background: var(--color-panel);
    }
  }
`;

const SetModalModeButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;

const SetModalModeButton = styled.button<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  background: var(--color-surface);
  color: ${props => props.$isActive ? 'var(--color-text-primary)' : 'var(--color-text-muted)'};
  border: 1px solid var(--color-border);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  min-height: 36px;
  min-width: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  font-size: 14px;
  font-weight: ${props => props.$isActive ? '600' : '400'};
  
  &:hover {
    background-color: var(--color-panel);
    border-color: var(--color-text-muted);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  ${props => props.$isActive && `
    border-color: var(--color-text-primary);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  `}
  
  @media (max-width: 420px) {
    padding: 6px 8px;
    font-size: 13px;
    min-height: 32px;
  }
`;

const OrderModeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
`;

const OrderModeQuizCard = styled.div<{ $isDragging?: boolean }>`
  background: var(--color-panel);
  border: 2px dashed var(--color-border);
  border-radius: 12px;
  padding: 14px;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 12px;
  align-items: start;
  cursor: default;
  transition: all 0.2s ease;
  opacity: ${({ $isDragging }) => ($isDragging ? 0.85 : 1)};
  position: relative;
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.12);

  :root:not([data-theme='light']) & {
    box-shadow: 0 6px 14px rgba(255, 255, 255, 0.18);
  }

  &:hover {
    border-color: var(--color-accent-strong);
  }
`;

const OrderModeDragHandle = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: transparent;
  border: 1px solid transparent;
  cursor: grab;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);
  transition: background 0.2s ease, border-color 0.2s ease;
  z-index: 1;

  &:hover {
    background: var(--color-panel);
    border-color: var(--color-border);
  }

  &:active {
    cursor: grabbing;
    transform: scale(0.98);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const OrderModeNumber = styled.div`
  font-size: 18px;
  font-weight: 800;
  color: var(--color-text-primary);
  text-align: left;
  grid-column: 1;
  grid-row: 1;
  line-height: 1.2;
`;

const OrderModeType = styled.div`
  font-size: 12px;
  font-weight: 800;
  color: var(--color-text-secondary);
  text-align: left;
  grid-column: 1;
  grid-row: 2;
  margin-top: -2px;
  line-height: 1.2;
`;

const OrderModeQuestion = styled.div`
  font-size: 14px;
  font-weight: 800;
  color: var(--color-text-primary);
  line-height: 1.2;
  text-align: left;
  grid-column: 2;
  grid-row: 1;
  padding-right: 40px;
`;

const OrderModeSmallText = styled.div`
  font-size: 12px;
  color: var(--color-text-secondary);
  line-height: 1.2;
  white-space: pre-wrap;
  text-align: left;
  grid-column: 2;
  grid-row: 2;
  padding-right: 40px;
  margin-top: -2px;
`;

const PresentationModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 2100;
`;

const PresentationModalContainer = styled.div`
  width: min(420px, 100%);
  background: var(--color-surface);
  border-radius: 20px;
  padding: 28px;
  box-shadow: var(--modal-shadow-xlarge);
  border: var(--modal-border);
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const PresentationModalTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--presentation-modal-title-color);
`;

const PresentationOptionRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const PresentationOptionLabel = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: var(--presentation-modal-label-color);
`;

const PresentationToggleGroup = styled.div<{ $isActive: boolean }>`
  display: flex;
  background: var(--color-accent-strong);
  border-radius: 13px;
  padding: 2px;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 26px;
  min-width: 120px;
  width: auto;
  
  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.$isActive ? '50%' : '2px'};
    width: calc(50% - 2px);
    height: calc(100% - 4px);
    background: var(--color-surface);
    border-radius: 11px;
    transition: left 0.3s ease;
    z-index: 1;
  }
`;

const PresentationToggleButton = styled.div<{ $isActive: boolean }>`
  flex: 1;
  padding: 2px 12px;
  font-size: 11px;
  font-weight: 600;
  color: ${props => props.$isActive ? 'var(--presentation-toggle-active-text)' : 'white'};
  text-align: center;
  position: relative;
  z-index: 2;
  transition: color 0.3s ease;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  cursor: pointer;
  
  @media (max-width: 600px) {
    padding: 2px 10px;
    font-size: 10px;
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const PresentationModalActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
  width: 100%;
`;

const PresentationSecondaryButton = styled.button`
  flex: 1;
  padding: 10px 18px;
  border-radius: 8px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: var(--color-panel);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const PresentationPrimaryButton = styled.button`
  flex: 1;
  padding: 10px 18px;
  border-radius: 8px;
  border: none;
  background: var(--color-accent-strong);
  color: #ffffff;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: var(--color-accent);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

// 드래그 가능한 선택지 행 컴포넌트 (컴포넌트 외부에 정의하여 리렌더링 시에도 동일 참조 유지)
interface SortableChoiceOptionRowProps {
  option: { id: string; text: string };
  index: number;
  isEditorLocked: boolean;
  correctAnswers: number[];
  totalChoicesCount: number;
  onChoiceNumberClick: (index: number) => void;
  onChoiceOptionChange: (index: number, value: string) => void;
  onRefreshChoiceOption: (index: number) => void;
  onDeleteChoiceOption: (index: number) => void;
}

const SortableChoiceOptionRow: React.FC<SortableChoiceOptionRowProps> = ({
  option,
  index,
  isEditorLocked,
  correctAnswers,
  totalChoicesCount,
  onChoiceNumberClick,
  onChoiceOptionChange,
  onRefreshChoiceOption,
  onDeleteChoiceOption,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: option.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <ChoiceOptionRow ref={setNodeRef} style={style} $isDragging={isDragging}>
      <ChoiceNumberButton
        $levelIndex={index < 8 ? index : undefined}
        onClick={() => {
          if (isEditorLocked) return;
          onChoiceNumberClick(index);
        }}
        disabled={isEditorLocked}
        tabIndex={-1}
      >
        {index + 1}
        {correctAnswers.includes(index) && (
          <CorrectAnswerBadge>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </CorrectAnswerBadge>
        )}
      </ChoiceNumberButton>
      <ChoiceInputWrapper>
        <ChoiceInputField
          type="text"
          value={option.text}
          onChange={(e) => {
            if (isEditorLocked) return;
            onChoiceOptionChange(index, e.target.value);
          }}
          placeholder={`${index + 1}번 선택지 내용을 입력하세요...`}
          disabled={isEditorLocked}
          tabIndex={isEditorLocked ? -1 : index + 2}
        />
        <ChoiceInputIcons>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              if (isEditorLocked) return;
              onRefreshChoiceOption(index);
            }}
            title="내용 초기화"
            disabled={isEditorLocked}
            tabIndex={-1}
          >
            <RefreshIcon viewBox="0 0 24 24" strokeWidth="2">
              <path d="M1 4v6h6" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M23 20v-6h-6" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" strokeLinecap="round" strokeLinejoin="round"/>
            </RefreshIcon>
          </IconButton>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              if (isEditorLocked) return;
              onDeleteChoiceOption(index);
            }}
            title="이 선택지 삭제"
            disabled={isEditorLocked || totalChoicesCount <= 2}
            tabIndex={-1}
          >
            <TrashIcon viewBox="0 0 24 24" strokeWidth="2">
              <path d="M3 6h18" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10 11v6" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M14 11v6" strokeLinecap="round" strokeLinejoin="round"/>
            </TrashIcon>
          </IconButton>
        </ChoiceInputIcons>
      </ChoiceInputWrapper>
      <DragHandle {...attributes} {...listeners}>
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
        </svg>
      </DragHandle>
    </ChoiceOptionRow>
  );
};

type SortableOrderCardProps = {
  section: {
    id: string;
    type: 'choice' | 'short' | 'essay';
    question: string;
    choices: { id: string; text: string }[];
    correctAnswers: number[];
    answers: string[];
    guide: string;
  };
  index: number;
};

const SortableOrderCard: React.FC<SortableOrderCardProps> = ({ section, index }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const typeLabel =
    section.type === 'choice' ? '선택형' : section.type === 'short' ? '단답형' : '서술형';

  const previewChoices =
    section.type === 'choice'
      ? (section.choices || []).filter((c) => (c?.text || '').trim())
      : [];

  const previewAnswers =
    section.type === 'short' ? (section.answers || []).filter((a) => (a || '').trim()).slice(0, 3) : [];

  const previewGuide = section.type === 'essay' ? (section.guide || '').trim() : '';

  return (
    <OrderModeQuizCard ref={setNodeRef} style={style as any} $isDragging={isDragging}>
      <OrderModeNumber>문항 {index + 1}</OrderModeNumber>
      <OrderModeType>{typeLabel}</OrderModeType>
      <OrderModeQuestion>{(section.question || '').trim() || '질문 없음'}</OrderModeQuestion>
      {section.type === 'choice' && previewChoices.length > 0 && (
        <OrderModeSmallText>
          {previewChoices.map((c, i) => {
            const isCorrect = (section.correctAnswers || []).includes(i);
            return `${isCorrect ? '✓ ' : ''}${c.text}`;
          }).join('\n')}
        </OrderModeSmallText>
      )}
      {section.type === 'short' && previewAnswers.length > 0 && (
        <OrderModeSmallText>{`정답: ${previewAnswers.join(', ')}${(section.answers || []).length > 3 ? '…' : ''}`}</OrderModeSmallText>
      )}
      {section.type === 'essay' && previewGuide && (
        <OrderModeSmallText>{`가이드: ${previewGuide}`}</OrderModeSmallText>
      )}
      <OrderModeDragHandle type="button" {...attributes} {...listeners} aria-label="문항 순서 이동">
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M3 7h18v2H3V7zm0 5h18v2H3v-2zm0 5h18v2H3v-2z" />
        </svg>
      </OrderModeDragHandle>
    </OrderModeQuizCard>
  );
};

/** 문제카드 캐러셀 → 세트 캐러셀 (위 화살표 + 나가기, 단일 둥근 직사각형) */
const BackToSetExitButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 8px 16px 8px 12px;
  border: 2px solid var(--color-border);
  border-radius: 12px;
  background: var(--color-surface);
  color: var(--color-text-primary);
  cursor: pointer;
  font: inherit;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease,
    color 0.2s ease;
  box-shadow: none;

  .back-to-set-exit-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    line-height: 0;
  }

  .back-to-set-exit-label {
    font-size: 14px;
    font-weight: 600;
    white-space: nowrap;
  }

  :root[data-theme='light'] & {
    border-color: var(--color-accent-strong);
    color: var(--color-accent-strong);
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12);
    background: rgba(var(--color-accent-rgb), 0.06);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: none;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const ChatPage: React.FC<ChatPageProps> = ({
  user,
  currentClass,
  onNavigate,
  onLogout,
  onUpdateUser,
  onProfileModalOpen,
  onClassManageModalOpen,
  classes,
  memberGroups,
  currentMemberGroup,
  onMemberGroupChange,
  onSeatAssignmentClick,
  onLevelSettingsClick,
  onPointManagementClick,
  onItemCardSettingsClick,
  onAssignmentSettingsClick,
  themeMode,
  onToggleTheme,
  onLicenseClick,
  onChatPlayingChange,
}) => {
  const [, setIsRedirecting] = useState(false);
  const [showLicenseLimitModal, setShowLicenseLimitModal] = useState(false);
  const [licenseLimitMessage, setLicenseLimitMessage] = useState('');
  const [memberProfileDisplayMode, setMemberProfileDisplayMode] = useState<MemberProfileDisplayMode>('svg');
  // 글로벌 알람 서비스 연결
  useGlobalAlarmService({ user, onAlarmPopupOpen: () => {} });
  

  // 드래그 앤 드롭 센서 설정
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px 이동 후 드래그 시작
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  // localStorage 키 생성 함수
  const getStorageKey = useCallback((key: string) => {
    return currentClass ? `chatPage_${key}_${currentClass.id}` : null;
  }, [currentClass]);

  // 인증/클래스 가드: 로그아웃되거나 클래스가 없으면 홈으로 이동
  useEffect(() => {
    if (!user) {
      setIsRedirecting(true);
      onNavigate?.('home');
      return;
    }
    if (!currentClass) {
      setIsRedirecting(true);
      onNavigate?.('home');
      return;
    }
    setIsRedirecting(false);
  }, [user, currentClass, onNavigate]);

  useEffect(() => {
    if (!currentClass) return;
    setMemberProfileDisplayMode(readMemberProfileDisplayMode(user.id, currentClass.id));
  }, [user.id, currentClass]);

  useEffect(() => {
    if (!currentClass) return;
    const handler = () => {
      setMemberProfileDisplayMode(readMemberProfileDisplayMode(user.id, currentClass.id));
    };
    window.addEventListener('memberProfileDisplayModeChanged', handler as EventListener);
    return () => {
      window.removeEventListener('memberProfileDisplayModeChanged', handler as EventListener);
    };
  }, [user.id, currentClass]);
  
  // 뷰 모드 상태 (번호/자리) - 초기값을 localStorage에서 읽어옴
  const [viewMode, setViewMode] = useState<'number' | 'seat'>(() => {
    if (!currentClass) return 'number';
    const storageKey = `chatPage_settings_${currentClass.id}`;
    try {
      const savedSettings = localStorage.getItem(storageKey);
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        if (settings.viewMode === 'number' || settings.viewMode === 'seat') {
          return settings.viewMode;
        }
      }
    } catch (error) {
      console.error('❌ [챗 페이지] 초기값 로드 실패:', error);
    }
    return 'number';
  });
  
  // localStorage에서 토글 상태 복원을 위한 플래그
  const [isSettingsRestored, setIsSettingsRestored] = useState<boolean>(false);
  
  // 🎯 그리드 정렬 상태 (번호 모드에서만 적용)
  const [gridAlignment, setGridAlignment] = useState<'left' | 'center'>('center');
  
  // currentClass가 변경되면 설정 복원 플래그 리셋 및 viewMode 초기화
  useEffect(() => {
    setIsSettingsRestored(false);
    if (currentClass) {
      const storageKey = `chatPage_settings_${currentClass.id}`;
      try {
        const savedSettings = localStorage.getItem(storageKey);
        if (savedSettings) {
          const settings = JSON.parse(savedSettings);
          if (settings.viewMode === 'number' || settings.viewMode === 'seat') {
            setViewMode(settings.viewMode);
          }
        } else {
          setViewMode('number');
        }
      } catch (error) {
        console.error('❌ [챗 페이지] 클래스 변경 시 설정 로드 실패:', error);
        setViewMode('number');
      }
    }
  }, [currentClass, currentClass?.id]);
  
  // localStorage에서 토글 설정 복원 (초기 로드 확인용)
  useEffect(() => {
    if (!currentClass) {
      setIsSettingsRestored(false);
      return;
    }
    
    if (isSettingsRestored) {
      console.log('⏭️ [챗 페이지] 설정 복원 이미 완료됨, 스킵');
      return;
    }
    
    const storageKey = getStorageKey('settings');
    if (!storageKey) {
      console.log('⚠️ [챗 페이지] storageKey 생성 실패, 기본값 사용');
      setIsSettingsRestored(true);
      return;
    }
    
    console.log('🔍 [챗 페이지] 설정 복원 시도:', { storageKey, currentClassId: currentClass.id });
    
    try {
      const savedSettings = localStorage.getItem(storageKey);
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        console.log('📦 [챗 페이지] localStorage에서 설정 로드:', settings, 'storageKey:', storageKey);
        
        if (settings.viewMode === 'number' || settings.viewMode === 'seat') {
          setViewMode(settings.viewMode);
          console.log('✅ [챗 페이지] viewMode 복원:', settings.viewMode);
        }
        if (settings.gridAlignment === 'left' || settings.gridAlignment === 'center') {
          setGridAlignment(settings.gridAlignment);
          console.log('✅ [챗 페이지] gridAlignment 복원:', settings.gridAlignment);
        }
        
        setIsSettingsRestored(true);
        console.log('✅ [챗 페이지] 설정 복원 완료:', settings);
      } else {
        console.log('ℹ️ [챗 페이지] 저장된 설정 없음, 기본값 사용. storageKey:', storageKey);
        setIsSettingsRestored(true);
      }
    } catch (error) {
      console.error('❌ [챗 페이지] localStorage에서 설정 복원 실패:', error);
      setIsSettingsRestored(true);
    }
  }, [currentClass, currentClass?.id, isSettingsRestored, getStorageKey]);
  
  // viewMode 변경 시 localStorage에 저장
  useEffect(() => {
    if (!currentClass || !isSettingsRestored) return;
    
    const storageKey = getStorageKey('settings');
    if (!storageKey) return;
    
    try {
      const existingSettings = localStorage.getItem(storageKey);
      const settings = existingSettings ? JSON.parse(existingSettings) : {};
      settings.viewMode = viewMode;
      localStorage.setItem(storageKey, JSON.stringify(settings));
      console.log('💾 [챗 페이지] viewMode 저장:', viewMode);
    } catch (error) {
      console.error('❌ [챗 페이지] localStorage에 viewMode 저장 실패:', error);
    }
  }, [viewMode, currentClass, isSettingsRestored, getStorageKey]);
  
  // gridAlignment 변경 시 localStorage에 저장
  useEffect(() => {
    if (!currentClass || !isSettingsRestored) return;
    
    const storageKey = getStorageKey('settings');
    if (!storageKey) return;
    
    try {
      const existingSettings = localStorage.getItem(storageKey);
      const settings = existingSettings ? JSON.parse(existingSettings) : {};
      settings.gridAlignment = gridAlignment;
      localStorage.setItem(storageKey, JSON.stringify(settings));
      console.log('💾 [챗 페이지] gridAlignment 저장:', gridAlignment);
    } catch (error) {
      console.error('❌ [챗 페이지] localStorage에 gridAlignment 저장 실패:', error);
    }
  }, [gridAlignment, currentClass, isSettingsRestored, getStorageKey]);
  
  // 문제 유형 상태
  const [questionType, setQuestionType] = useState<'choice' | 'short' | 'essay'>('choice');
  const [isQuestionImageMode, setIsQuestionImageMode] = useState<boolean>(false);
  const [questionImageUrl, setQuestionImageUrl] = useState<string>('');
  const [isImageSourceMenuOpen, setIsImageSourceMenuOpen] = useState<boolean>(false);
  const [isCameraModalOpen, setIsCameraModalOpen] = useState<boolean>(false);
  const [isCropModalOpen, setIsCropModalOpen] = useState<boolean>(false);
  const [rawImageSrc, setRawImageSrc] = useState<string>('');
  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const cropPixelsRef = useRef<CropAreaPixels | null>(null);
  const [isUploadingQuestionImage, setIsUploadingQuestionImage] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const cameraStreamRef = useRef<MediaStream | null>(null);
  
  // 세트 모달 섹션별 이미지 관리
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const setSectionFileInputRef = useRef<HTMLInputElement | null>(null);
  
  // 텍스트 입력
  const [messageText, setMessageText] = useState<string>('');
  
  // 정답 입력 (단답형일 때만 사용)
  const [, setAnswerText] = useState<string>('');
  const [answerBlocks, setAnswerBlocks] = useState<string[]>([]);
  
  // 안내 메시지 입력 (서술형일 때만 사용)
  const [guideMessage, setGuideMessage] = useState<string>('');
  
  // 선택형 옵션 상태 (번호별 텍스트)
  // 각 옵션에 고유 ID를 부여하여 React가 컴포넌트를 올바르게 추적하도록 함
  const [choiceOptions, setChoiceOptions] = useState<Array<{ id: string; text: string }>>([
    { id: '0', text: '' },
    { id: '1', text: '' },
    { id: '2', text: '' },
    { id: '3', text: '' },
  ]);
  const [correctAnswers, setCorrectAnswers] = useState<number[]>([]); // 정답 표시를 위한 인덱스 배열
  
  // 선택지 드래그 앤 드롭 핸들러
  const handleChoiceDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setChoiceOptions((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over?.id);

        // 정답 인덱스도 함께 업데이트
        setCorrectAnswers((prevCorrectAnswers) => {
          return prevCorrectAnswers.map(answerIndex => {
            if (answerIndex === oldIndex) {
              return newIndex;
            } else if (oldIndex < newIndex) {
              // 아래로 이동: oldIndex ~ newIndex 사이의 항목들은 위로 이동
              if (answerIndex > oldIndex && answerIndex <= newIndex) {
                return answerIndex - 1;
              }
            } else {
              // 위로 이동: newIndex ~ oldIndex 사이의 항목들은 아래로 이동
              if (answerIndex >= newIndex && answerIndex < oldIndex) {
                return answerIndex + 1;
              }
            }
            return answerIndex;
          });
        });

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }, []);
  
  // 퀴즈 관련 상태
  const [savedQuizzes, setSavedQuizzes] = useState<ChatQuiz[]>([]);
  /** listenToActiveQuiz·loadQuizSets가 동일 틱에서 최신 목록을 보도록 state 커밋 전에도 갱신 */
  const savedQuizzesRef = useRef<ChatQuiz[]>([]);
  const [currentQuizIndex, setCurrentQuizIndex] = useState<number>(-1); // 편집 중인 퀴즈 인덱스
  const [displayedQuizIndex, setDisplayedQuizIndex] = useState<number>(0); // 현재 표시할 퀴즈 인덱스
  /** 캐러셀 중앙 퀴즈 — 재생 전환 중 옛 activeQuizId 응답 리스너가 학생 블럭을 덮어 깜박이지 않게 */
  const displayedCenterQuizIdRef = useRef<string | null>(null);
  if (displayedQuizIndex >= 0 && displayedQuizIndex < savedQuizzes.length) {
    displayedCenterQuizIdRef.current = savedQuizzes[displayedQuizIndex]?.id ?? null;
  } else {
    displayedCenterQuizIdRef.current = null;
  }
  const [activeQuizId, setActiveQuizId] = useState<string | null>(null);
  const [quizResponses, setQuizResponses] = useState<{ [studentId: string]: StudentQuizResponse | StudentQuizResponse[] }>({});
  const [connectedStudentsMap, setConnectedStudentsMap] = useState<{ [studentId: string]: ConnectedStudentInfo }>({});
  const [answeredStudents, setAnsweredStudents] = useState<string[]>([]);
  const [setProgress, setSetProgress] = useState<
    Record<string, { answered: number; correct: number; essay: number; total: number }>
  >({});
  const [, setSetProgressCache] = useState<
    Record<string, Record<string, { answered: number; correct: number; essay: number; total: number }>>
  >({});
  const [editorMultiAnswerSetting, setEditorMultiAnswerSetting] = useState<boolean>(false);
  const [editorPublicModeSetting, setEditorPublicModeSetting] = useState<boolean>(false);
  const [isMultiAnswerMode, setIsMultiAnswerMode] = useState<boolean>(false);
  const [isPublicMode, setIsPublicMode] = useState<boolean>(false);
  // localStorage에서 선택 상태 복원
  const getSelectedStudentsStorageKey = useCallback(() => {
    if (!user?.id || !currentClass?.id) return null;
    return `selectedStudents_${user.id}_${currentClass.id}`;
  }, [user?.id, currentClass?.id]);

  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(() => {
    const storageKey = user?.id && currentClass?.id ? `selectedStudents_${user.id}_${currentClass.id}` : null;
    if (!storageKey) return new Set();
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        return new Set(Array.isArray(parsed) ? parsed : []);
      }
    } catch (error) {
      console.error('Failed to restore selected students from localStorage:', error);
    }
    return new Set();
  });
  
  // 문제카드 에디터 초기값 저장 (변경사항 감지용)
  const [initialQuizValues, setInitialQuizValues] = useState<{
    questionType: 'choice' | 'short' | 'essay';
    messageText: string;
    questionImageUrl: string;
    choiceOptions: Array<{ id: string; text: string }>;
    correctAnswers: number[];
    answerBlocks: string[];
    guideMessage: string;
    editorMultiAnswerSetting: boolean;
    editorPublicModeSetting: boolean;
  } | null>(null);
  const [isCloseQuizModalConfirmOpen, setIsCloseQuizModalConfirmOpen] = useState<boolean>(false);
  const [isChatOpen, setIsChatOpen] = useState<boolean>(false);
  const [studentAnswerIndices, setStudentAnswerIndices] = useState<{ [studentId: string]: number }>({});
  const [isPresentationModalOpen, setIsPresentationModalOpen] = useState(false);
  // 전역 변수로 창 참조 관리 (컴포넌트 리렌더링과 무관하게 유지)
  // ref도 함께 사용하되, 전역 변수를 우선적으로 확인
  const [presentationOptions, setPresentationOptions] = useState({
    // 단일 문제 프레젠테이션
    isResponsePublic: false,
    isNameVisible: false,
    isAnswerVisible: false,
    // 세트 프레젠테이션
    showCorrectRate: false,
    showScore: false,
  });
  const responsesQuizIdRef = useRef<string | null>(null);
  
  // 토스트 메시지 상태
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  
  // 토스트 표시 함수
  const showToastMessage = useCallback((message: string) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 1500);
  }, []);
  const pendingRemovalRef = useRef<{ studentId: string; responseIndex?: number } | null>(null);
  const [isSwitchingQuiz, setIsSwitchingQuiz] = useState<boolean>(false);
  const [isQuizModalOpen, setIsQuizModalOpen] = useState<boolean>(false);
  const [isSetModalOpen, setIsSetModalOpen] = useState<boolean>(false);
  const [isStudentCreateMode, setIsStudentCreateMode] = useState(false);
  const [editingSetId, setEditingSetId] = useState<string | null>(null);
  const [showSetSaveSuccess, setShowSetSaveSuccess] = useState<boolean>(false);
  const [showFloatingSaveButton, setShowFloatingSaveButton] = useState<boolean>(false);
  const [isCloseSetModalConfirmOpen, setIsCloseSetModalConfirmOpen] = useState<boolean>(false);
  const [setModalMode, setSetModalMode] = useState<'edit' | 'order'>('edit');
  const [isSetMode, setIsSetMode] = useState<boolean>(true);
  const [setTitle, setSetTitle] = useState<string>('');
  const [setSections, setSetSections] = useState<Array<{
    id: string;
    type: 'choice' | 'short' | 'essay';
    question: string;
    questionImageUrl?: string;
    choices: { id: string; text: string }[];
    correctAnswers: number[];
    answers: string[];
    guide: string;
    defaultMultiAnswerMode?: boolean;
    defaultPublicMode?: boolean;
  }>>([]);
  const [initialSetTitle, setInitialSetTitle] = useState<string>('');
  const [initialSetSections, setInitialSetSections] = useState<Array<{
    id: string;
    type: 'choice' | 'short' | 'essay';
    question: string;
    questionImageUrl?: string;
    choices: { id: string; text: string }[];
    correctAnswers: number[];
    answers: string[];
    guide: string;
    defaultMultiAnswerMode?: boolean;
    defaultPublicMode?: boolean;
  }>>([]);
  const [, setIsLoadingSets] = useState<boolean>(false);
  const [editingQuizId, setEditingQuizId] = useState<string | null>(null);
  const [quizSets, setQuizSets] = useState<ChatQuizSet[]>([]);
  const [selectedSetIndex, setSelectedSetIndex] = useState<number>(-1);
  const [displayedSetIndex, setDisplayedSetIndex] = useState<number>(0);
  const [activeQuizSetId, setActiveQuizSetId] = useState<string | null>(null);
  // 서버 메타 기준: 현재 활성 퀴즈 데이터가 저장된 컨테이너(세트 문서) ID
  // - 'main' 또는 setId
  const [activeQuizContainerId, setActiveQuizContainerId] = useState<string | null>(null);
  const [activeQuizPlayingId, setActiveQuizPlayingId] = useState<string | null>(null);
  const [activeQuizSetPlayingId, setActiveQuizSetPlayingId] = useState<string | null>(null);
  /** 세트 재생 시 문제 순서: 'sequential' 순차, 'random' 랜덤 */
  const [setPlayOrder, setSetPlayOrder] = useState<'sequential' | 'random'>('sequential');
  const loadSetErrorNotifiedRef = useRef(false);

  const connectedStudentIds = useMemo(
    () => Object.keys(connectedStudentsMap || {}),
    [connectedStudentsMap]
  );

  const isQuizPlaying = !!activeQuizId;
  const isSetSelected = selectedSetIndex >= 0 && selectedSetIndex < quizSets.length;
  // 퀴즈 재생 활성화 상태: 단일 퀴즈 재생 중이고 세트 재생이 아닐 때
  const isQuizPlayActive = !!activeQuizPlayingId && !activeQuizSetPlayingId;
  // 세트 재생 활성화 상태: 세트 재생 중일 때
  const isSetPlayActive = !!activeQuizSetPlayingId;
  const isChatPlaying = isSetPlayActive || isQuizPlayActive || isStudentCreateMode;

  useEffect(() => {
    onChatPlayingChange?.(isChatPlaying);
  }, [isChatPlaying, onChatPlayingChange]);

  const multiAnswerTooltip = isMultiAnswerMode ? '멀티 응답' : '단일 응답';
  const publicModeTooltip = isPublicMode ? '응답 공개' : '응답 비공개';
  const isViewingNewQuiz = currentQuizIndex === -1;
  // 모달이 열려있을 때는 항상 편집 가능하도록 isEditorLocked를 false로 설정
  const isEditorLocked = isQuizModalOpen ? false : (isQuizPlaying && !isViewingNewQuiz);
  const showSetViewButton = quizSets.length > 0 && !(isChatOpen && isSetMode);

  // 세트/문항 변환 헬퍼
  const createDefaultChoices = useCallback(
    (seed?: string) =>
      Array.from({ length: 4 }).map((_, i) => ({
        id: `choice_${seed ?? Date.now()}_${i}`,
        text: '',
      })),
    []
  );

  const createEmptySection = useCallback(() => ({
    id: `quiz_${Date.now()}`,
    type: 'choice' as const,
    question: '',
    questionImageUrl: '',
    choices: createDefaultChoices(),
    correctAnswers: [],
    answers: [],
    guide: '',
    defaultMultiAnswerMode: false,
    defaultPublicMode: false,
  }), [createDefaultChoices]);

  const mapSectionToQuiz = useCallback((section: {
    id: string;
    type: 'choice' | 'short' | 'essay';
    question: string;
    questionImageUrl?: string;
    choices: { id: string; text: string }[];
    correctAnswers: number[];
    answers: string[];
    guide: string;
    defaultMultiAnswerMode?: boolean;
    defaultPublicMode?: boolean;
  }, index: number): ChatQuiz => {
    const now = new Date();
    const filteredAnswers = (section.answers || []).filter((a) => a && a.trim() !== '');
    const base: ChatQuiz = {
      id: section.id || `quiz_${Date.now()}_${index}`,
      type: section.type,
      question: section.question,
      questionImageUrl: section.questionImageUrl && section.questionImageUrl.trim() ? section.questionImageUrl.trim() : '',
      createdAt: Timestamp.fromDate(now),
      updatedAt: Timestamp.fromDate(now),
      order: index,
      defaultMultiAnswerMode: !!section.defaultMultiAnswerMode,
      defaultPublicMode: !!section.defaultPublicMode,
      responses: {},
      answeredStudents: [],
    };
    if (section.type === 'choice') {
      return {
        ...base,
        choices: section.choices,
        correctAnswers: section.correctAnswers,
      };
    }
    if (section.type === 'short') {
      return {
        ...base,
        answers: filteredAnswers,
      };
    }
    return {
      ...base,
      guide: section.guide,
    };
  }, []);

  const mapSetToQuizzes = useCallback((set: ChatQuizSet): ChatQuiz[] => {
    const quizzes = Object.values(set.quizzes || {});
    return quizzes.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, []);

  const mapQuizToSection = useCallback((quiz: ChatQuiz) => ({
    id: quiz.id,
    type: quiz.type,
    question: quiz.question,
    questionImageUrl: quiz.questionImageUrl || '',
    choices: quiz.choices || [],
    correctAnswers: quiz.correctAnswers || [],
    answers: quiz.answers || [],
    guide: quiz.guide || '',
    defaultMultiAnswerMode: quiz.defaultMultiAnswerMode ?? false,
    defaultPublicMode: quiz.defaultPublicMode ?? false,
  }), []);

  const loadQuizSets = useCallback(async (preferredSetId?: string): Promise<ChatQuiz[] | null> => {
    if (!user || !currentClass?.classCode) {
      setQuizSets([]);
      setSelectedSetIndex(-1);
      setDisplayedSetIndex(0);
      savedQuizzesRef.current = [];
      setSavedQuizzes([]);
      setActiveQuizSetId(null);
      setIsLoadingSets(false);
      loadSetErrorNotifiedRef.current = false;
      return null;
    }
    setIsLoadingSets(true);
    try {
      const sets = await chatQuizService.listQuizSets(currentClass.classCode);
      loadSetErrorNotifiedRef.current = false;
      setQuizSets(sets);
      const targetId = preferredSetId || activeQuizSetId;
      let nextSelected = targetId ? sets.findIndex((s) => s.id === targetId) : -1;
      if (nextSelected === -1 && sets.length > 0) {
        nextSelected = 0;
      }
      setSelectedSetIndex(nextSelected);
      setDisplayedSetIndex(Math.max(0, nextSelected));
      if (nextSelected >= 0 && sets[nextSelected]) {
        const quizzes = mapSetToQuizzes(sets[nextSelected]);
        savedQuizzesRef.current = quizzes;
        setSavedQuizzes(quizzes);
        setActiveQuizSetId(sets[nextSelected].id);
        return quizzes;
      }
      savedQuizzesRef.current = [];
      setSavedQuizzes([]);
      setActiveQuizSetId(null);
      return [];
    } catch (error: any) {
      console.error('❌ 퀴즈 세트 로드 실패:', error);
      const code = error?.code;
      if (!loadSetErrorNotifiedRef.current) {
        loadSetErrorNotifiedRef.current = true;
        if (code === 'permission-denied' || code === 'unauthenticated') {
          showToastMessage('로그인 상태를 확인해주세요. 문제 세트를 불러올 수 없습니다.');
        } else {
          showToastMessage('문제카드 세트를 불러오지 못했습니다.');
        }
      }
      return null;
    } finally {
      setIsLoadingSets(false);
    }
  }, [user, currentClass?.classCode, mapSetToQuizzes, showToastMessage, activeQuizSetId]);

  const handleSelectSetIndex = useCallback(async (index: number) => {
    if (index < 0 || index >= quizSets.length) return;
    setSelectedSetIndex(index);
    setDisplayedSetIndex(index);
    const targetSet = quizSets[index];
    if (targetSet && currentClass?.classCode) {
      try {
        // 서버에서 최신 세트 데이터 가져오기
        const latestSet = await chatQuizService.getQuizSetById(currentClass.classCode, targetSet.id);
        if (latestSet) {
          console.log('✅ 세트 로드 성공:', latestSet.id, 'quizzes:', Object.keys(latestSet.quizzes || {}).length);
          setActiveQuizSetId(latestSet.id);
          const quizzes = mapSetToQuizzes(latestSet);
          console.log('✅ 변환된 퀴즈 개수:', quizzes.length);
          setSavedQuizzes(quizzes);
          setCurrentQuizIndex(-1);
          setDisplayedQuizIndex(0);
          // 최신 응답 계산
          const quizData = latestSet.quizzes || {};
          const total = Object.keys(quizData).length;
          const progress: Record<string, { answered: number; correct: number; essay: number; total: number }> = {};

          // 모든 학생에 대해 초기값 설정 (students 리스트가 있는 경우)
          const currentStudents = studentsRef.current;
          if (currentStudents.length > 0) {
            currentStudents.forEach((student) => {
              progress[student.id] = { answered: 0, correct: 0, essay: 0, total };
            });
          }

          // 학생별로 응답 여부 계산
          Object.values(quizData).forEach((quiz: any) => {
            const responses = quiz?.responses || {};
            const quizType = quiz?.type;
            Object.entries(responses).forEach(([studentId, resp]) => {
              const arr = Array.isArray(resp) ? resp : [resp];
              const hasAnyAnswer = arr.some((r: any) => !!r?.answer || !!r?.submittedAt);
              if (!hasAnyAnswer) return;

              const isEssay = quizType === 'essay';
              const hasCorrect = arr.some((r: any) => r?.isCorrect === true);

              if (!progress[studentId]) {
                progress[studentId] = { answered: 0, correct: 0, essay: 0, total };
              }
              progress[studentId].answered += 1;
              if (isEssay) {
                progress[studentId].essay += 1;
              } else if (hasCorrect) {
                progress[studentId].correct += 1;
              }
            });
          });

          setSetProgress(progress);
          setSetProgressCache((prev) => ({ ...prev, [latestSet.id]: progress }));
        } else {
          // 세트가 삭제된 경우
          console.warn('⚠️ 세트가 삭제되었습니다:', targetSet.id);
          setActiveQuizSetId(null);
          setSavedQuizzes([]);
          setCurrentQuizIndex(-1);
          setDisplayedQuizIndex(0);
        }
      } catch (error) {
        console.error('❌ 세트 로드 실패:', error);
        // 에러 발생 시 로컬 데이터 사용
      setActiveQuizSetId(targetSet.id);
      setSavedQuizzes(mapSetToQuizzes(targetSet));
      setCurrentQuizIndex(-1);
      setDisplayedQuizIndex(0);
        // 최신 응답 계산
        const quizData = targetSet.quizzes || {};
        const total = Object.keys(quizData).length;
        const progress: Record<string, { answered: number; correct: number; essay: number; total: number }> = {};

        // 모든 학생에 대해 초기값 설정 (students 리스트가 있는 경우)
        const currentStudents = studentsRef.current;
        if (currentStudents.length > 0) {
          currentStudents.forEach((student) => {
            progress[student.id] = { answered: 0, correct: 0, essay: 0, total };
          });
        }

        // 학생별로 응답 여부 계산
        Object.values(quizData).forEach((quiz: any) => {
          const responses = quiz?.responses || {};
          const quizType = quiz?.type;
          Object.entries(responses).forEach(([studentId, resp]) => {
            const arr = Array.isArray(resp) ? resp : [resp];
            const hasAnyAnswer = arr.some((r: any) => !!r?.answer || !!r?.submittedAt);
            if (!hasAnyAnswer) return;

            const isEssay = quizType === 'essay';
            const hasCorrect = arr.some((r: any) => r?.isCorrect === true);

            if (!progress[studentId]) {
              progress[studentId] = { answered: 0, correct: 0, essay: 0, total };
            }
            progress[studentId].answered += 1;
            if (isEssay) {
              progress[studentId].essay += 1;
            } else if (hasCorrect) {
              progress[studentId].correct += 1;
            }
          });
        });

        setSetProgress(progress);
        setSetProgressCache((prev) => ({ ...prev, [targetSet.id]: progress }));
      }
    }
  }, [quizSets, mapSetToQuizzes, currentClass?.classCode]);

  const handleRefreshSetProgress = useCallback(async () => {
    if (!isSetMode || selectedSetIndex < 0 || selectedSetIndex >= quizSets.length) {
      setSetProgress({});
      return;
    }
    const targetSet = quizSets[selectedSetIndex];
    if (!currentClass?.classCode || !targetSet) return;

    try {
      const setDoc = await chatQuizService.getQuizSetById(currentClass.classCode, targetSet.id);
      if (!setDoc) {
        setSetProgress({});
        return;
      }
      const quizzes = setDoc.quizzes || {};
      const total = Object.keys(quizzes).length;
      const progress: Record<string, { answered: number; correct: number; essay: number; total: number }> = {};

      // 모든 학생에 대해 초기값 설정 (students 리스트가 있는 경우)
      const currentStudents = studentsRef.current;
      if (currentStudents.length > 0) {
        currentStudents.forEach((student) => {
          progress[student.id] = { answered: 0, correct: 0, essay: 0, total };
        });
      }

      // 학생별로 응답 여부 계산
      Object.values(quizzes).forEach((quiz: any) => {
        const responses = quiz?.responses || {};
        const quizType = quiz?.type;
        Object.entries(responses).forEach(([studentId, resp]) => {
          const arr = Array.isArray(resp) ? resp : [resp];
          const hasAnyAnswer = arr.some((r: any) => !!r?.answer || !!r?.submittedAt);
          if (!hasAnyAnswer) return;

          const isEssay = quizType === 'essay';
          const hasCorrect = arr.some((r: any) => r?.isCorrect === true);

          if (!progress[studentId]) {
            progress[studentId] = { answered: 0, correct: 0, essay: 0, total };
          }
          progress[studentId].answered += 1;
          if (isEssay) {
            progress[studentId].essay += 1;
          } else if (hasCorrect) {
            progress[studentId].correct += 1;
          }
        });
      });

      setSetProgress(progress);
      setSetProgressCache((prev) => ({ ...prev, [targetSet.id]: progress }));
    } catch (error) {
      console.error('❌ 세트 응답 새로고침 실패:', error);
      showToastMessage('세트 응답을 불러오지 못했습니다.');
    }
  }, [isSetMode, selectedSetIndex, quizSets, currentClass?.classCode, showToastMessage]);

  // 문제세트가 선택된 상태에서 초기 로드 시 setProgress 업데이트
  useEffect(() => {
    if (!isSetMode || selectedSetIndex < 0 || selectedSetIndex >= quizSets.length || !currentClass?.classCode) {
      return;
    }

    // 초기 로드만 수행 (주기적 업데이트는 제거, 진행 새로고침 버튼으로만 갱신)
    void handleRefreshSetProgress();
  }, [isSetMode, selectedSetIndex, quizSets.length, currentClass?.classCode, handleRefreshSetProgress]);

  const resetSetForm = useCallback(() => {
    setSetTitle('');
    setSetSections([createEmptySection()]);
    setEditingSetId(null);
    setInitialSetTitle('');
    setInitialSetSections([createEmptySection()]);
    setSetModalMode('edit');
  }, [createEmptySection]);

  const handleOpenSetModal = useCallback(async (setId?: string) => {
    if (setId && currentClass?.classCode) {
      try {
        // 서버에서 최신 데이터 가져오기
        const target = await chatQuizService.getQuizSetById(currentClass.classCode, setId);
        if (target) {
          setEditingSetId(setId);
          setSetModalMode('edit');
          const title = target.title || '';
          setSetTitle(title);
          const quizzes = mapSetToQuizzes(target);
          const sections = quizzes.map(mapQuizToSection);
          setSetSections(sections);
          // 초기값 저장
          setInitialSetTitle(title);
          setInitialSetSections(JSON.parse(JSON.stringify(sections))); // deep copy
          setIsSetModalOpen(true);
          return;
        }
      } catch (error) {
        console.error('❌ 세트 로드 실패:', error);
        showToastMessage('세트를 불러오지 못했습니다.');
      }
    }
    resetSetForm();
    setIsSetModalOpen(true);
  }, [currentClass?.classCode, mapSetToQuizzes, mapQuizToSection, resetSetForm, showToastMessage]);

  // 수정사항이 있는지 확인
  const hasSetChanges = useCallback(() => {
    // 새로 생성하는 경우: 제목이나 섹션에 내용이 있으면 수정사항이 있는 것으로 봄
    if (!editingSetId) {
      if (setTitle.trim()) return true;
      if (setSections.some(section => section.question.trim())) return true;
      return false;
    }
    
    // 수정하는 경우: 초기값과 비교
    if (setTitle !== initialSetTitle) return true;
    if (setSections.length !== initialSetSections.length) return true;
    
    for (let i = 0; i < setSections.length; i++) {
      const current = setSections[i];
      const initial = initialSetSections[i];
      if (!initial) return true;
      
      if (current.id !== initial.id ||
          current.type !== initial.type ||
          current.question !== initial.question ||
          current.questionImageUrl !== initial.questionImageUrl ||
          current.guide !== initial.guide ||
          current.defaultMultiAnswerMode !== initial.defaultMultiAnswerMode ||
          current.defaultPublicMode !== initial.defaultPublicMode) {
        return true;
      }
      
      // choices 비교
      if (current.choices.length !== initial.choices.length) return true;
      for (let j = 0; j < current.choices.length; j++) {
        if (current.choices[j].id !== initial.choices[j]?.id ||
            current.choices[j].text !== initial.choices[j]?.text) {
          return true;
        }
      }
      
      // correctAnswers 비교
      if (JSON.stringify(current.correctAnswers.sort()) !== JSON.stringify(initial.correctAnswers.sort())) {
        return true;
      }
      
      // answers 비교
      if (JSON.stringify(current.answers) !== JSON.stringify(initial.answers)) {
        return true;
      }
    }
    
    return false;
  }, [editingSetId, setTitle, setSections, initialSetTitle, initialSetSections]);

  const handleCloseSetModal = useCallback(() => {
    // 저장 성공 후에는 경고 모달을 표시하지 않음
    if (showSetSaveSuccess) {
      setIsSetModalOpen(false);
      setShowSetSaveSuccess(false);
      setShowFloatingSaveButton(false);
      resetSetForm();
      return;
    }
    
    if (hasSetChanges()) {
      setIsCloseSetModalConfirmOpen(true);
    } else {
      setIsSetModalOpen(false);
      setShowSetSaveSuccess(false);
      setShowFloatingSaveButton(false);
      resetSetForm();
    }
  }, [hasSetChanges, resetSetForm, showSetSaveSuccess]);
  
  // 확인 모달에서 닫기 확인
  const handleConfirmCloseSetModal = useCallback(() => {
    setIsCloseSetModalConfirmOpen(false);
    setIsSetModalOpen(false);
    setShowSetSaveSuccess(false);
    setShowFloatingSaveButton(false);
    resetSetForm();
  }, [resetSetForm]);
  
  // 확인 모달에서 취소
  const handleCancelCloseSetModal = useCallback(() => {
    setIsCloseSetModalConfirmOpen(false);
  }, []);

  // 스크롤 감지하여 플로팅 버튼 표시
  useEffect(() => {
    if (!isSetModalOpen) {
      setShowFloatingSaveButton(false);
      return;
    }

    const handleScroll = () => {
      const overlays = document.querySelectorAll('[role="presentation"]');
      let overlay: HTMLElement | null = null;
      
      // 문제카드 세트 모달의 overlay 찾기
      for (let i = 0; i < overlays.length; i++) {
        const o = overlays[i] as HTMLElement;
        const container = o.querySelector('[aria-label="문제카드 세트 생성/수정"]');
        if (container) {
          overlay = o;
          break;
        }
      }

      if (!overlay) return;

      const container = overlay.querySelector('[role="dialog"]') as HTMLElement;
      if (!container) return;

      const footer = container.querySelector('div:last-child') as HTMLElement;
      if (!footer) return;

      const overlayRect = overlay.getBoundingClientRect();
      const footerRect = footer.getBoundingClientRect();
      const isFooterVisible = footerRect.bottom <= overlayRect.bottom && footerRect.top >= overlayRect.top;

      setShowFloatingSaveButton(!isFooterVisible);
    };

    // 초기 체크 및 스크롤 이벤트 리스너 추가
    const timer = setTimeout(() => {
      handleScroll();
    }, 100);

    const overlay = document.querySelector('[aria-label="문제카드 세트 생성/수정"]')?.closest('[role="presentation"]') as HTMLElement;
    if (overlay) {
      overlay.addEventListener('scroll', handleScroll);
      window.addEventListener('scroll', handleScroll);
    }

    return () => {
      clearTimeout(timer);
      if (overlay) {
        overlay.removeEventListener('scroll', handleScroll);
        window.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isSetModalOpen]);

  const handleSaveSet = useCallback(async () => {
    if (!currentClass?.classCode) return;
    
    // 새 세트 생성 시 문제 세트 수 제한 체크
    if (!editingSetId) {
      const setLimitCheck = checkQuizSetLimit(user, quizSets.length);
      if (!setLimitCheck.allowed) {
        setLicenseLimitMessage(setLimitCheck.message || '');
        setShowLicenseLimitModal(true);
        return;
      }
    }
    
    // 문제 카드 수 제한 체크
    const validSections = setSections.filter(section => (section.question || '').trim());
    const cardLimitCheck = checkQuizCardLimit(user, validSections.length);
    if (!cardLimitCheck.allowed) {
      setLicenseLimitMessage(cardLimitCheck.message || '');
      setShowLicenseLimitModal(true);
      return;
    }
    
    let hasInvalidShort = false;
    // 질문이 비어있는 섹션은 제외하고 저장
    const sanitizedSections = setSections.reduce<typeof setSections>((acc, section) => {
      const trimmedQuestion = (section.question || '').trim();
      // 질문이 비어있으면 저장하지 않음
      if (!trimmedQuestion) {
        return acc;
      }

      const trimmedAnswers = Array.isArray(section.answers)
        ? section.answers.filter((a) => a && a.trim() !== '')
        : [];

      if (section.type === 'short' && trimmedAnswers.length === 0) {
        hasInvalidShort = true;
      }

      acc.push({
        ...section,
        question: trimmedQuestion,
        answers: section.type === 'short'
          ? (trimmedAnswers.length > 0 ? trimmedAnswers : [''])
          : trimmedAnswers,
      });
      return acc;
    }, []);

    if (hasInvalidShort) {
      showToastMessage('단답형은 최소 1개의 정답을 입력해야 합니다.');
      setIsLoadingSets(false);
      return;
    }
    try {
      let nextActiveSetId = editingSetId || null;
      
      if (editingSetId) {
        // 기존 세트 수정: 기존 퀴즈 데이터를 가져와서 responses와 answeredStudents 보존
        const existingSet = await chatQuizService.getQuizSetById(currentClass.classCode, editingSetId);
        const existingQuizzesMap = existingSet?.quizzes || {};
        
        // 기존 퀴즈 데이터의 responses/answeredStudents(+ 그룹별 필드)를 보존하면서 새 퀴즈 데이터 생성
        const quizzes = sanitizedSections.map((section, idx) => {
          const quiz = mapSectionToQuiz(section, idx);
          const existingQuiz = existingQuizzesMap[quiz.id];
          
          // 기존 퀴즈가 있으면 responses와 answeredStudents 보존
          if (existingQuiz) {
            return {
              ...quiz,
              responses: existingQuiz.responses || {},
              answeredStudents: existingQuiz.answeredStudents || [],
              responsesByGroup: (existingQuiz as any).responsesByGroup || {},
              answeredStudentsByGroup: (existingQuiz as any).answeredStudentsByGroup || {},
              createdAt: existingQuiz.createdAt || quiz.createdAt,
            };
          }
          // 새로 추가된 퀴즈인 경우
          return quiz;
        });
        
        await chatQuizService.updateQuizSet(currentClass.classCode, editingSetId, {
          title: setTitle || '무제 세트',
          quizzes,
        });
        nextActiveSetId = editingSetId;
      } else {
        // 새 세트 생성
        const quizzes = sanitizedSections.map((section, idx) => mapSectionToQuiz(section, idx));
        const newId = await chatQuizService.createQuizSet(currentClass.classCode, setTitle || '무제 세트', quizzes);
        nextActiveSetId = newId;
        // 새로 생성된 세트이므로 editingSetId를 설정하여 수정 모드로 전환
        setEditingSetId(newId);
      }
      
      setActiveQuizSetId(nextActiveSetId);
      await loadQuizSets(nextActiveSetId || undefined);
      
      // 저장 후 초기값 업데이트 (변경사항 감지 방지)
      // loadQuizSets 완료 후 현재 setTitle과 setSections를 초기값으로 설정
      // (loadQuizSets가 setSections를 변경할 수 있으므로 저장된 값 사용)
      setInitialSetTitle(setTitle || '무제 세트');
      setInitialSetSections(JSON.parse(JSON.stringify(sanitizedSections))); // deep copy
      
      // 저장 성공 플래그 설정 (모달 닫기 시 경고 방지)
      setShowSetSaveSuccess(true);
    } catch (error) {
      console.error('❌ 세트 저장 실패:', error);
      showToastMessage('세트를 저장하지 못했습니다.');
    }
  }, [currentClass?.classCode, editingSetId, setTitle, setSections, mapSectionToQuiz, loadQuizSets, showToastMessage, quizSets.length, user]);

  const handleDeleteSet = useCallback(async (setId: string) => {
    if (!currentClass?.classCode) return;
    if (!window.confirm('이 세트를 삭제할까요?')) return;
    try {
      await chatQuizService.deleteQuizSet(currentClass.classCode, setId);
      await loadQuizSets();
      // 삭제된 세트를 사용 중이면 상태 초기화
      if (activeQuizSetId === setId) {
        setActiveQuizSetId(null);
        setSavedQuizzes([]);
        setCurrentQuizIndex(-1);
        setDisplayedQuizIndex(0);
      }
    } catch (error) {
      console.error('❌ 세트 삭제 실패:', error);
      showToastMessage('세트를 삭제하지 못했습니다.');
    }
  }, [currentClass?.classCode, loadQuizSets, showToastMessage, activeQuizSetId]);

  const handleSetTitleChange = useCallback((value: string) => {
    setSetTitle(value);
  }, []);

  const handleSectionChange = useCallback((id: string, partial: Partial<{
    type: 'choice' | 'short' | 'essay';
    question: string;
    questionImageUrl?: string;
    choices: { id: string; text: string }[];
    correctAnswers: number[];
    answers: string[];
    guide: string;
    defaultMultiAnswerMode: boolean;
    defaultPublicMode: boolean;
  }>) => {
    setSetSections((prev) =>
      prev.map((sec) => (sec.id === id ? { ...sec, ...partial } : sec))
    );
  }, []);

  const handleAddSection = useCallback(() => {
    // 문제 카드 수 제한 체크
    const validSections = setSections.filter(section => (section.question || '').trim());
    const cardLimitCheck = checkQuizCardLimit(user, validSections.length);
    if (!cardLimitCheck.allowed) {
      setLicenseLimitMessage(cardLimitCheck.message || '');
      setShowLicenseLimitModal(true);
      return;
    }
    setSetSections((prev) => [...prev, createEmptySection()]);
  }, [createEmptySection, setSections, user]);

  const handleRemoveSection = useCallback((id: string) => {
    setSetSections((prev) => prev.filter((sec) => sec.id !== id));
  }, []);

  useEffect(() => {
    void loadQuizSets();
  }, [loadQuizSets]);

  // 문제 만들기 중에는 세트 문서 실시간 구독 → 학생 제출 시 말풍선에 "문제 제출을 완료했습니다." 즉시 반영
  useEffect(() => {
    if (!isStudentCreateMode || !activeQuizSetId || !currentClass?.classCode) return;
    const unsubscribe = chatQuizService.listenToQuizSet(
      currentClass.classCode,
      activeQuizSetId,
      (set) => {
        if (set) setSavedQuizzes(mapSetToQuizzes(set));
      }
    );
    return () => unsubscribe();
  }, [isStudentCreateMode, activeQuizSetId, currentClass?.classCode, mapSetToQuizzes]);

  const clearResponses = useCallback(() => {
    responsesQuizIdRef.current = null;
    setQuizResponses({});
    setAnsweredStudents([]);
    setStudentAnswerIndices({});
  }, []);

  const processResponses = useCallback((responses: { [studentId: string]: StudentQuizResponse | StudentQuizResponse[] }) => {
    const processed: { [studentId: string]: StudentQuizResponse | StudentQuizResponse[] } = {};

    Object.keys(responses || {}).forEach((studentId) => {
      const response = responses[studentId];

      // 챗 시작 여부와 관계없이 저장된 모든 응답을 표시 (항상 멀티 응답 모드로 처리)
      if (Array.isArray(response)) {
        const validResponses = response.filter((r) => {
          const hasAnswer = r?.answer !== undefined && r?.answer !== null;
          const hasSubmittedAt = r?.submittedAt !== undefined && r?.submittedAt !== null;
          return hasAnswer || hasSubmittedAt;
        });
        processed[studentId] = validResponses.length > 0 ? validResponses : response;
      } else if (response) {
        const hasAnswer = response.answer !== undefined && response.answer !== null;
        const hasSubmittedAt = response.submittedAt !== undefined && response.submittedAt !== null;
        if (hasAnswer || hasSubmittedAt) {
          processed[studentId] = [response];
        }
      }
    });

    return processed;
  }, []); // isMultiAnswerMode 의존성 제거
  
  // 이전 응답을 ref로 추적하여 applyResponses가 quizResponses에 의존하지 않도록 함
  const previousResponsesRef = useRef<{ [studentId: string]: StudentQuizResponse | StudentQuizResponse[] }>({});

  const applyResponses = useCallback((
    quizId: string,
    responses: { [studentId: string]: StudentQuizResponse | StudentQuizResponse[] },
    answered?: string[],
  ) => {
    const processed = processResponses(responses || {});
    const isQuizSwitch = responsesQuizIdRef.current !== quizId;
    responsesQuizIdRef.current = quizId;

    const previousResponses = previousResponsesRef.current;
    setQuizResponses(processed);
    setAnsweredStudents(answered || []);

    setStudentAnswerIndices((prev) => {
      const next: { [studentId: string]: number } = isQuizSwitch ? {} : { ...prev };

      Object.keys(next).forEach((studentId) => {
        const resp = processed[studentId];
        const length = Array.isArray(resp) ? resp.length : resp ? 1 : 0;
        if (!resp || length <= 0) {
          delete next[studentId];
          return;
        }

        const pending = pendingRemovalRef.current;
        let newIdx = next[studentId] ?? 0;
        const prevResp = previousResponses?.[studentId];
        const prevLength = Array.isArray(prevResp) ? prevResp.length : prevResp ? 1 : 0;
        const hasNewResponse = length > prevLength;

        if (pending && pending.studentId === studentId) {
          const targetIndex = pending.responseIndex ?? 0;
          newIdx = Math.min(Math.max(targetIndex, 0), length - 1);
        } else if (hasNewResponse && !isQuizSwitch) {
          // 실시간으로 새로운 응답이 추가된 경우에만 마지막 응답으로 이동
          newIdx = length - 1;
        } else if (newIdx >= length) {
          newIdx = length - 1;
        }

        if (newIdx < 0) {
          newIdx = 0;
        }

        next[studentId] = newIdx;
      });

      Object.keys(processed).forEach((studentId) => {
        if (studentId in next) {
          return;
        }

        const resp = processed[studentId];
        const length = Array.isArray(resp) ? resp.length : resp ? 1 : 0;
        if (length <= 0) {
          return;
        }

        const pending = pendingRemovalRef.current;
        const initialIndex =
          pending && pending.studentId === studentId
            ? Math.min(Math.max(pending.responseIndex ?? 0, 0), length - 1)
            : length - 1; // 문제 카드 전환 시에는 마지막 응답부터 시작

        next[studentId] = initialIndex;
      });

      return next;
    });

    // 최신 응답을 ref에 저장
    previousResponsesRef.current = processed;
    pendingRemovalRef.current = null;
  }, [processResponses]);
  
  // savedQuizzes가 변경되면 displayedQuizIndex 조정
  // 문제 생성 버튼까지 포함하므로 savedQuizzes.length까지 허용
  useEffect(() => {
    const maxIndex = savedQuizzes.length; // 문제 생성 버튼 인덱스
    if (savedQuizzes.length > 0 && displayedQuizIndex > maxIndex) {
      setDisplayedQuizIndex(maxIndex);
    } else if (savedQuizzes.length === 0) {
      setDisplayedQuizIndex(0); // 문제 생성 버튼만 있음
    }
  }, [savedQuizzes.length, displayedQuizIndex]);
  
  // 현재 로드된 퀴즈 ID·세트 ID를 ref로 추적 (재생 시 올바른 setId로 1회만 읽기)
  const loadedQuizIdRef = useRef<string | null>(null);
  const loadedQuizSetIdRef = useRef<string | null>(null);
  const isSavingQuizRef = useRef(false);
  const isPlayingQuizRef = useRef(false);

  // 퀴즈를 에디터에 로드 (setId: 이 퀴즈가 속한 세트 ID, 재생 시 사용)
  const loadQuizToEditor = useCallback((quiz: ChatQuiz, shouldSaveInitialValues: boolean = false, setId?: string | null) => {
    // 퀴즈가 변경되었는지 확인
    const isQuizChanged = loadedQuizIdRef.current !== quiz.id;
    
    // 이미 같은 퀴즈가 로드되어 있고 초기값 저장이 필요하지 않으면 업데이트하지 않음 (무한 루프 방지)
    if (!shouldSaveInitialValues && !isQuizChanged) {
      return;
    }
    
    setQuestionType(quiz.type);
    setMessageText(quiz.question);
    const hasImage = !!(quiz.questionImageUrl && quiz.questionImageUrl.trim());
    setIsQuestionImageMode(hasImage);
    setQuestionImageUrl(quiz.questionImageUrl || '');
    
    // 퀴즈가 변경되었을 때 항상 저장된 옵션값으로 설정
    if (isQuizChanged) {
    const defaultMulti = quiz.defaultMultiAnswerMode ?? false;
    const defaultPublic = quiz.defaultPublicMode ?? false;
      console.log('📋 [loadQuizToEditor] 퀴즈 변경, 옵션 값 설정:', {
        quizId: quiz.id,
        defaultMulti,
        defaultPublic,
        previousMulti: isMultiAnswerMode,
        previousPublic: isPublicMode,
      });
    setEditorMultiAnswerSetting(defaultMulti);
    setEditorPublicModeSetting(defaultPublic);
    setIsMultiAnswerMode(defaultMulti);
    setIsPublicMode(defaultPublic);
      console.log('✅ [loadQuizToEditor] 옵션 값 설정 완료:', {
        newMulti: defaultMulti,
        newPublic: defaultPublic,
      });
    }
    
    loadedQuizIdRef.current = quiz.id;
    loadedQuizSetIdRef.current = setId ?? null;

    if (quiz.type === 'choice') {
      setChoiceOptions(quiz.choices || []);
      setCorrectAnswers(quiz.correctAnswers || []);
    } else if (quiz.type === 'short') {
      setAnswerBlocks(quiz.answers || []);
      setAnswerText('');
    } else if (quiz.type === 'essay') {
      setGuideMessage(quiz.guide || '');
    }
    
    // 초기값 저장 (변경사항 감지용) - 모달이 열릴 때만 저장
    if (shouldSaveInitialValues) {
      const defaultMulti = quiz.defaultMultiAnswerMode ?? false;
      const defaultPublic = quiz.defaultPublicMode ?? false;
      setInitialQuizValues({
        questionType: quiz.type,
        messageText: quiz.question,
        questionImageUrl: quiz.questionImageUrl || '',
        choiceOptions: quiz.type === 'choice' ? (quiz.choices || []) : [],
        correctAnswers: quiz.type === 'choice' ? (quiz.correctAnswers || []) : [],
        answerBlocks: quiz.type === 'short' ? (quiz.answers || []) : [],
        guideMessage: quiz.type === 'essay' ? (quiz.guide || '') : '',
        editorMultiAnswerSetting: defaultMulti,
        editorPublicModeSetting: defaultPublic,
      });
    }
  }, [isMultiAnswerMode, isPublicMode]);

  const initializeNewQuizForm = useCallback(() => {
    // 새 퀴즈 생성 모드로 표시
    loadedQuizIdRef.current = null;
    loadedQuizSetIdRef.current = null;
    
    const emptyChoices = [
      { id: '0', text: '' },
      { id: '1', text: '' },
      { id: '2', text: '' },
      { id: '3', text: '' },
    ];
    
    setMessageText('');
    setIsQuestionImageMode(false);
    setQuestionImageUrl('');
    setIsImageSourceMenuOpen(false);
    setAnswerText('');
    setAnswerBlocks([]);
    setGuideMessage('');
    setChoiceOptions(emptyChoices);
    setCorrectAnswers([]);
    setQuestionType('short');
    setCurrentQuizIndex(-1);
    setEditorMultiAnswerSetting(isMultiAnswerMode);
    setEditorPublicModeSetting(isPublicMode);
    
    // 초기값 저장 (변경사항 감지용)
    setInitialQuizValues({
      questionType: 'short',
      messageText: '',
      questionImageUrl: '',
      choiceOptions: emptyChoices,
      correctAnswers: [],
      answerBlocks: [],
      guideMessage: '',
      editorMultiAnswerSetting: isMultiAnswerMode,
      editorPublicModeSetting: isPublicMode,
    });
  }, [isMultiAnswerMode, isPublicMode]);

  // displayedQuizIndex가 변경되면 자동으로 해당 카드를 선택 상태로 설정
  useEffect(() => {
    // activeQuizIdRef를 통해 최신 값을 읽음 (dependency에서 제거하여 퀴즈 중지 시 깜박임 방지)
    const currentActiveQuizId = activeQuizIdRef.current;
    console.log('🔍 [displayedQuizIndex useEffect] 시작:', {
      displayedQuizIndex,
      savedQuizzesLength: savedQuizzes.length,
      activeQuizId: currentActiveQuizId,
      loadedQuizId: loadedQuizIdRef.current,
      currentClassCode: currentClass?.classCode,
    });
    
    // 빈 카드 인덱스인 경우 (새 문제 생성 모드)
    if (displayedQuizIndex === savedQuizzes.length) {
      console.log('📝 [displayedQuizIndex useEffect] 새 문제 생성 모드');
      // 재생 여부와 관계없이 ref 초기화 — 미초기화 시 재생 버튼이 직전에 보던 퀴즈를 재생함
      loadedQuizIdRef.current = null;
      loadedQuizSetIdRef.current = null;
      // activeQuizId가 null일 때 (클래스챗이 닫혔을 때)는 currentQuizIndex를 변경하지 않음 (깜박임 방지)
      if (currentQuizIndex !== -1 && currentActiveQuizId !== null) {
        setCurrentQuizIndex(-1);
      }
      return;
    }

    // activeQuizId가 null일 때 (클래스챗이 닫혔을 때)는 퀴즈 로드 로직을 실행하지 않음 (깜박임 방지)
    // 단, displayedQuizIndex가 실제로 변경된 경우에만 early return
    // (activeQuizId가 dependency에 있어서 변경될 때마다 실행되지만, displayedQuizIndex가 같으면 로직 실행하지 않음)

    // 네비게이션 이동 시 중앙 카드를 항상 선택 상태로 설정
    if (savedQuizzes.length > 0 && displayedQuizIndex >= 0 && displayedQuizIndex < savedQuizzes.length) {
      const currentQuiz = savedQuizzes[displayedQuizIndex];
      if (currentQuiz) {
        // activeQuizIdRef를 통해 최신 값을 읽음 (dependency에서 제거하여 퀴즈 중지 시 깜박임 방지)
        const currentActiveQuizId = activeQuizIdRef.current;
        const isActiveQuiz = !!currentActiveQuizId && currentActiveQuizId === currentQuiz.id;
        
        console.log('🔍 [displayedQuizIndex useEffect] 퀴즈 확인:', {
          currentQuizId: currentQuiz.id,
          activeQuizId: currentActiveQuizId,
          isActiveQuiz,
          loadedQuizId: loadedQuizIdRef.current,
          condition1: !!currentActiveQuizId,
          condition2: currentActiveQuizId !== currentQuiz.id,
          condition3: !!currentClass?.classCode,
          willEnterPlayNewQuiz: !!(!!currentActiveQuizId && currentActiveQuizId !== currentQuiz.id && currentClass?.classCode),
        });
        
        // 재생 중일 때 퀴즈를 이동하면 새로운 퀴즈를 재생하도록 함
        // 조건: activeQuizId가 있고 (재생 중), 현재 선택한 퀴즈와 다른 퀴즈로 이동하는 경우
        // 단, 세트 재생 중일 때는 재생하지 않고 탐색만 함 (activeQuizSetPlayingId가 있으면 스킵)
        // 이 조건을 먼저 확인하여 재생 중 퀴즈 이동 로직을 실행
        if (!!currentActiveQuizId && currentActiveQuizId !== currentQuiz.id && currentClass?.classCode && !activeQuizSetPlayingId) {
          // 재생 중인 다른 퀴즈로 이동 - playNewQuiz 실행 후 return
          const classCode = currentClass.classCode;
          console.log('🔄 [재생 중 퀴즈 이동] 시작:', {
            fromQuizId: currentActiveQuizId,
            toQuizId: currentQuiz.id,
            currentMultiAnswer: isMultiAnswerMode,
            currentPublicMode: isPublicMode,
            quizDefaultMulti: currentQuiz.defaultMultiAnswerMode ?? false,
            quizDefaultPublic: currentQuiz.defaultPublicMode ?? false,
          });
          
          // 재생 중인 다른 퀴즈로 이동한 경우, 새로운 퀴즈를 저장된 옵션 값으로 재생
          // loadQuizToEditor가 옵션 값을 설정하므로 여기서는 설정하지 않음
          // loadQuizToEditor 호출하여 UI 업데이트 (옵션 값 포함)
          loadQuizToEditor(currentQuiz, false, activeQuizSetId ?? null);
          
          // playNewQuiz에서 설정한 옵션 값 추적 (listenToActiveQuiz가 덮어쓰지 않도록)
          const defaultMulti = currentQuiz.defaultMultiAnswerMode ?? false;
          const defaultPublic = currentQuiz.defaultPublicMode ?? false;
          pendingQuizOptionsRef.current = {
            quizId: currentQuiz.id,
            multiAnswer: defaultMulti,
            publicMode: defaultPublic,
          };
          console.log('📝 [재생 중 퀴즈 이동] pendingQuizOptionsRef 설정:', pendingQuizOptionsRef.current);
          
          // 새로운 퀴즈를 저장된 옵션 값으로 재생
          const playNewQuiz = async () => {
            try {
              console.log('▶️ [재생 중 퀴즈 이동] playQuiz 호출:', {
                quizId: currentQuiz.id,
                multiAnswer: defaultMulti,
                publicMode: defaultPublic,
              });
              await chatQuizService.playQuiz(
                classCode,
                currentQuiz.id,
                defaultMulti,
                defaultPublic,
                activeQuizSetId || undefined
              );
              console.log('✅ [재생 중 퀴즈 이동] playQuiz 완료');
              // 재생 완료 후 잠시 대기하여 activeId 업데이트 대기
              // 그 후 pendingQuizOptionsRef 초기화
              setTimeout(() => {
                if (activeQuizIdRef.current === currentQuiz.id) {
                  console.log('🧹 [재생 중 퀴즈 이동] pendingQuizOptionsRef 초기화');
                  pendingQuizOptionsRef.current = null;
                }
              }, 300);
            } catch (error) {
              console.error('❌ [재생 중 퀴즈 이동] playQuiz 실패:', error);
              pendingQuizOptionsRef.current = null;
            }
          };
          void playNewQuiz();
          
          // 항상 중앙 카드를 선택 상태로 설정 (네비게이션 이동 시)
          console.log('📝 [displayedQuizIndex useEffect] 재생 중 퀴즈 이동 - currentQuizIndex 업데이트:', {
            from: currentQuizIndex,
            to: displayedQuizIndex,
          });
          setCurrentQuizIndex(displayedQuizIndex);
          return;
        }
        
        // activeQuizId가 null일 때 (클래스챗이 닫혔거나 퀴즈가 중지되었을 때)는 퀴즈 로드 로직을 실행하지 않음 (깜박임 방지)
        if (currentActiveQuizId === null) {
          console.log('⏸️ [displayedQuizIndex useEffect] activeQuizId가 null이므로 퀴즈 로드 로직 스킵 (깜박임 방지)');
          return;
        }
        
        // 이미 같은 퀴즈가 로드되어 있고 인덱스도 같으면 무시 (무한 루프 방지)
        if (loadedQuizIdRef.current === currentQuiz.id) {
          console.log('⏸️ [displayedQuizIndex useEffect] 이미 로드된 퀴즈, 스킵:', {
            loadedQuizId: loadedQuizIdRef.current,
            currentQuizId: currentQuiz.id,
            currentQuizIndex,
            displayedQuizIndex,
          });
          // 인덱스만 업데이트 (이미 로드된 퀴즈)
          // activeQuizId가 null이 아닐 때만 업데이트 (퀴즈 중지 시 깜박임 방지)
          if (currentQuizIndex !== displayedQuizIndex && currentActiveQuizId !== null) {
            console.log('📝 [displayedQuizIndex useEffect] 이미 로드된 퀴즈 - currentQuizIndex 업데이트:', {
              from: currentQuizIndex,
              to: displayedQuizIndex,
              currentActiveQuizId,
            });
            setCurrentQuizIndex(displayedQuizIndex);
          } else if (currentActiveQuizId === null) {
            console.log('⏸️ [displayedQuizIndex useEffect] 이미 로드된 퀴즈 - currentQuizIndex 업데이트 스킵 (activeQuizId가 null):', {
              currentQuizIndex,
              displayedQuizIndex,
            });
          }
          return;
        }
        
        if (!isActiveQuiz) {
          clearResponses();
        }
        // 항상 중앙 카드를 선택 상태로 설정 (네비게이션 이동 시)
        // activeQuizId가 null이 아닐 때만 업데이트 (퀴즈 중지 시 깜박임 방지)
        if (currentActiveQuizId !== null) {
          console.log('📝 [displayedQuizIndex useEffect] currentQuizIndex 업데이트 (activeQuizId가 null이 아님):', {
            from: currentQuizIndex,
            to: displayedQuizIndex,
            currentActiveQuizId,
          });
        setCurrentQuizIndex(displayedQuizIndex);
        } else {
          console.log('⏸️ [displayedQuizIndex useEffect] currentQuizIndex 업데이트 스킵 (activeQuizId가 null):', {
            currentQuizIndex,
            displayedQuizIndex,
            currentActiveQuizId,
          });
        }
        
        // 재생 중이 아닐 때는 서버에서 최신 데이터를 가져와서 로드
        if (!isActiveQuiz && currentClass?.classCode) {
          const classCode = currentClass.classCode; // 타입 가드를 위한 변수
          const loadQuizFromServer = async () => {
            try {
              // activeQuizSetId가 있으면 해당 세트에서 퀴즈를 가져옴
              // activeQuizSetId가 없으면 로컬 데이터만 사용 (main 세트 자동 생성 방지)
              if (activeQuizSetId) {
                // 특정 세트에서 퀴즈 가져오기 (quizzes는 quizId 키 객체)
                const setData = await chatQuizService.getQuizSetById(classCode, activeQuizSetId);
                if (setData && setData.quizzes) {
                  const quizInSet = (setData.quizzes as Record<string, any>)[currentQuiz.id];
                  if (quizInSet) {
                    // ChatQuiz 형식으로 변환
                    const quizData: ChatQuiz = {
                      id: (quizInSet as any).id || currentQuiz.id,
                      type: (quizInSet as any).type,
                      question: (quizInSet as any).question || '',
                      questionImageUrl: (quizInSet as any).questionImageUrl || '',
                      choices: (quizInSet as any).choices || [],
                      correctAnswers: (quizInSet as any).correctAnswers || [],
                      answers: (quizInSet as any).answers || [],
                      guide: (quizInSet as any).guide || '',
                      createdAt: (quizInSet as any).createdAt || currentQuiz.createdAt,
                      updatedAt: (quizInSet as any).updatedAt || currentQuiz.updatedAt,
                      order: (quizInSet as any).order ?? currentQuiz.order,
                      defaultMultiAnswerMode: (quizInSet as any).defaultMultiAnswerMode ?? false,
                      defaultPublicMode: (quizInSet as any).defaultPublicMode ?? false,
                      responses: (quizInSet as any).responses || {},
                      answeredStudents: (quizInSet as any).answeredStudents || [],
                    };
                    // savedQuizzes 업데이트 (프레젠테이션 영역에 최신 데이터 표시)
                    setSavedQuizzes((prev) => {
                      const updated = [...prev];
                      const index = updated.findIndex((q) => q.id === quizData.id);
                      if (index !== -1) {
                        updated[index] = quizData;
                      }
                      return updated;
                    });
                    // 서버에서 가져온 최신 데이터로 로드
                    loadQuizToEditor(quizData, false, activeQuizSetId ?? null);
                    return;
                  }
                }
                // 세트에서 퀴즈를 찾지 못하면 로컬 데이터 사용
                loadQuizToEditor(currentQuiz, false, activeQuizSetId ?? null);
              } else {
                // activeQuizSetId가 없으면 로컬 데이터만 사용 (main 세트 자동 생성 방지)
        loadQuizToEditor(currentQuiz, false, activeQuizContainerId ?? null);
      }
            } catch (error) {
              console.error('❌ 서버에서 퀴즈 로드 실패:', error);
              // 에러 발생 시 로컬 데이터 사용
              loadQuizToEditor(currentQuiz, false, activeQuizSetId ?? activeQuizContainerId ?? null);
            }
          };
          void loadQuizFromServer();
        } else {
          // 재생 중일 때도 퀴즈를 이동하면 저장된 옵션값으로 설정
          // loadQuizToEditor가 퀴즈 변경 시 옵션값을 자동으로 설정함
          loadQuizToEditor(currentQuiz, false, activeQuizSetId ?? activeQuizContainerId ?? null);
        }
      }
    }
  }, [displayedQuizIndex, savedQuizzes, savedQuizzes.length, currentQuizIndex, activeQuizSetId, activeQuizSetPlayingId, activeQuizContainerId, currentClass?.classCode, clearResponses, loadQuizToEditor, isMultiAnswerMode, isPublicMode]); // activeQuizId는 dependency에서 제거 (퀴즈 중지 시 깜박임 방지), isMultiAnswerMode, isPublicMode는 playNewQuiz에서 사용
  
  // 멤버 데이터
  const [students, setStudents] = useState<ClassMember[]>([]);
  const [isLoadingStudents, setIsLoadingStudents] = useState<boolean>(false);

  // 자리 배치 데이터
  const [seatArrangementData, setSeatArrangementData] = useState<any[] | null>(null);
  const [seatArrangementError, setSeatArrangementError] = useState<string>('');
  const [isSeatRotated, setIsSeatRotated] = useState<boolean>(false);

  // 멤버 데이터 로드
  const loadStudents = useCallback(async () => {
    if (!user || !currentClass) {
      setStudents([]);
      return;
    }

    setIsLoadingStudents(true);
    try {
      const members = await userDataService.loadClassMembers(user.id, currentClass.id, currentMemberGroup);
      setStudents(members);
      console.log('✅ 멤버 데이터 로드 완료:', members.length);
    } catch (error) {
      console.error('❌ 멤버 데이터 로드 실패:', error);
      setStudents([]);
    } finally {
      setIsLoadingStudents(false);
    }
  }, [user, currentClass, currentMemberGroup]);

  // students를 ref로 추적하여 무한 루프 방지
  const studentsRef = useRef<ClassMember[]>([]);
  useEffect(() => {
    studentsRef.current = students;
  }, [students]);

  // 자리 배치 데이터 로드
  const loadSeatArrangementData = useCallback(async () => {
    if (!user || !currentClass) {
      setSeatArrangementError('클래스 정보를 찾을 수 없습니다.');
      return;
    }

    try {
      const { ClassMembersPartitionService } = await import('../services/classMembersPartitionService');
      const partitionService = new ClassMembersPartitionService(user.id, currentClass.id, currentMemberGroup);
      const members = await partitionService.loadAllMembers();

      if (members.length === 0) {
        setSeatArrangementError('자리배치를 먼저 해주세요');
        setSeatArrangementData(null);
        return;
      }
      
      // 현재 월에 해당하는 자리 배치 찾기
      const currentDate = new Date();
      const currentMonth = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
      
      // 자리 배치 데이터가 있는 월들 찾기
      const availableMonths = new Set<string>();
      members.forEach((member: any) => {
        if (member.seatPositions && typeof member.seatPositions === 'object') {
          Object.keys(member.seatPositions).forEach((month: string) => {
            availableMonths.add(month);
          });
        }
      });

      let selectedMonth = currentMonth;
      
      // 현재 월이 없으면 가장 최근 월 선택
      if (!availableMonths.has(currentMonth)) {
        const sortedMonths = Array.from(availableMonths).sort().reverse();
        if (sortedMonths.length === 0) {
          setSeatArrangementError('자리배치를 먼저 해주세요');
          setSeatArrangementData(null);
          return;
        }
        selectedMonth = sortedMonths[0];
      }

      // 선택된 월의 자리 배치 데이터 추출 (ref를 통해 최신 students 참조)
      const currentStudents = studentsRef.current;
      const seatData = currentStudents.map((student) => {
        const member = members.find((m: any) => m.id === student.id);
        const seatPosition = member?.seatPositions?.[selectedMonth];
        
        return {
          studentId: student.id,
          name: student.name || '이름 없음',
          number: student.number || '',
          seatRow: seatPosition?.row || 0,
          seatCol: seatPosition?.col || 0,
          profileImage: student.profileImage,
          photoProfileUrl: student.photoProfileUrl,
        };
      }).filter((seat) => seat.seatRow !== 0 || seat.seatCol !== 0); // 자리가 설정된 멤버만

      setSeatArrangementData(seatData);
      setSeatArrangementError('');
    } catch (error) {
      console.error('❌ 자리 배치 데이터 로드 실패:', error);
      setSeatArrangementError('자리배치를 먼저 해주세요');
      setSeatArrangementData(null);
    }
  }, [user, currentClass, currentMemberGroup]); // students를 dependency에서 제거하여 무한 루프 방지

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  useEffect(() => {
    if (viewMode === 'seat' && students.length > 0) {
      loadSeatArrangementData();
    }
  }, [viewMode, students.length, loadSeatArrangementData]); // students.length만 사용하여 무한 루프 방지

  const handleOpenChat = useCallback(async () => {
    if (!currentClass?.classCode) {
      alert('클래스 코드가 없습니다.');
      return;
    }

    // 프리 모드 체크
    if (user.userType === 'free') {
      const accessCheck = checkChatAccess(user);
      setLicenseLimitMessage(accessCheck.message || '');
      setShowLicenseLimitModal(true);
      return;
    }

    try {
      // ✅ 클래스챗 시작
      await chatQuizService.openChat(currentClass.classCode, currentMemberGroup);

      // ✅ UI 상태 업데이트 (서버 상태와 동기화)
      setIsChatOpen(true);
    } catch (error) {
      console.error('❌ 클래스챗 시작 실패:', error);
      alert('클래스챗를 시작할 수 없습니다.');
    }
  }, [currentClass?.classCode, user, currentMemberGroup]);

  const handleCloseChat = useCallback(async () => {
    if (!currentClass?.classCode) {
      return;
    }
    try {
      // 모든 재생 상태를 중지 (퀴즈, 세트, 학생 문제 만들기)
      await chatQuizService.stopQuiz(currentClass.classCode);
      await chatQuizService.closeChat(currentClass.classCode);
      await chatQuizService.setStudentCreateMode(currentClass.classCode, false, null);
      
      // 모든 재생 관련 상태 초기화
      setIsStudentCreateMode(false);
      setActiveQuizPlayingId(null);
      setActiveQuizSetPlayingId(null);
      setActiveQuizId(null);
      setActiveQuizContainerId(null);
      // currentQuizIndex는 유지 (퀴즈 선택 상태 보존)
      
      // 챗을 닫아도 저장된 응답들은 유지 (clearResponses() 제거)
    } catch (error) {
      console.error('❌ 클래스챗 닫기 실패:', error);
      alert('클래스챗를 닫을 수 없습니다.');
    }
  }, [currentClass?.classCode]);

  // 클래스챗이 열려있지만 퀴즈 재생이 없는 상태가 30분 지속되면 자동으로 닫기
  // - Cloud Functions 없이(서버 인프라 없이) 선생님 클라이언트에서만 수행
  // - 선생님 페이지가 켜져 있을 때만 동작
  const idleAutoCloseTimerRef = useRef<number | null>(null);
  useEffect(() => {
    const classCode = currentClass?.classCode;
    if (!classCode) return;

    // 기존 타이머 정리
    if (idleAutoCloseTimerRef.current) {
      window.clearTimeout(idleAutoCloseTimerRef.current);
      idleAutoCloseTimerRef.current = null;
    }

    // 조건: 챗 오픈 && 퀴즈/세트 재생 없음
    // - 단일 퀴즈 재생 중: activeQuizId가 있음
    // - 세트 재생 중: activeQuizSetPlayingId가 있음 (이 경우 activeQuizId도 있음)
    if (!isChatOpen) return;
    if (activeQuizId || activeQuizSetPlayingId) return;

    // 30분 후 자동 닫기
    idleAutoCloseTimerRef.current = window.setTimeout(async () => {
      try {
        // 타이머 시점에 상태가 바뀌었을 수 있으니 마지막으로 가드
        if (!currentClass?.classCode) return;
        // activeQuizIdRef는 ref이므로 최신 activeQuizId를 반영
        // activeQuizSetPlayingId는 state이므로 현재 값 확인 필요 (하지만 ref로 추적하지 않으므로 현재 값 사용)
        if (activeQuizIdRef.current) return;
        // 다른 그룹이 활성화되었을 수도 있으므로(그룹 전환), 현재 그룹 기준으로 한 번 더 확인
        if (!currentMemberGroup) return;
        console.log('⏰ [AutoCloseChat] 30분 동안 퀴즈 미재생 상태 → 클래스챗 자동 닫기');
        await chatQuizService.stopQuiz(currentClass.classCode);
        await chatQuizService.closeChat(currentClass.classCode);
        await chatQuizService.setStudentCreateMode(currentClass.classCode, false, null);
      } catch (error) {
        console.error('❌ [AutoCloseChat] 자동 닫기 실패:', error);
      }
    }, 30 * 60 * 1000);

    return () => {
      if (idleAutoCloseTimerRef.current) {
        window.clearTimeout(idleAutoCloseTimerRef.current);
        idleAutoCloseTimerRef.current = null;
      }
    };
  }, [currentClass?.classCode, isChatOpen, activeQuizId, activeQuizSetPlayingId, currentMemberGroup]);

  // 퀴즈 로드
  // displayedQuizIndex를 ref로 추적하여 무한 루프 방지
  const displayedQuizIndexRef = useRef<number>(0);
  useEffect(() => {
    displayedQuizIndexRef.current = displayedQuizIndex;
  }, [displayedQuizIndex]);

  useEffect(() => {
    const loadQuizzes = async () => {
      if (!currentClass || !currentClass.classCode) return;
      
      // 세트 재생 중이거나 세트 모드일 때는 단일 퀴즈 목록을 로드하지 않음
      // (세트의 퀴즈 목록은 handlePlayQuizSet, handleSelectSetIndex 등에서 관리)
      // activeQuizSetId가 설정되어 있으면 세트 모드로 간주하여 로드하지 않음
      if (activeQuizSetPlayingId || isSetMode || activeQuizSetId) {
        return;
      }
      
      // activeQuizSetId가 없으면 빈 배열로 설정 (main 세트는 더 이상 사용하지 않음)
      try {
        setSavedQuizzes([]);
        console.log('✅ 퀴즈 로드 완료: 0 (세트 모드가 아님)');
          setDisplayedQuizIndex(0); // 문제 생성 버튼만 있음
      } catch (error) {
        console.error('❌ 퀴즈 로드 실패:', error);
      }
    };

    loadQuizzes();
  }, [currentClass, activeQuizSetPlayingId, isSetMode, activeQuizSetId]); // displayedQuizIndex를 dependency에서 제거하여 무한 루프 방지

  // 활성 퀴즈 실시간 리스너
  const previousDisplayedIndexRef = useRef<number>(0); // 취소 시 복원할 인덱스
  const activeQuizIdRef = useRef<string | null>(null);
  // displayedQuizIndexRef는 위에서 이미 선언됨 (5564번 줄)
  const isQuizNavigationInProgressRef = useRef<boolean>(false); // 퀴즈 이동 중 플래그
  const pendingQuizOptionsRef = useRef<{ quizId: string | null; multiAnswer?: boolean; publicMode?: boolean } | null>(null); // playNewQuiz에서 설정한 옵션 값 추적
  useEffect(() => {
    savedQuizzesRef.current = savedQuizzes;
  }, [savedQuizzes]);
  useEffect(() => {
    activeQuizIdRef.current = activeQuizId;
  }, [activeQuizId]);
  useEffect(() => {
    // displayedQuizIndex가 변경되었는지 확인
    if (displayedQuizIndexRef.current !== displayedQuizIndex) {
      displayedQuizIndexRef.current = displayedQuizIndex;
      // 재생 중이 아닐 때만 플래그 설정 (재생 중일 때는 playNewQuiz에서 관리)
      // 재생 중이 아닐 때는 loadQuizToEditor가 옵션 값을 설정할 시간을 줌
      if (!activeQuizId) {
        isQuizNavigationInProgressRef.current = true;
        setTimeout(() => {
          isQuizNavigationInProgressRef.current = false;
        }, 100);
      }
    }
  }, [displayedQuizIndex, activeQuizId]);

  useEffect(() => {
    if (!currentClass || !currentClass.classCode || !isChatOpen) return;

    const unsubscribe = chatQuizService.listenToActiveQuiz(
      currentClass.classCode,
      (quiz, activeId, isMultiAnswerModeFromServer, isPublicModeFromServer, metaInfo) => {
        console.log('🔔 [listenToActiveQuiz] 콜백 시작:', {
          activeId,
          hasQuiz: !!quiz,
          quizId: quiz?.id,
          currentQuizIndexBefore: currentQuizIndex,
          displayedQuizIndexBefore: displayedQuizIndex,
        });
        
        setActiveQuizId(activeId);
        activeQuizIdRef.current = activeId; // ref 업데이트
        
        // playNewQuiz에서 설정한 옵션 값과 서버 값이 일치하는지 확인
        const pendingOptions = pendingQuizOptionsRef.current;
        const isPendingQuiz = pendingOptions && pendingOptions.quizId === activeId;
        
        console.log('🔔 [listenToActiveQuiz] 서버 값 수신:', {
          activeId,
          isMultiAnswerModeFromServer,
          isPublicModeFromServer,
          isPendingQuiz,
          pendingOptions,
          currentMulti: isMultiAnswerMode,
          currentPublic: isPublicMode,
        });
        
        // pendingQuizOptionsRef가 설정되어 있으면 (퀴즈 이동 중), 
        // 서버 값이 pending 값과 일치할 때까지 업데이트를 스킵
        if (isPendingQuiz) {
          const matchesPending = 
            (isMultiAnswerModeFromServer === undefined || isMultiAnswerModeFromServer === pendingOptions.multiAnswer) &&
            (isPublicModeFromServer === undefined || isPublicModeFromServer === pendingOptions.publicMode);
          
          if (matchesPending) {
            // 서버 값이 pending 값과 일치하면 초기화하고 정상 업데이트 진행
            console.log('✅ [listenToActiveQuiz] 서버 값이 pending 값과 일치, 초기화 후 업데이트:', {
              activeId,
              isMultiAnswerModeFromServer,
              isPublicModeFromServer,
              pendingOptions,
            });
            pendingQuizOptionsRef.current = null;
            // 이제 정상적으로 업데이트 (아래 로직 계속 실행)
          } else {
            // 서버 값이 pending 값과 다르면 (중간 상태일 수 있음) 스킵
            console.log('⏸️ [listenToActiveQuiz] pending 값과 다름, 업데이트 스킵 (중간 상태일 수 있음):', {
              activeId,
              isMultiAnswerModeFromServer,
              isPublicModeFromServer,
              pendingOptions,
            });
            return; // 이번 콜백은 무시
          }
        }
        
        // 재생 중일 때만 서버 값으로 멀티/공개 옵션 동기화 (미재생 시 사용자가 토글한 값 유지)
        if (activeId && isMultiAnswerModeFromServer !== undefined) {
          setIsMultiAnswerMode((prev) => {
            const shouldUpdate = prev !== isMultiAnswerModeFromServer;
            if (shouldUpdate) {
              console.log('🔄 [listenToActiveQuiz] 멀티응답 옵션 업데이트:', {
                from: prev,
                to: isMultiAnswerModeFromServer,
              });
            } else {
              console.log('⏸️ [listenToActiveQuiz] 멀티응답 옵션 업데이트 스킵 (prev와 동일):', prev);
            }
            return shouldUpdate ? isMultiAnswerModeFromServer : prev;
          });
        }
        if (activeId && isPublicModeFromServer !== undefined) {
          setIsPublicMode((prev) => {
            const shouldUpdate = prev !== isPublicModeFromServer;
            if (shouldUpdate) {
              console.log('🔄 [listenToActiveQuiz] 공개모드 옵션 업데이트:', {
                from: prev,
                to: isPublicModeFromServer,
              });
            } else {
              console.log('⏸️ [listenToActiveQuiz] 공개모드 옵션 업데이트 스킵 (prev와 동일):', prev);
            }
            return shouldUpdate ? isPublicModeFromServer : prev;
          });
        }
        if (metaInfo) {
          setIsChatOpen((prev) => {
            const newValue = metaInfo.isChatOpen ?? false;
            return prev !== newValue ? newValue : prev;
          });
          if (metaInfo.studentCreateMode !== undefined) {
            setIsStudentCreateMode(!!metaInfo.studentCreateMode);
          }
          // 재생 상태는 서버 메타를 단일 진실(SSOT)로 사용한다.
          // - 세트 재생 중: metaInfo.activeQuizSetId === setId (truthy)
          // - 단일 퀴즈 재생 중: metaInfo.activeQuizSetId === null
          const serverSetPlayingId = metaInfo.activeQuizSetId ?? null;
          setActiveQuizSetPlayingId(serverSetPlayingId);
          setActiveQuizPlayingId(serverSetPlayingId ? null : (activeId ?? null));
          const serverContainerId = (metaInfo as any).activeQuizContainerId ?? serverSetPlayingId ?? null;
          setActiveQuizContainerId(serverContainerId);
          setConnectedStudentsMap((prev) => {
            const newValue = metaInfo.connectedStudents ?? {};
            // 객체 비교를 위해 JSON.stringify 사용 (간단한 비교)
            if (JSON.stringify(prev) !== JSON.stringify(newValue)) {
              return newValue;
            }
            return prev;
          });
        }
        if (activeId && quiz) {
          // 활성 퀴즈 인덱스 — ref는 loadQuizSets에서 setState와 동시에 갱신됨(재생 직전 목록 동기화)
          const trySyncCarousel = (): boolean => {
            const index = savedQuizzesRef.current.findIndex((q) => q.id === activeId);
            console.log('🔍 [listenToActiveQuiz] 활성 퀴즈 인덱스 찾기:', {
              activeId,
              quizId: quiz.id,
              foundIndex: index,
              currentQuizIndex,
              displayedQuizIndex,
            });
            if (index === -1) {
              return false;
            }
            console.log('✅ [listenToActiveQuiz] 캐러셀을 활성 퀴즈에 맞춤:', { toIndex: index });
            setCurrentQuizIndex(index);
            setDisplayedQuizIndex(index);
            return true;
          };
          if (!trySyncCarousel()) {
            // React 커밋 직후 ref가 맞춰지는 한 틱 지연 재시도 (새 저장 직후 재생 등)
            queueMicrotask(() => {
              if (!trySyncCarousel()) {
                window.setTimeout(() => {
                  trySyncCarousel();
                }, 0);
              }
            });
          }
        } else {
          console.log('⏸️ [listenToActiveQuiz] activeId나 quiz가 없어서 인덱스 업데이트 스킵:', {
            activeId,
            hasQuiz: !!quiz,
            currentQuizIndex,
            displayedQuizIndex,
          });
        }
        
        console.log('🔔 [listenToActiveQuiz] 콜백 종료:', {
          activeId,
          currentQuizIndex,
          displayedQuizIndex,
        });
      },
      currentMemberGroup
    );

    return () => unsubscribe();
  }, [currentClass, currentClass?.classCode, currentMemberGroup, isChatOpen, currentQuizIndex, displayedQuizIndex, isMultiAnswerMode, isPublicMode]);

  // applyResponses를 ref로 추적하여 무한 루프 방지
  const applyResponsesRef = useRef(applyResponses);
  useEffect(() => {
    applyResponsesRef.current = applyResponses;
  }, [applyResponses]);

  // 응답 실시간 리스너 (활성 퀴즈 전용)
  useEffect(() => {
    if (!currentClass?.classCode || !activeQuizId) return;
    const classCode = currentClass.classCode;
    const subscribedQuizId = activeQuizId;

    // 문제세트 모드일 때 activeQuizSetId를 전달하여 해당 세트에서 응답 구독
    const setId = activeQuizSetId || undefined;
    const unsubscribe = chatQuizService.listenToResponses(
      classCode,
      subscribedQuizId,
      (responses, answered) => {
        // 재생 중 캐러셀을 먼저 옮기면 서버 activeId·응답 구독은 잠시 이전 퀴즈일 수 있음.
        // 캐러셀 중앙과 구독 quizId가 다르면 적용하지 않음(옛 스냅샷이 getQuizResponses 결과를 덮어 깜박임).
        if (subscribedQuizId !== displayedCenterQuizIdRef.current) {
          return;
        }
        applyResponsesRef.current(subscribedQuizId, responses || {}, answered || []);
      },
      setId,
      currentMemberGroup
    );

    return () => unsubscribe();
  }, [currentClass?.classCode, activeQuizId, activeQuizSetId, currentMemberGroup]); // applyResponses 제거

  // 활성 퀴즈가 아닐 때 선택한 카드의 응답을 단발성으로 로드
  useEffect(() => {
    if (!currentClass?.classCode) return;
    const classCode = currentClass.classCode;
 
    if (currentQuizIndex === -1 || currentQuizIndex >= savedQuizzes.length || currentQuizIndex < 0) {
      if (!activeQuizId) {
        clearResponses();
      }
      return;
    }

    const selectedQuiz = savedQuizzes[currentQuizIndex];
    if (!selectedQuiz) {
      if (!activeQuizId) {
        clearResponses();
      }
      return;
    }

    const quizId = selectedQuiz.id;

    // 활성 퀴즈와 동일한 경우 실시간 리스너가 처리
    if (activeQuizId && activeQuizId === quizId) {
      return;
    }

    let cancelled = false;

    const loadResponses = async () => {
      try {
        // 문제세트 모드일 때 activeQuizSetId를 전달하여 해당 세트에서 응답 가져오기
        const setId = activeQuizSetId || undefined;
        const { responses, answeredStudents: answered } = await chatQuizService.getQuizResponses(classCode, quizId, setId);
        if (cancelled) return;
        // ref를 통해 최신 applyResponses 사용 (의존성 루프 방지)
        applyResponsesRef.current(quizId, responses || {}, answered || []);
      } catch (error) {
        if (!cancelled) {
          console.error('❌ 퀴즈 응답 로드 실패:', error);
        }
      }
    };

    loadResponses();

    return () => {
      cancelled = true;
    };
  }, [currentClass?.classCode, currentQuizIndex, savedQuizzes, activeQuizId, activeQuizSetId, clearResponses]);

  // 퀴즈 저장
  const handleSaveQuiz = async () => {
    if (isSavingQuizRef.current) {
      return;
    }
    if (!currentClass || (!messageText.trim() && !questionImageUrl)) {
      alert('문제를 입력하거나 이미지를 추가하세요.');
      return;
    }

    if (!currentClass.classCode) {
      alert('클래스 코드가 없습니다. 클래스 설정을 확인해주세요.');
      return;
    }

    isSavingQuizRef.current = true;
    try {
      const controlSettings = {
        multiAnswerMode: editorMultiAnswerSetting,
        publicMode: editorPublicModeSetting,
      };
      let quizId: string;

      // 기존 문제가 선택되어 있으면 수정, 아니면 새로 생성
      const isEditing = currentQuizIndex >= 0 && currentQuizIndex < savedQuizzes.length;
      const existingQuiz = isEditing ? savedQuizzes[currentQuizIndex] : null;

      if (isEditorLocked && isEditing) {
        alert('재생 중에는 현재 문제를 수정할 수 없습니다. 퀴즈를 먼저 정지해주세요.');
        return;
      }

      if (isEditing && existingQuiz) {
        // 기존 퀴즈 수정
        if (questionType === 'choice') {
          if (choiceOptions.length < 2) {
            alert('최소 2개의 선택지가 필요합니다.');
            return;
          }
          if (correctAnswers.length === 0) {
            alert('정답을 선택하세요.');
            return;
          }

          await chatQuizService.updateQuiz(
            currentClass.classCode,
            existingQuiz.id,
            'choice',
            messageText,
            {
              questionImageUrl: questionImageUrl || undefined,
              choices: choiceOptions, // 공란 포함하여 전체 저장
              correctAnswers: correctAnswers.length > 0 ? correctAnswers : undefined,
              controlSettings,
              setId: activeQuizSetId || undefined, // 현재 선택된 세트 ID 전달
            }
          );
          quizId = existingQuiz.id;
        } else if (questionType === 'short') {
          const filteredShort = answerBlocks.filter((a) => a && a.trim() !== '');
          if (filteredShort.length === 0) {
            alert('정답을 입력하세요.');
            return;
          }
          await chatQuizService.updateQuiz(
            currentClass.classCode,
            existingQuiz.id,
            'short',
            messageText,
            {
              questionImageUrl: questionImageUrl || undefined,
              answers: filteredShort,
              controlSettings,
              setId: activeQuizSetId || undefined, // 현재 선택된 세트 ID 전달
            }
          );
          quizId = existingQuiz.id;
        } else {
          await chatQuizService.updateQuiz(
            currentClass.classCode,
            existingQuiz.id,
            'essay',
            messageText,
            {
              questionImageUrl: questionImageUrl || undefined,
              guide: guideMessage && guideMessage.trim() ? guideMessage.trim() : undefined,
              controlSettings,
              setId: activeQuizSetId || undefined, // 현재 선택된 세트 ID 전달
            }
          );
          quizId = existingQuiz.id;
        }

        alert('퀴즈가 수정되었습니다.');
        
        // 기존 문제 수정 시
        // activeQuizSetId가 있으면 해당 세트를 다시 로드
        if (activeQuizSetId) {
          await loadQuizSets(activeQuizSetId);
        } else {
          // activeQuizSetId가 없으면 빈 배열로 설정 (main 세트는 더 이상 사용하지 않음)
          setSavedQuizzes([]);
        }
      } else {
        // 새 퀴즈 생성
        const shouldAutoPlayAfterSave = isQuizPlaying;

        if (questionType === 'choice') {
          if (choiceOptions.length < 2) {
            alert('최소 2개의 선택지가 필요합니다.');
            return;
          }
          if (correctAnswers.length === 0) {
            alert('정답을 선택하세요.');
            return;
          }

          quizId = await chatQuizService.saveQuiz(
            currentClass.classCode,
            'choice',
            messageText,
            {
              questionImageUrl: questionImageUrl || undefined,
              choices: choiceOptions, // 공란 포함하여 전체 저장
              correctAnswers: correctAnswers.length > 0 ? correctAnswers : undefined,
              controlSettings,
              setId: activeQuizSetId || undefined, // 현재 선택된 세트 ID 전달
            }
          );
        } else if (questionType === 'short') {
          const filteredShort = answerBlocks.filter((a) => a && a.trim() !== '');
          if (filteredShort.length === 0) {
            alert('정답을 입력하세요.');
            return;
          }
          quizId = await chatQuizService.saveQuiz(
            currentClass.classCode,
            'short',
            messageText,
            {
              questionImageUrl: questionImageUrl || undefined,
              answers: filteredShort,
              controlSettings,
              setId: activeQuizSetId || undefined, // 현재 선택된 세트 ID 전달
            }
          );
        } else {
          quizId = await chatQuizService.saveQuiz(
            currentClass.classCode,
            'essay',
            messageText,
            {
              questionImageUrl: questionImageUrl || undefined,
              guide: guideMessage && guideMessage.trim() ? guideMessage.trim() : undefined,
              controlSettings,
              setId: activeQuizSetId || undefined, // 현재 선택된 세트 ID 전달
            }
          );
        }

        // 저장 직후 ref 갱신 — 미갱신 시 재생이 다시 saveQuiz를 타며 동일 내용 퀴즈가 중복 생성될 수 있음
        loadedQuizIdRef.current = quizId;
        loadedQuizSetIdRef.current = activeQuizSetId ?? null;

        alert(shouldAutoPlayAfterSave ? '퀴즈가 저장되었고 곧바로 재생됩니다.' : '퀴즈가 저장되었습니다.');
        
        // 새 문제 생성 시
        // activeQuizSetId가 있으면 해당 세트를 다시 로드
        if (activeQuizSetId) {
          const refreshed = await loadQuizSets(activeQuizSetId);
          const idx = refreshed?.findIndex((q) => q.id === quizId) ?? -1;
          if (idx >= 0) {
            setCurrentQuizIndex(idx);
            setDisplayedQuizIndex(idx);
          }
        } else {
          // activeQuizSetId가 없으면 빈 배열로 설정 (main 세트는 더 이상 사용하지 않음)
          setSavedQuizzes([]);
        }

        if (shouldAutoPlayAfterSave) {
          try {
            await chatQuizService.playQuiz(
              currentClass.classCode,
              quizId,
              isMultiAnswerMode,
              isPublicMode,
              activeQuizSetId || undefined // 현재 선택된 세트 ID 전달
            );
          } catch (error) {
            console.error('❌ 새 퀴즈 자동 재생 실패:', error);
            alert('퀴즈는 저장되었지만 자동 재생에는 실패했습니다. 재생 버튼을 눌러주세요.');
          }
        }

        // 새 문제 생성 시에도 모달을 닫고 폼을 초기화
      }

      setIsQuizModalOpen(false);
      setEditingQuizId(null);
    } catch (error) {
      console.error('❌ 퀴즈 저장/수정 실패:', error);
      alert('퀴즈 저장/수정에 실패했습니다.');
    } finally {
      isSavingQuizRef.current = false;
    }
  };

  // 퀴즈 정지 (단일)
  const handleStopQuiz = useCallback(async () => {
    if (!currentClass || !currentClass.classCode) return;

    console.log('🛑 [handleStopQuiz] 퀴즈 정지 시작:', {
      currentQuizIndex,
      displayedQuizIndex,
      activeQuizId: activeQuizIdRef.current,
      activeQuizPlayingId,
      activeQuizSetPlayingId,
    });

    try {
      await chatQuizService.stopQuiz(currentClass.classCode);
      console.log('✅ [handleStopQuiz] stopQuiz 호출 완료');
      
      console.log('📝 [handleStopQuiz] 상태 업데이트 전:', {
        currentQuizIndex,
        displayedQuizIndex,
      });
      
      setActiveQuizPlayingId(null);
      setActiveQuizSetPlayingId(null);
      setActiveQuizId(null);
      activeQuizIdRef.current = null; // ref도 업데이트
      
      console.log('📝 [handleStopQuiz] 상태 업데이트 후 (activeQuizId = null):', {
        currentQuizIndex,
        displayedQuizIndex,
      });
      // setCurrentQuizIndex(-1); // 퀴즈 선택 상태는 유지 (깜박임 방지)
      showToastMessage('퀴즈가 정지되었습니다.');
    } catch (error) {
      console.error('❌ 퀴즈 정지 실패:', error);
      showToastMessage('퀴즈 정지에 실패했습니다.');
    }
  }, [currentClass, showToastMessage, currentQuizIndex, displayedQuizIndex, activeQuizPlayingId, activeQuizSetPlayingId]);

  const handleStopStudentCreateMode = useCallback(async () => {
    if (!currentClass?.classCode) return;
    try {
      await chatQuizService.setStudentCreateMode(currentClass.classCode, false, null);
      setIsStudentCreateMode(false);
      showToastMessage('학생 문제 만들기 모드를 종료했습니다.');
      // 학생이 만든 문제가 반영된 세트 데이터를 서버에서 다시 로드
      await loadQuizSets(activeQuizSetId ?? undefined);
    } catch (error) {
      console.error('❌ 학생 문제 만들기 모드 종료 실패:', error);
      showToastMessage('학생 문제 만들기 모드를 종료하지 못했습니다.');
    }
  }, [currentClass?.classCode, showToastMessage, loadQuizSets, activeQuizSetId]);

  // 퀴즈 재생 (저장하지 않고 바로 재생 가능)
  const handlePlayQuiz = async () => {
    if (!currentClass || !currentClass.classCode) return;

    if (isPlayingQuizRef.current) {
      return;
    }
    
    if (!isChatOpen) {
      showToastMessage('클래스챗를 시작한 후 퀴즈를 재생할 수 있습니다.');
      return;
    }

    // 다른 재생 모드 정지 (세트/학생 문제 만들기)
    // 세트 "선택" 상태(activeQuizSetId)가 아니라, 실제 세트 "재생" 상태(activeQuizSetPlayingId)만을 기준으로 정지한다.
    if (activeQuizSetPlayingId) {
      await handleStopQuizSet();
    }
    if (isStudentCreateMode) {
      await handleStopStudentCreateMode();
    }

    // 단일 퀴즈 재생 모드로 전환
    setIsSetMode(false);

    const safeMessage = (messageText || '').trim();
    const isNewCardSlot = displayedQuizIndex === savedQuizzes.length;

    // 기존 문제가 로드되어 있는지 확인 (loadedQuizIdRef 사용)
    // 재생 종료 후 currentQuizIndex가 -1이어도 loadedQuizIdRef는 유지됨
    // 새 문제 슬롯이면 ref를 쓰지 않음(직전 퀴즈로 잘못 재생되는 것 방지).
    // 저장 직후에는 ref가 새 퀴즈 ID인데 캐러셀 인덱스가 아직 옛 카드를 가리키는 경우가 있어,
    // "중앙 카드 우선"으로 ref를 덮어쓰면 안 됨(기존 퀴즈가 재생됨).
    if (loadedQuizIdRef.current && !isNewCardSlot) {
      const existingQuizId = loadedQuizIdRef.current;

      // 단일 퀴즈 재생: 로드된 퀴즈가 속한 세트 ID 사용 (1회 읽기만 하도록)
      const currentSetId = loadedQuizSetIdRef.current ?? activeQuizSetId ?? activeQuizContainerId ?? undefined;
      if (!currentSetId) {
        showToastMessage('퀴즈를 재생할 세트를 찾을 수 없습니다.');
        return;
      }

      // 기존 문제이므로 저장하지 않고 바로 재생
      // 퀴즈의 저장된 옵션 값을 사용 (현재 상태 값이 아닌)
      const existingQuiz = savedQuizzes.find(q => q.id === existingQuizId);
      const quizMultiAnswerMode = existingQuiz?.defaultMultiAnswerMode ?? isMultiAnswerMode;
      const quizPublicMode = existingQuiz?.defaultPublicMode ?? isPublicMode;
      
      isPlayingQuizRef.current = true;
      try {
        console.log('▶️ 기존 퀴즈 재생:', { 
          quizId: existingQuizId, 
          quizDefaultMulti: existingQuiz?.defaultMultiAnswerMode,
          quizDefaultPublic: existingQuiz?.defaultPublicMode,
          usingMulti: quizMultiAnswerMode,
          usingPublic: quizPublicMode,
          currentStateMulti: isMultiAnswerMode,
          currentStatePublic: isPublicMode,
          currentSetId 
        });
        await chatQuizService.playQuiz(
          currentClass.classCode,
          existingQuizId,
          quizMultiAnswerMode,
          quizPublicMode,
          currentSetId
        );
        setActiveQuizId(existingQuizId);
        setActiveQuizPlayingId(existingQuizId);
        setActiveQuizSetPlayingId(null);
        showToastMessage('퀴즈가 시작되었습니다.');
        return;
      } catch (error) {
        console.error('❌ 퀴즈 재생 실패:', error);
        showToastMessage('퀴즈 재생에 실패했습니다.');
        return;
      } finally {
        isPlayingQuizRef.current = false;
      }
    }

    // 입력된 문제가 있으면 임시 저장 후 바로 재생 (새 문제이거나 수정된 경우)
    if (safeMessage) {
      try {
        let quizId: string;

        // 단일 퀴즈 재생: 새 퀴즈는 현재 선택된 세트에 저장 (activeQuizSetId 사용)
        // activeQuizSetId가 없으면 에러 (main을 기본값으로 사용하지 않음)
        const currentSetId = activeQuizSetId || undefined;
        if (!currentSetId) {
          showToastMessage('퀴즈를 저장할 세트를 선택하세요.');
          return;
        }

        // 입력된 내용을 임시로 저장 (실시간 재생을 위해) — 동기 검증만 먼저 수행
        if (questionType === 'choice') {
          if (choiceOptions.length < 2) {
            showToastMessage('최소 2개의 선택지가 필요합니다.');
            return;
          }
          if (correctAnswers.length === 0) {
            showToastMessage('정답을 선택하세요.');
            return;
          }
        } else if (questionType === 'short') {
          const filteredShort = answerBlocks.filter((a) => a && a.trim() !== '');
          if (filteredShort.length === 0) {
            showToastMessage('정답을 입력하세요.');
            return;
          }
        }

        isPlayingQuizRef.current = true;
        try {
          if (questionType === 'choice') {
            quizId = await chatQuizService.saveQuiz(
              currentClass.classCode,
              'choice',
              messageText,
              {
                choices: choiceOptions,
                correctAnswers: correctAnswers.length > 0 ? correctAnswers : undefined,
                controlSettings: {
                  multiAnswerMode: isMultiAnswerMode,
                  publicMode: isPublicMode,
                },
                setId: currentSetId,
              }
            );
          } else if (questionType === 'short') {
            const filteredShort = answerBlocks.filter((a) => a && a.trim() !== '');
            quizId = await chatQuizService.saveQuiz(
              currentClass.classCode,
              'short',
              messageText,
              {
                answers: filteredShort,
                controlSettings: {
                  multiAnswerMode: isMultiAnswerMode,
                  publicMode: isPublicMode,
                },
                setId: currentSetId,
              }
            );
          } else {
            quizId = await chatQuizService.saveQuiz(
              currentClass.classCode,
              'essay',
              messageText,
              {
                guide: guideMessage && guideMessage.trim() ? guideMessage.trim() : undefined,
                controlSettings: {
                  multiAnswerMode: isMultiAnswerMode,
                  publicMode: isPublicMode,
                },
                setId: currentSetId,
              }
            );
          }

          loadedQuizIdRef.current = quizId;
          loadedQuizSetIdRef.current = currentSetId;

          // playQuiz보다 먼저 목록을 맞춤 — 그렇지 않으면 listenToActiveQuiz가 옛 목록으로 findIndex해
          // 캐러셀이 이전 카드에 머무는 현상이 남음
          if (activeQuizSetId) {
            const refreshed = await loadQuizSets(activeQuizSetId);
            const index = refreshed?.findIndex((q) => q.id === quizId) ?? -1;
            if (index !== -1) {
              setCurrentQuizIndex(index);
              setDisplayedQuizIndex(index);
            }
          } else {
            savedQuizzesRef.current = [];
            setSavedQuizzes([]);
          }

          console.log('▶️ 새 퀴즈 저장 및 재생:', { quizId, isMultiAnswerMode, isPublicMode, currentSetId });
          await chatQuizService.playQuiz(
            currentClass.classCode,
            quizId,
            isMultiAnswerMode,
            isPublicMode,
            currentSetId
          );
          setActiveQuizPlayingId(quizId);
          setActiveQuizSetPlayingId(null);
          showToastMessage('퀴즈가 시작되었습니다.');
        } finally {
          isPlayingQuizRef.current = false;
        }
      } catch (error) {
        console.error('❌ 퀴즈 재생 실패:', error);
        showToastMessage('퀴즈 재생에 실패했습니다.');
      }
    } else {
      // 현재 선택된 퀴즈 재생
      const currentQuiz = savedQuizzes[currentQuizIndex];
      if (!currentQuiz) {
        showToastMessage('재생할 퀴즈를 선택하세요.');
        return;
      }

      const containerId = activeQuizContainerId ?? activeQuizSetId;
      if (!containerId) {
        showToastMessage('퀴즈를 재생할 세트를 찾을 수 없습니다.');
        return;
      }

      isPlayingQuizRef.current = true;
      try {
        console.log('▶️ 현재 선택된 퀴즈 재생:', { quizId: currentQuiz.id, isMultiAnswerMode, isPublicMode, containerId });
        await chatQuizService.playQuiz(currentClass.classCode, currentQuiz.id, isMultiAnswerMode, isPublicMode, containerId);
        setActiveQuizId(currentQuiz.id);
        setActiveQuizPlayingId(currentQuiz.id);
        setActiveQuizSetPlayingId(null);
        showToastMessage('퀴즈가 시작되었습니다.');
      } catch (error) {
        console.error('❌ 퀴즈 재생 실패:', error);
        showToastMessage('퀴즈 재생에 실패했습니다.');
      } finally {
        isPlayingQuizRef.current = false;
      }
    }
  };

  // 세트 정지
  const handleStopQuizSet = useCallback(async () => {
    if (!currentClass || !currentClass.classCode) return;

    try {
      await chatQuizService.stopQuiz(currentClass.classCode);
      // activeQuizSetId는 유지 (문제세트 선택 상태 유지)
      setActiveQuizSetPlayingId(null);
      setActiveQuizPlayingId(null);
      setActiveQuizId(null);
      setCurrentQuizIndex(-1);
      setDisplayedQuizIndex(0);
      // 세트 재생 중지 후에는 자동 업데이트하지 않음 (진행 새로고침 버튼으로만 갱신)
      showToastMessage('세트 재생이 정지되었습니다.');
    } catch (error) {
      console.error('❌ 세트 정지 실패:', error);
      showToastMessage('세트 정지에 실패했습니다.');
    }
  }, [currentClass, showToastMessage]);

  const handlePlayQuizSet = useCallback(async () => {
    if (!currentClass || !currentClass.classCode) return;
    
    if (!isChatOpen) {
      showToastMessage('클래스챗를 시작한 후 퀴즈를 재생할 수 있습니다.');
      return;
    }
    if (!isSetSelected || selectedSetIndex < 0 || selectedSetIndex >= quizSets.length) {
      showToastMessage('재생할 문제카드 세트를 선택하세요.');
      return;
    }
    const targetSet = quizSets[selectedSetIndex];
    try {
      // 다른 재생 모드 정지 (퀴즈/학생 문제 만들기)
      if (activeQuizId) {
        await handleStopQuiz();
      }
      if (isStudentCreateMode) {
        await chatQuizService.setStudentCreateMode(currentClass.classCode, false, null);
        setIsStudentCreateMode(false);
      }

      await chatQuizService.playQuizSet(
        currentClass.classCode,
        targetSet.id,
        isMultiAnswerMode,
        isPublicMode
      );
      setActiveQuizSetId(targetSet.id);
      setActiveQuizSetPlayingId(targetSet.id);
      setActiveQuizPlayingId(null);
      // 세트의 첫 문항을 로드 (순차 또는 랜덤)
      let quizzes = mapSetToQuizzes(targetSet);
      if (setPlayOrder === 'random') {
        const shuffled = [...quizzes];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        quizzes = shuffled;
      }
      setSavedQuizzes(quizzes);
      if (quizzes.length > 0) {
        setDisplayedQuizIndex(0);
        setCurrentQuizIndex(0);
        loadQuizToEditor(quizzes[0], false, targetSet.id);
      }
      showToastMessage('세트 재생을 시작했습니다.');
    } catch (error) {
      console.error('❌ 세트 재생 실패:', error);
      showToastMessage('세트를 재생하지 못했습니다.');
    }
  }, [
    currentClass,
    isChatOpen,
    isSetSelected,
    selectedSetIndex,
    quizSets,
    isMultiAnswerMode,
    isPublicMode,
    setPlayOrder,
    mapSetToQuizzes,
    loadQuizToEditor,
    isStudentCreateMode,
    activeQuizId,
    handleStopQuiz,
    showToastMessage,
  ]);

  const handleStartStudentCreateMode = useCallback(async () => {
    if (!currentClass?.classCode) return;
    const targetSet = quizSets[selectedSetIndex];
    if (!targetSet) {
      showToastMessage('학생 문제를 추가할 세트를 선택하세요.');
      return;
    }
    try {
      // 다른 재생 모드 정지 (퀴즈/세트)
      if (activeQuizId) {
        await handleStopQuiz();
      }
      if (activeQuizSetId) {
        await handleStopQuizSet();
      }

      await chatQuizService.setStudentCreateMode(currentClass.classCode, true, targetSet.id);
      setIsStudentCreateMode(true);
      showToastMessage('학생 문제 만들기 모드를 시작했습니다.');
    } catch (error) {
      console.error('❌ 학생 문제 만들기 모드 시작 실패:', error);
      showToastMessage('학생 문제 만들기 모드를 시작하지 못했습니다.');
    }
  }, [
    currentClass?.classCode,
    quizSets,
    selectedSetIndex,
    showToastMessage,
    activeQuizId,
    activeQuizSetId,
    handleStopQuiz,
    handleStopQuizSet,
  ]);


  const openQuizModal = useCallback(
    (quizId: string | null, options?: { initialIndex?: number }) => {
      if (quizId) {
        const index =
          options?.initialIndex ?? savedQuizzes.findIndex((quiz) => quiz.id === quizId);
        if (index === -1) {
          return;
        }
        setEditingQuizId(quizId);
        setCurrentQuizIndex(index);
        setDisplayedQuizIndex(index);
        const targetQuiz = savedQuizzes[index];
        if (targetQuiz) {
          loadQuizToEditor(targetQuiz, true, activeQuizSetId ?? null);
        }
        setIsQuizModalOpen(true);
        return;
      }

      previousDisplayedIndexRef.current = displayedQuizIndex;
      initializeNewQuizForm();
      setDisplayedQuizIndex(savedQuizzes.length);
      setEditingQuizId(null);
      setIsQuizModalOpen(true);
    },
    [savedQuizzes, activeQuizSetId, loadQuizToEditor, initializeNewQuizForm, displayedQuizIndex],
  );

  // 문제카드 변경사항 감지
  const hasQuizChanges = useCallback(() => {
    if (!initialQuizValues) return false;
    
    // 기본 필드 비교
    if (questionType !== initialQuizValues.questionType) return true;
    if (messageText !== initialQuizValues.messageText) return true;
    if (questionImageUrl !== initialQuizValues.questionImageUrl) return true;
    if (editorMultiAnswerSetting !== initialQuizValues.editorMultiAnswerSetting) return true;
    if (editorPublicModeSetting !== initialQuizValues.editorPublicModeSetting) return true;
    
    // 타입별 필드 비교
    if (questionType === 'choice') {
      if (choiceOptions.length !== initialQuizValues.choiceOptions.length) return true;
      for (let i = 0; i < choiceOptions.length; i++) {
        if (choiceOptions[i].id !== initialQuizValues.choiceOptions[i]?.id ||
            choiceOptions[i].text !== initialQuizValues.choiceOptions[i]?.text) {
          return true;
        }
      }
      if (JSON.stringify(correctAnswers.sort()) !== JSON.stringify(initialQuizValues.correctAnswers.sort())) {
        return true;
      }
    } else if (questionType === 'short') {
      if (JSON.stringify(answerBlocks) !== JSON.stringify(initialQuizValues.answerBlocks)) {
        return true;
      }
    } else if (questionType === 'essay') {
      if (guideMessage !== initialQuizValues.guideMessage) return true;
    }
    
    return false;
  }, [questionType, messageText, questionImageUrl, choiceOptions, correctAnswers, answerBlocks, guideMessage, editorMultiAnswerSetting, editorPublicModeSetting, initialQuizValues]);

  const handleCancelQuizModal = useCallback(() => {
    if (hasQuizChanges()) {
      setIsCloseQuizModalConfirmOpen(true);
      return;
    }
    
    if (!editingQuizId) {
      const previousIndex = previousDisplayedIndexRef.current;
      if (previousIndex !== undefined && previousIndex !== null) {
        setDisplayedQuizIndex(previousIndex);
        if (previousIndex >= 0 && previousIndex < savedQuizzes.length) {
          const previousQuiz = savedQuizzes[previousIndex];
          if (previousQuiz) {
            setCurrentQuizIndex(previousIndex);
            loadQuizToEditor(previousQuiz, false, activeQuizSetId ?? null);
          }
        } else {
          setCurrentQuizIndex(-1);
        }
      }
    } else {
      const index = savedQuizzes.findIndex((quiz) => quiz.id === editingQuizId);
      if (index !== -1) {
        setDisplayedQuizIndex(index);
        setCurrentQuizIndex(index);
        const originalQuiz = savedQuizzes[index];
        if (originalQuiz) {
          loadQuizToEditor(originalQuiz, false, activeQuizSetId ?? null);
        }
      }
    }

    setIsQuizModalOpen(false);
    setEditingQuizId(null);
    setIsQuestionImageMode(false);
    setQuestionImageUrl('');
    setIsImageSourceMenuOpen(false);
    setInitialQuizValues(null);
  }, [editingQuizId, savedQuizzes, activeQuizSetId, loadQuizToEditor, hasQuizChanges]);
  
  // 확인 모달에서 닫기 확인
  const handleConfirmCloseQuizModal = useCallback(() => {
    setIsCloseQuizModalConfirmOpen(false);
    
    if (!editingQuizId) {
      const previousIndex = previousDisplayedIndexRef.current;
      if (previousIndex !== undefined && previousIndex !== null) {
        setDisplayedQuizIndex(previousIndex);
        if (previousIndex >= 0 && previousIndex < savedQuizzes.length) {
          const previousQuiz = savedQuizzes[previousIndex];
          if (previousQuiz) {
            setCurrentQuizIndex(previousIndex);
            loadQuizToEditor(previousQuiz, false, activeQuizSetId ?? null);
          }
        } else {
          setCurrentQuizIndex(-1);
        }
      }
    } else {
      const index = savedQuizzes.findIndex((quiz) => quiz.id === editingQuizId);
      if (index !== -1) {
        setDisplayedQuizIndex(index);
        setCurrentQuizIndex(index);
        const originalQuiz = savedQuizzes[index];
        if (originalQuiz) {
          loadQuizToEditor(originalQuiz, false, activeQuizSetId ?? null);
        }
      }
    }

    setIsQuizModalOpen(false);
    setEditingQuizId(null);
    setIsQuestionImageMode(false);
    setQuestionImageUrl('');
    setIsImageSourceMenuOpen(false);
    setInitialQuizValues(null);
  }, [editingQuizId, savedQuizzes, activeQuizSetId, loadQuizToEditor]);
  
  // 확인 모달에서 취소
  const handleCancelCloseQuizModal = useCallback(() => {
    setIsCloseQuizModalConfirmOpen(false);
  }, []);

  const handleSelectQuizIndex = async (
    targetIndex: number,
    options: { rememberPrevious?: boolean; allowNewCard?: boolean } = {}
  ) => {
    if (isSetMode) {
      return;
    }
    if (isSwitchingQuiz) {
      return;
    }

    const totalQuizzes = savedQuizzes.length;
    const allowNewCard = options.allowNewCard ?? false;
    const maxIndex = allowNewCard ? totalQuizzes : Math.max(totalQuizzes - 1, 0);
    const normalizedIndex = Math.min(Math.max(targetIndex, 0), maxIndex);

    if (options.rememberPrevious) {
      previousDisplayedIndexRef.current = displayedQuizIndex;
    }

    setDisplayedQuizIndex(normalizedIndex);

    // 문제 카드 전환 시 멤버 응답 인덱스 초기화 (첫 번째 응답부터 표시)
    setStudentAnswerIndices({});

    if (allowNewCard && normalizedIndex === totalQuizzes) {
      if (options.rememberPrevious) {
        previousDisplayedIndexRef.current = displayedQuizIndex;
      }
      initializeNewQuizForm();
      openQuizModal(null);
      return;
    }

    const targetQuiz = savedQuizzes[normalizedIndex];
    if (!targetQuiz) {
      return;
    }

    setCurrentQuizIndex(normalizedIndex);
    loadQuizToEditor(targetQuiz, false, activeQuizContainerId ?? activeQuizSetId ?? null);

    if (!currentClass?.classCode) {
      return;
    }

    // 퀴즈 카드 이동으로는 "세트 재생"을 시작하지 않는다.
    const currentSetId = activeQuizContainerId ?? undefined;

    // 세트 재생 중일 때는 개별 퀴즈를 재생하지 않고 탐색만 함
    if (activeQuizId && activeQuizId !== targetQuiz.id && !activeQuizSetPlayingId) {
      setIsSwitchingQuiz(true);
      try {
        await chatQuizService.playQuiz(
          currentClass.classCode,
          targetQuiz.id,
          isMultiAnswerMode,
          isPublicMode,
          currentSetId
        );
        // 재생 상태는 리스너(서버 메타)에서 동기화되지만, 전환 직후 UX를 위해 최소한의 로컬 동기화만 해준다.
        if (!currentSetId) {
          setActiveQuizId(targetQuiz.id);
        }
      } catch (error) {
        console.error('❌ 퀴즈 전환 실패:', error);
        alert('퀴즈 전환에 실패했습니다. 다시 시도해주세요.');
      } finally {
        setIsSwitchingQuiz(false);
      }
    }
  };

  // 선택형 번호 버튼 클릭 핸들러 (정답 표시/해제)
  const handleChoiceNumberClick = (index: number) => {
    if (isEditorLocked) {
      return;
    }
    setCorrectAnswers((prev) => {
      if (prev.includes(index)) {
        // 이미 정답으로 표시된 경우 제거
        return prev.filter((i) => i !== index);
      } else {
        // 정답으로 추가
        return [...prev, index];
      }
    });
  };
  // 선택형 옵션 텍스트 변경 핸들러
  const handleChoiceOptionChange = (index: number, value: string) => {
    if (isEditorLocked) {
      return;
    }
    setChoiceOptions((prev) => {
      const newOptions = [...prev];
      newOptions[index] = { ...newOptions[index], text: value };
      return newOptions;
    });
  };

  // [+] 버튼 클릭 핸들러 (새 번호 추가, 최대 6개)
  const handleAddChoiceOption = () => {
    if (isEditorLocked) {
      return;
    }
    if (choiceOptions.length >= 6) {
      alert('선택지는 최대 6개까지 생성할 수 있습니다.');
      return;
    }
    setChoiceOptions((prev) => [
      ...prev,
      { id: `choice-${Date.now()}`, text: '' },
    ]);
  };

  // 새로고침 버튼 클릭 핸들러 (필드 내용 초기화)
  const handleRefreshChoiceOption = (index: number) => {
    if (isEditorLocked) {
      return;
    }
    setChoiceOptions((prev) => {
      const newOptions = [...prev];
      newOptions[index] = { ...newOptions[index], text: '' };
      return newOptions;
    });
  };

  // 휴지통 버튼 클릭 핸들러 (번호 버튼 제거)
  const handleDeleteChoiceOption = (index: number) => {
    if (isEditorLocked) {
      return;
    }
    // 최소 2개는 유지
    if (choiceOptions.length <= 2) {
      return;
    }
    setChoiceOptions((prev) => prev.filter((_, i) => i !== index));
    // 정답 표시도 제거
    setCorrectAnswers((prev) => prev.filter((i) => i !== index).map((i) => i > index ? i - 1 : i));
  };

  const handleResetQuizResponses = useCallback(async (quizId: string) => {
    if (!currentClass?.classCode) {
      return;
    }

    if (!window.confirm('이 문제의 모든 응답을 초기화하시겠습니까?')) {
      return;
    }

    try {
      await chatQuizService.resetQuizResponses(currentClass.classCode, quizId);
      responsesQuizIdRef.current = null;
      applyResponses(quizId, {}, []);
    } catch (error) {
      console.error('❌ 퀴즈 응답 초기화 실패:', error);
      alert('응답 초기화에 실패했습니다.');
    }
  }, [currentClass, applyResponses]);

  const selectedQuizForReset = useMemo(() => {
    if (currentQuizIndex >= 0 && currentQuizIndex < savedQuizzes.length) {
      return savedQuizzes[currentQuizIndex];
    }
    return null;
  }, [currentQuizIndex, savedQuizzes]);

  const canResetResponses = useMemo(() => {
    return !!selectedQuizForReset && !!currentClass?.classCode;
  }, [selectedQuizForReset, currentClass?.classCode]);

  const canStartPresentation = useMemo(() => {
    if (!currentClass?.classCode) return false;
    if (isSetMode) {
      return selectedSetIndex >= 0 && selectedSetIndex < quizSets.length;
    }
    return !!selectedQuizForReset || !!activeQuizId;
  }, [currentClass?.classCode, selectedQuizForReset, activeQuizId, isSetMode, selectedSetIndex, quizSets.length]);


  // 카메라 모달 오픈 시 스트림 연결/해제
  useEffect(() => {
    if (!isCameraModalOpen) {
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((t) => t.stop());
        cameraStreamRef.current = null;
      }
      return;
    }

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        cameraStreamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
      } catch (e) {
        console.error('❌ 카메라 접근 실패:', e);
        setIsCameraModalOpen(false);
        showToastMessage('카메라를 사용할 수 없습니다.');
      }
    })();

    return () => {
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((t) => t.stop());
        cameraStreamRef.current = null;
      }
    };
  }, [isCameraModalOpen, showToastMessage]);

  const openImageSourceMenu = useCallback(() => {
    if (isEditorLocked) return;
    setIsQuestionImageMode(true);
    setIsImageSourceMenuOpen(true);
  }, [isEditorLocked]);

  const handleSelectImageFile = useCallback(async (file: File) => {
    try {
      const src = await readFileAsDataURL(file);
      setRawImageSrc(src);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      cropPixelsRef.current = null;
      setIsCropModalOpen(true);
      setIsImageSourceMenuOpen(false);
      setIsQuestionImageMode(true);
      setEditingSectionId(null); // 단일 퀴즈 모드
    } catch (e) {
      console.error('❌ 파일 읽기 실패:', e);
      showToastMessage('이미지 파일을 읽지 못했습니다.');
    }
  }, [showToastMessage]);

  const handleStartFileUpload = useCallback(() => {
    if (isEditorLocked) return;
    setIsQuestionImageMode(true);
    setIsImageSourceMenuOpen(false);
    fileInputRef.current?.click();
  }, [isEditorLocked]);

  const handleStartCamera = useCallback(() => {
    if (isEditorLocked) return;
    setIsQuestionImageMode(true);
    setIsImageSourceMenuOpen(false);
    setIsCameraModalOpen(true);
  }, [isEditorLocked]);

  const handleCaptureFromCamera = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const w = video.videoWidth || 1280;
    const h = video.videoHeight || 720;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, w, h);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    setIsCameraModalOpen(false);
    setRawImageSrc(dataUrl);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    cropPixelsRef.current = null;
    setIsCropModalOpen(true);
    // editingSectionId가 있으면 섹션 모드, 없으면 단일 퀴즈 모드
  }, []);

  // 세트 모달 섹션별 이미지 업로드 핸들러
  const handleUploadSectionImage = useCallback(async () => {
    if (!rawImageSrc || !editingSectionId) return;
    const cropPixels = cropPixelsRef.current;
    if (!cropPixels) {
      showToastMessage('크롭 영역을 결정해 주세요.');
      return;
    }
    if (!user?.id || !currentClass?.id) {
      showToastMessage('사용자 정보 또는 클래스 정보를 찾을 수 없습니다.');
      return;
    }
    setIsUploadingQuestionImage(true);
    try {
      let blob = await cropToWebpBlob(rawImageSrc, cropPixels, 1280, 0.8);
      if (blob.size > 400_000) {
        blob = await cropToWebpBlob(rawImageSrc, cropPixels, 960, 0.75);
      }

      // 폴더 결정: 문제 세트 모드이고 선택된 세트가 있으면 세트 ID 사용
      let quizFolder: string;
      if (isSetMode && selectedSetIndex >= 0 && selectedSetIndex < quizSets.length) {
        quizFolder = quizSets[selectedSetIndex].id;
      } else if (editingSetId) {
        quizFolder = editingSetId;
      } else {
        quizFolder = `draft_${Date.now()}`;
      }

      const fileName = `question_${Date.now()}.${IMAGE_WEBP_EXT}`;
      const path = buildQuizQuestionImagePath(user.id, currentClass.id, quizFolder, fileName);
      await userStorageQuotaService.assertCanUpload(user.id, blob.size);
      const fileRef = storageRef(storage, path);
      await uploadBytes(fileRef, blob, { contentType: IMAGE_WEBP_MIME });
      userStorageQuotaService.invalidateUsageCache();
      const url = await getDownloadURL(fileRef);
      console.log('✅ 섹션 이미지 업로드 완료, URL:', url, '섹션:', editingSectionId);
      
      // 해당 섹션의 questionImageUrl 업데이트
      handleSectionChange(editingSectionId, { questionImageUrl: url });
      setIsCropModalOpen(false);
      setEditingSectionId(null);
      showToastMessage('이미지가 업로드되었습니다.');
    } catch (e) {
      console.error('❌ 섹션 이미지 업로드 실패:', e);
      showToastMessage(
        e instanceof StorageQuotaExceededError ? e.message : '이미지 업로드에 실패했습니다.'
      );
    } finally {
      setIsUploadingQuestionImage(false);
    }
  }, [rawImageSrc, editingSectionId, user?.id, currentClass?.id, isSetMode, selectedSetIndex, quizSets, editingSetId, handleSectionChange, showToastMessage]);

  const handleUploadCroppedImage = useCallback(async () => {
    if (!rawImageSrc) return;
    const cropPixels = cropPixelsRef.current;
    if (!cropPixels) {
      showToastMessage('크롭 영역을 결정해 주세요.');
      return;
    }
    if (!user?.id || !currentClass?.id) {
      showToastMessage('사용자 정보 또는 클래스 정보를 찾을 수 없습니다.');
      return;
    }
    
    // 섹션 모드인지 확인
    if (editingSectionId) {
      await handleUploadSectionImage();
      return;
    }
    
    setIsUploadingQuestionImage(true);
    try {
      let blob = await cropToWebpBlob(rawImageSrc, cropPixels, 1280, 0.8);
      if (blob.size > 400_000) {
        blob = await cropToWebpBlob(rawImageSrc, cropPixels, 960, 0.75);
      }

      // 폴더 결정: 문제 세트 모드이고 선택된 세트가 있으면 세트 ID 사용, 아니면 기존 로직
      let quizFolder: string;
      if (isSetMode && selectedSetIndex >= 0 && selectedSetIndex < quizSets.length) {
        // 문제 세트 모드: 세트 ID를 폴더로 사용
        quizFolder = quizSets[selectedSetIndex].id;
      } else if (editingQuizId) {
        // 단일 퀴즈 수정 모드: 해당 퀴즈가 속한 세트를 찾아서 세트 ID 사용
        let foundSetId: string | null = null;
        for (const set of quizSets) {
          if (set.quizzes && Object.keys(set.quizzes).includes(editingQuizId)) {
            foundSetId = set.id;
            break;
          }
        }
        if (foundSetId) {
          quizFolder = foundSetId;
        } else {
          // 세트에 속하지 않은 경우 퀴즈 ID 사용
          quizFolder = editingQuizId;
        }
      } else {
        // 새 퀴즈 생성 모드: 현재 활성화된 세트가 있으면 세트 ID 사용, 없으면 draft 타임스탬프 사용
        // 단, 이미지 업로드 시점에는 퀴즈가 아직 저장되지 않았으므로 activeQuizSetId를 확인
        if (activeQuizSetId) {
          quizFolder = activeQuizSetId;
        } else {
          // 활성화된 세트가 없으면 draft 타임스탬프 사용 (나중에 퀴즈 저장 시 세트에 속하면 해당 세트 폴더로 이동 필요)
          quizFolder = `draft_${Date.now()}`;
        }
      }
      
      const fileName = `question_${Date.now()}.${IMAGE_WEBP_EXT}`;
      const path = buildQuizQuestionImagePath(user.id, currentClass.id, quizFolder, fileName);
      await userStorageQuotaService.assertCanUpload(user.id, blob.size);
      const fileRef = storageRef(storage, path);
      await uploadBytes(fileRef, blob, { contentType: IMAGE_WEBP_MIME });
      userStorageQuotaService.invalidateUsageCache();
      const url = await getDownloadURL(fileRef);
      console.log('✅ 이미지 업로드 완료, URL:', url, '폴더:', quizFolder);
      setQuestionImageUrl(url);
      setIsQuestionImageMode(true);
      setIsCropModalOpen(false);
      showToastMessage('이미지가 업로드되었습니다.');
    } catch (e) {
      console.error('❌ 이미지 업로드 실패:', e);
      showToastMessage(
        e instanceof StorageQuotaExceededError ? e.message : '이미지 업로드에 실패했습니다.'
      );
    } finally {
      setIsUploadingQuestionImage(false);
    }
  }, [rawImageSrc, editingQuizId, editingSectionId, isSetMode, selectedSetIndex, quizSets, activeQuizSetId, showToastMessage, user?.id, currentClass?.id, handleUploadSectionImage]);

  // Firebase Storage URL에서 경로 추출
  const extractStoragePathFromUrl = useCallback((url: string): string | null => {
    try {
      // Firebase Storage URL 형식: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{encodedPath}?alt=media&token={token}
      const match = url.match(/\/o\/(.+?)\?/);
      if (match && match[1]) {
        // URL 디코딩
        return decodeURIComponent(match[1]);
      }
      return null;
    } catch (e) {
      console.error('❌ URL 파싱 실패:', e);
      return null;
    }
  }, []);

  // 이미지 삭제 핸들러
  const handleDeleteQuestionImage = useCallback(async () => {
    if (!questionImageUrl) return;
    if (isEditorLocked) return;

    if (!window.confirm('이미지를 삭제하시겠습니까?')) {
      return;
    }

    try {
      // Firebase Storage에서 파일 삭제
      const path = extractStoragePathFromUrl(questionImageUrl);
      if (path) {
        const fileRef = storageRef(storage, path);
        await deleteObject(fileRef);
        userStorageQuotaService.invalidateUsageCache();
      }

      // 상태 초기화
      setQuestionImageUrl('');
      setIsQuestionImageMode(false);
      showToastMessage('이미지가 삭제되었습니다.');
    } catch (e) {
      console.error('❌ 이미지 삭제 실패:', e);
      // 삭제 실패해도 로컬 상태는 초기화
      setQuestionImageUrl('');
      setIsQuestionImageMode(false);
      showToastMessage('이미지 삭제에 실패했습니다.');
    }
  }, [questionImageUrl, isEditorLocked, extractStoragePathFromUrl, showToastMessage]);

  // 세트 모달 섹션별 이미지 관리
  const openSectionImageSourceMenu = useCallback((sectionId: string) => {
    setEditingSectionId(sectionId);
    setIsImageSourceMenuOpen(true);
  }, []);

  const handleSelectSectionImageFile = useCallback(async (file: File, sectionId: string) => {
    try {
      const src = await readFileAsDataURL(file);
      setRawImageSrc(src);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      cropPixelsRef.current = null;
      setEditingSectionId(sectionId);
      setIsCropModalOpen(true);
      setIsImageSourceMenuOpen(false);
    } catch (e) {
      console.error('❌ 파일 읽기 실패:', e);
      showToastMessage('이미지 파일을 읽지 못했습니다.');
    }
  }, [showToastMessage]);

  const handleStartSectionFileUpload = useCallback((sectionId: string) => {
    setEditingSectionId(sectionId);
    setIsImageSourceMenuOpen(false);
    setSectionFileInputRef.current?.click();
  }, []);

  const handleStartSectionCamera = useCallback((sectionId: string) => {
    setEditingSectionId(sectionId);
    setIsImageSourceMenuOpen(false);
    setIsCameraModalOpen(true);
  }, []);

  const handleDeleteSectionImage = useCallback(async (sectionId: string, imageUrl: string) => {
    if (!imageUrl) return;

    if (!window.confirm('이미지를 삭제하시겠습니까?')) {
      return;
    }

    try {
      // Firebase Storage에서 파일 삭제
      const path = extractStoragePathFromUrl(imageUrl);
      if (path) {
        const fileRef = storageRef(storage, path);
        await deleteObject(fileRef);
        userStorageQuotaService.invalidateUsageCache();
      }

      // 해당 섹션의 questionImageUrl 초기화
      handleSectionChange(sectionId, { questionImageUrl: '' });
      showToastMessage('이미지가 삭제되었습니다.');
    } catch (e) {
      console.error('❌ 섹션 이미지 삭제 실패:', e);
      // 삭제 실패해도 로컬 상태는 초기화
      handleSectionChange(sectionId, { questionImageUrl: '' });
      showToastMessage('이미지 삭제에 실패했습니다.');
    }
  }, [extractStoragePathFromUrl, handleSectionChange, showToastMessage]);

  const handleOpenPresentationModal = useCallback(() => {
    if (!currentClass?.classCode) {
      alert('클래스 정보를 찾을 수 없습니다.');
      return;
    }

    if (isSetMode) {
      if (selectedSetIndex < 0 || selectedSetIndex >= quizSets.length) {
        alert('프레젠테이션할 문제 세트를 선택해 주세요.');
        return;
      }
      setPresentationOptions((prev) => ({
        ...prev,
        isResponsePublic: false,
        isNameVisible: false,
        isAnswerVisible: false,
        showCorrectRate: false,
        showScore: false,
      }));
      setIsPresentationModalOpen(true);
      return;
    }

    if (!selectedQuizForReset && !activeQuizId) {
      alert('프레젠테이션할 문제를 선택해 주세요.');
      return;
    }

    setPresentationOptions((prev) => ({
      ...prev,
      isResponsePublic: false,
      isNameVisible: false,
      isAnswerVisible: false,
    }));
    setIsPresentationModalOpen(true);
  }, [currentClass?.classCode, selectedQuizForReset, activeQuizId, isSetMode, quizSets.length, selectedSetIndex]);

  const handleClosePresentationModal = useCallback(() => {
    setIsPresentationModalOpen(false);
  }, []);

  // URL 생성 함수
  const getPresentationUrl = useCallback((): string | null => {
    if (!currentClass?.classCode) {
      alert('클래스 정보를 찾을 수 없습니다.');
      return null;
    }

    if (isSetMode) {
      const targetSet = selectedSetIndex >= 0 && selectedSetIndex < quizSets.length ? quizSets[selectedSetIndex] : null;
      if (!targetSet) {
        alert('프레젠테이션할 문제 세트를 선택해 주세요.');
        return null;
      }
      const params = new URLSearchParams({
        classCode: currentClass.classCode,
        setId: targetSet.id,
        mode: 'set',
        names: presentationOptions.isNameVisible ? 'public' : 'private',
        correctRate: presentationOptions.showCorrectRate ? 'public' : 'private',
        ...(presentationOptions.showScore ? { score: 'public' } : {}),
        ...(currentMemberGroup ? { memberGroup: currentMemberGroup } : {}),
      });
      return `${window.location.origin}/presentation?${params.toString()}`;
    } else {
      const targetQuizId = selectedQuizForReset?.id || activeQuizId;
      if (!targetQuizId) {
        alert('프레젠테이션할 문제를 선택해 주세요.');
        return null;
      }

      const params = new URLSearchParams({
        classCode: currentClass.classCode,
        quizId: targetQuizId,
        responses: presentationOptions.isResponsePublic ? 'public' : 'private',
        names: presentationOptions.isNameVisible ? 'public' : 'private',
        answers: presentationOptions.isAnswerVisible ? 'public' : 'private',
        ...(currentMemberGroup ? { memberGroup: currentMemberGroup } : {}),
      });
      return `${window.location.origin}/presentation?${params.toString()}`;
    }
  }, [currentClass?.classCode, selectedQuizForReset, activeQuizId, presentationOptions, isSetMode, selectedSetIndex, quizSets, currentMemberGroup]);

  const handleStartPresentation = useCallback((e: React.MouseEvent) => {
    // URL 생성
    const url = getPresentationUrl();
    if (!url) {
      return;
    }

    // 전역 변수로 창 참조 확인 (컴포넌트 리렌더링과 무관하게 유지)
    let existingWindow = (window as any).__presentationWindow;
    
    console.log('🔍 프레젠테이션 시작 - 기존 창 확인:', { 
      hasExistingWindow: !!existingWindow,
      existingWindowClosed: existingWindow ? existingWindow.closed : 'N/A'
    });
    
    // 기존 프레젠테이션 창이 열려있고 닫히지 않았는지 확인
    if (existingWindow) {
      try {
        // closed 속성 확인 (접근 가능한 경우에만)
        if (!existingWindow.closed) {
          console.log('✅ 기존 창이 열려있음. URL 업데이트:', url);
          // 기존 창의 URL 업데이트 및 포커스
          // window.open을 호출하지 않고 URL만 변경 (새 탭이 열리지 않음)
          existingWindow.location.href = url;
          existingWindow.focus();
          setIsPresentationModalOpen(false);
          return; // 기존 창이 있으면 여기서 종료 (window.open 호출 안 함)
        } else {
          console.log('⚠️ 기존 창이 닫혀있음. 참조 제거');
          // 창이 닫혀있으면 참조 제거
          (window as any).__presentationWindow = null;
        }
      } catch (error) {
        // cross-origin 오류 등으로 접근 불가능한 경우
        // 창이 닫혔거나 다른 origin일 수 있음
        console.warn('❌ 기존 창에 접근할 수 없습니다. 새 창을 엽니다.', error);
        (window as any).__presentationWindow = null;
      }
    } else {
      console.log('ℹ️ 기존 창이 없음. 새 창 열기');
    }

    // 기존 창이 없거나 닫혀있을 때만 새 탭 열기
    // 같은 이름으로 열면 브라우저가 기존 탭을 재사용하려고 시도
    const PRESENTATION_WINDOW_NAME = 'presentation-window';
    let openedWindow: Window | null = null;
    
    console.log('🚀 새 탭 열기 시도 (클릭 핸들러에서 직접 호출):', { url, windowName: PRESENTATION_WINDOW_NAME });
    
    try {
      // 클릭 이벤트 핸들러에서 직접 호출하여 브라우저가 사용자 상호작용으로 인식
      // 같은 이름으로 열면 브라우저가 기존 탭을 재사용하거나 새 탭을 엽니다
      openedWindow = window.open(url, PRESENTATION_WINDOW_NAME);
      console.log('📝 window.open 결과:', { openedWindow: !!openedWindow, isNull: openedWindow === null });
      
      // window.open이 null을 반환해도 브라우저가 탭을 열었을 수 있습니다
      // (특히 새 탭으로 열 때)
      // 같은 이름으로 열었으므로 브라우저가 기존 탭을 재사용했을 가능성이 있습니다
      
      // openedWindow가 null이어도 전역 변수에 저장된 창이 있을 수 있으므로
      // 전역 변수를 확인해봅니다
      if (!openedWindow && (window as any).__presentationWindow) {
        const savedWindow = (window as any).__presentationWindow;
        try {
          if (!savedWindow.closed) {
            // 저장된 창이 열려있으면 그것을 사용
            openedWindow = savedWindow;
            if (openedWindow) {
              openedWindow.location.href = url;
              openedWindow.focus();
              console.log('✅ 저장된 창을 재사용하여 URL 업데이트');
            }
          }
        } catch (error) {
          console.warn('저장된 창에 접근할 수 없습니다:', error);
        }
      }
    } catch (error) {
      console.error('❌ 프레젠테이션 창 열기 실패:', error);
    }
    
    if (openedWindow) {
      console.log('✅ 새 탭 열기 성공. 전역 변수에 저장');
      // 전역 변수에 저장 (컴포넌트 리렌더링과 무관하게 유지)
      (window as any).__presentationWindow = openedWindow;
      openedWindow.focus();
    } else {
      // window.open이 null을 반환했지만, 브라우저가 같은 이름의 탭을 재사용했을 수 있습니다
      // 다음에 호출될 때를 위해 전역 변수는 유지하지 않습니다
      console.warn('⚠️ window.open이 null을 반환했습니다. 브라우저가 탭을 열었을 수도 있습니다.');
    }
    
    setIsPresentationModalOpen(false);
  }, [getPresentationUrl]);

  const handleResetStudentResponse = useCallback(async (studentId: string, responseIndex?: number) => {
    if (!currentClass?.classCode) {
      return;
    }

    let targetQuizId: string | null = null;

    if (displayedQuizIndex >= 0 && displayedQuizIndex < savedQuizzes.length) {
      targetQuizId = savedQuizzes[displayedQuizIndex]?.id || null;
    }

    if (!targetQuizId && activeQuizId) {
      targetQuizId = activeQuizId;
    }

    if (!targetQuizId && responsesQuizIdRef.current) {
      targetQuizId = responsesQuizIdRef.current;
    }

    if (!targetQuizId) {
      return;
    }

    const confirmMessage = responseIndex !== undefined
      ? '이 응답을 삭제하시겠습니까?'
      : '이 멤버의 모든 응답을 초기화하시겠습니까?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await chatQuizService.resetStudentResponse(currentClass.classCode, targetQuizId, studentId, responseIndex);
      pendingRemovalRef.current = { studentId, responseIndex };

      const isActiveTarget = activeQuizId && activeQuizId === targetQuizId;
      if (!isActiveTarget) {
        const { responses, answeredStudents: answered } = await chatQuizService.getQuizResponses(currentClass.classCode, targetQuizId);
        applyResponses(targetQuizId, responses || {}, answered || []);
      }
    } catch (error) {
      console.error('❌ 멤버 응답 초기화 실패:', error);
      alert('멤버 응답 초기화에 실패했습니다.');
    }
  }, [currentClass?.classCode, displayedQuizIndex, savedQuizzes, activeQuizId, applyResponses]);

  const isStudentActive = useCallback(
    (studentId: string) => {
      if (!isChatOpen) {
        return true;
      }
      if (!connectedStudentsMap) {
        return false;
      }
      return Object.prototype.hasOwnProperty.call(connectedStudentsMap, studentId);
    },
    [connectedStudentsMap, isChatOpen]
  );

  const handleTogglePublicMode = useCallback(async () => {
    if (!isChatOpen) {
      alert('클래스챗를 시작한 후 옵션을 변경할 수 있습니다.');
      return;
    }
    const nextMode = !isPublicMode;
    setIsPublicMode(nextMode);
    // 사용자가 옵션을 변경했음을 표시

    // 재생 중일 때만 서버에 업데이트 (재생 전에는 로컬 상태만 변경)
    if (activeQuizId && currentClass?.classCode) {
    try {
      await chatQuizService.updateActiveQuizSettings(currentClass.classCode, {
        isPublicMode: nextMode,
      });
    } catch (error) {
      console.error('❌ 공개/비공개 전환 실패:', error);
      alert('응답 공개 설정 변경에 실패했습니다.');
    }
    }
  }, [isPublicMode, currentClass?.classCode, isChatOpen, activeQuizId]);

  const handleToggleMultiAnswerMode = useCallback(async () => {
    if (!isChatOpen) {
      alert('클래스챗를 시작한 후 옵션을 변경할 수 있습니다.');
      return;
    }
    const nextMode = !isMultiAnswerMode;
    setIsMultiAnswerMode(nextMode);
    // 사용자가 옵션을 변경했음을 표시
    console.log('🔄 멀티응답 모드 변경:', nextMode, '재생 중:', !!activeQuizId);

    // 재생 중일 때만 서버에 업데이트 (재생 전에는 로컬 상태만 변경)
    if (activeQuizId && currentClass?.classCode) {
    try {
      await chatQuizService.updateActiveQuizSettings(currentClass.classCode, {
        isMultiAnswerMode: nextMode,
      });
    } catch (error) {
      console.error('❌ 단일/멀티 전환 실패:', error);
      alert('응답 모드 변경에 실패했습니다.');
    }
    }
  }, [isMultiAnswerMode, currentClass?.classCode, isChatOpen, activeQuizId]);

  const displayedResponses = useMemo(() => {
    if (isViewingNewQuiz) {
      return {} as typeof quizResponses;
    }
    return quizResponses;
  }, [isViewingNewQuiz, quizResponses]);

  /** 재생 중 단일 퀴즈(멤버 블럭 … 대기 조건용) */
  const activeLiveQuizForCoordinatorUi = useMemo(() => {
    if (!activeQuizId || !savedQuizzes?.length) return null;
    return savedQuizzes.find((q) => q.id === activeQuizId) ?? null;
  }, [activeQuizId, savedQuizzes]);

  // 학생 선택 핸들러
  const handleStudentSelection = useCallback((studentId: string) => {
    setSelectedStudents(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(studentId)) {
        newSelected.delete(studentId);
      } else {
        newSelected.add(studentId);
      }
      // localStorage에 저장
      const storageKey = getSelectedStudentsStorageKey();
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, JSON.stringify(Array.from(newSelected)));
        } catch (error) {
          console.error('Failed to save selected students to localStorage:', error);
        }
      }
      return newSelected;
    });
  }, [getSelectedStudentsStorageKey]);

  // 전체 선택 핸들러
  const handleSelectAll = useCallback(() => {
    if (!students) return;
    const allStudentIds = new Set(students.map(student => student.id));
    setSelectedStudents(allStudentIds);
    // localStorage에 저장
    const storageKey = getSelectedStudentsStorageKey();
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(Array.from(allStudentIds)));
      } catch (error) {
        console.error('Failed to save selected students to localStorage:', error);
      }
    }
  }, [students, getSelectedStudentsStorageKey]);

  // 선택 취소 핸들러
  const handleDeselectAll = useCallback(() => {
    setSelectedStudents(new Set());
    // localStorage에 저장
    const storageKey = getSelectedStudentsStorageKey();
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, JSON.stringify([]));
      } catch (error) {
        console.error('Failed to save selected students to localStorage:', error);
      }
    }
  }, [getSelectedStudentsStorageKey]);

  // 정답자 선택 핸들러
  const handleSelectCorrectStudents = useCallback(() => {
    if (!students || !displayedResponses) return;
    const correctStudentIds = new Set<string>();
    
    students.forEach(student => {
      const response = displayedResponses[student.id];
      if (!response) return;
      
      const isArray = Array.isArray(response);
      if (isArray) {
        // 멀티 응답: 하나라도 정답이면 선택
        const hasCorrect = response.some((r: StudentQuizResponse) => r?.isCorrect === true);
        if (hasCorrect) {
          correctStudentIds.add(student.id);
        }
      } else {
        // 단일 응답
        if (response.isCorrect === true) {
          correctStudentIds.add(student.id);
        }
      }
    });
    
    setSelectedStudents(correctStudentIds);
    // localStorage에 저장
    const storageKey = getSelectedStudentsStorageKey();
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(Array.from(correctStudentIds)));
      } catch (error) {
        console.error('Failed to save selected students to localStorage:', error);
      }
    }
  }, [students, displayedResponses, getSelectedStudentsStorageKey]);

  // 문제 만들기 모드에서 제출 완료한 멤버 ID 집합 (현재 세트의 savedQuizzes 기준)
  const studentCreateSubmittedIds = useMemo(() => {
    return new Set(
      savedQuizzes
        .map((q) => q.createdByStudentId)
        .filter((id): id is string => !!id)
    );
  }, [savedQuizzes]);

  // 정답자가 있는지 확인
  const hasCorrectAnswers = useMemo(() => {
    if (!students || !displayedResponses) return false;
    return students.some(student => {
      const response = displayedResponses[student.id];
      if (!response) return false;
      const isArray = Array.isArray(response);
      if (isArray) {
        return response.some((r: StudentQuizResponse) => r?.isCorrect === true);
      } else {
        return response.isCorrect === true;
      }
    });
  }, [students, displayedResponses]);

  const guestSection = useMemo(() => {
    const studentIdSet = new Set(students.map((s) => s.id));
    const responseIds = Object.keys(displayedResponses || {});
    const connectedGuestIds = connectedStudentIds.filter((id) => !studentIdSet.has(id));
    const responseGuestIds = responseIds.filter(
      (id) => !studentIdSet.has(id) && !connectedGuestIds.includes(id)
    );
    const guestIds = [...connectedGuestIds, ...responseGuestIds];

    if (guestIds.length === 0) {
      return null;
    }

    return (
      <GuestSectionWrapper $fullWidth={true}>
        <GuestSectionTitle>게스트</GuestSectionTitle>
        <StudentsContainer>
          {guestIds.map((guestId) => {
            const response = displayedResponses[guestId];
            const isArray = Array.isArray(response);
            const currentIndex = studentAnswerIndices[guestId] ?? (Array.isArray(response) ? response.length - 1 : 0);
            const isActiveGuest = isStudentActive(guestId);

            let currentResponse: StudentQuizResponse | null = null;
            if (isArray && response.length > 0) {
              const validIndex = Math.max(0, Math.min(currentIndex, response.length - 1));
              currentResponse = response[validIndex] || null;
            } else if (!isArray && response) {
              currentResponse = response;
            }

            const isConnected = isChatOpen ? connectedStudentIds.includes(guestId) : true;
            const hasStoredResponse = isArray ? (Array.isArray(response) && response.length > 0) : !!response;
            const isWaitingForResponse = shouldShowMemberQuizTypingWait({
              isQuizPlaying,
              isConnected,
              isStudentCreateMode,
              isSetMode,
              isMultiAnswerMode,
              activeQuizType: activeLiveQuizForCoordinatorUi?.type,
              rawResponse: response,
            });
            const hasResponseData = hasStoredResponse || isWaitingForResponse;

            const responseLength = isArray && Array.isArray(response) ? response.length : 0;
            const removalIndex =
              isArray && responseLength > 0
                ? Math.max(0, Math.min(currentIndex, responseLength - 1))
                : undefined;

            const metaGuestInfo = connectedStudentsMap[guestId];
            const guestName =
              metaGuestInfo?.name ||
              (Array.isArray(response) ? currentResponse?.guestName : (currentResponse as any)?.guestName) ||
              '게스트';
            const guestNumberLabel = metaGuestInfo?.isGuest ? 'G' : metaGuestInfo?.number || 'G';

            return (
              <StudentBlock key={guestId} $isActive={isActiveGuest}>
                <StudentResetButton
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleResetStudentResponse(guestId, removalIndex);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  disabled={!hasResponseData}
                  title="게스트 응답 삭제"
                  aria-label={`${guestName} 응답 삭제`}
                >
                  <TrashIcon viewBox="0 0 24 24" strokeWidth="2">
                    <path d="M3 6h18" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M10 11v6" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M14 11v6" strokeLinecap="round" strokeLinejoin="round" />
                  </TrashIcon>
                </StudentResetButton>
                <MessageBubble>
                  {(() => {
                    if (!currentResponse) {
                      return null;
                    }

                    const hasSubmittedAt = !!currentResponse.submittedAt;
                    const hasAnswer =
                      currentResponse.answer !== undefined &&
                      currentResponse.answer !== null &&
                      (typeof currentResponse.answer === 'string'
                        ? currentResponse.answer.trim().length > 0
                        : Array.isArray(currentResponse.answer)
                        ? currentResponse.answer.length > 0
                        : true);

                    if (hasSubmittedAt || hasAnswer) {
                      if (typeof currentResponse.answer === 'string') {
                        const answerText = currentResponse.answer.trim();
                        if (answerText) {
                          return (
                            <MessageContent $isWaiting={isWaitingForResponse}>
                              <MessageText>{answerText}</MessageText>
                            </MessageContent>
                          );
                        }
                      } else if (Array.isArray(currentResponse.answer) && currentResponse.answer.length > 0) {
                        const sortedAnswers = [...currentResponse.answer].sort((a, b) => a - b);
                        return (
                          <MessageContent $isWaiting={isWaitingForResponse}>
                            <ChoiceBadgesContainer>
                              {sortedAnswers.map((index) => {
                                const levelIndex = index < 6 ? index + 1 : undefined;
                                return (
                                  <ChoiceBadge key={index} $levelIndex={levelIndex}>
                                    {index + 1}
                                  </ChoiceBadge>
                                );
                              })}
                            </ChoiceBadgesContainer>
                          </MessageContent>
                        );
                      }
                    }

                    return null;
                  })()}

                  {isWaitingForResponse && (
                    <TypingIndicator $isOverlay={true}>
                      <span />
                      <span />
                      <span />
                    </TypingIndicator>
                  )}

                  {isArray && responseLength > 1 && (
                    <AnswerNavigationContainer>
                      <AnswerNavigationButton
                        disabled={currentIndex === 0}
                        onClick={() => {
                          setStudentAnswerIndices((prev) => ({
                            ...prev,
                            [guestId]: Math.max(0, (prev[guestId] || 0) - 1),
                          }));
                        }}
                      >
                        &lt;
                      </AnswerNavigationButton>
                      <AnswerNavigationText>
                        {currentIndex + 1}/{responseLength}
                      </AnswerNavigationText>
                      <AnswerNavigationButton
                        disabled={currentIndex >= responseLength - 1}
                        onClick={() => {
                          setStudentAnswerIndices((prev) => ({
                            ...prev,
                            [guestId]: Math.min(responseLength - 1, (prev[guestId] || 0) + 1),
                          }));
                        }}
                      >
                        &gt;
                      </AnswerNavigationButton>
                    </AnswerNavigationContainer>
                  )}

                    {currentResponse?.submittedAt !== undefined && (() => {
                      const hasAnswer =
                        currentResponse.answer !== undefined &&
                        currentResponse.answer !== null &&
                        (typeof currentResponse.answer === 'string'
                          ? currentResponse.answer.trim().length > 0
                          : Array.isArray(currentResponse.answer)
                            ? currentResponse.answer.length > 0
                            : true);
                      const badgeStatus =
                        currentResponse.isCorrect === true
                          ? 'correct'
                          : currentResponse.isCorrect === false
                            ? 'wrong'
                            : hasAnswer
                              ? 'neutral'
                              : 'wrong'; // 서술형 무응답은 X

                      return (
                        <ResultBadge $status={badgeStatus}>
                          {badgeStatus === 'correct' ? (
                            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                              <circle cx="12" cy="12" r="9" stroke="currentColor" fill="none" />
                            </svg>
                          ) : badgeStatus === 'wrong' ? (
                            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                            </svg>
                          ) : (
                            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                              <circle cx="12" cy="12" r="9" stroke="currentColor" fill="none" />
                            </svg>
                          )}
                        </ResultBadge>
                      );
                    })()}
                </MessageBubble>

                <ProfileImageContainer>
                  <ProfileTextContainer>
                    <ProfileNumber>{guestNumberLabel}</ProfileNumber>
                    <ProfileName>{guestName}</ProfileName>
                  </ProfileTextContainer>
                  <ProfileImageWrapper>
                    <CachedProfileImage imageId="basic-0" alt={guestName} />
                  </ProfileImageWrapper>
                </ProfileImageContainer>
              </StudentBlock>
            );
          })}
        </StudentsContainer>
      </GuestSectionWrapper>
    );
  }, [
    students,
    displayedResponses,
    connectedStudentIds,
    studentAnswerIndices,
    isStudentActive,
    isChatOpen,
    isQuizPlaying,
    isStudentCreateMode,
    isSetMode,
    isMultiAnswerMode,
    activeLiveQuizForCoordinatorUi,
    connectedStudentsMap,
    handleResetStudentResponse,
  ]);

  return (
    <>
      <Header
        user={user}
        currentClass={currentClass}
        currentPage="chat"
        onNavigate={onNavigate}
        onLogout={onLogout}
        onUpdateUser={onUpdateUser}
        onProfileModalOpen={onProfileModalOpen}
        onClassManageModalOpen={onClassManageModalOpen}
        classes={classes}
        memberGroups={memberGroups}
        currentMemberGroup={currentMemberGroup}
        onMemberGroupChange={onMemberGroupChange}
        themeMode={themeMode}
        onToggleTheme={onToggleTheme}
        onSeatAssignmentClick={onSeatAssignmentClick}
        onLevelSettingsClick={onLevelSettingsClick}
        onPointManagementClick={onPointManagementClick}
        onItemCardSettingsClick={onItemCardSettingsClick}
        onAssignmentSettingsClick={onAssignmentSettingsClick}
      />

      <Container>
        {/* 퀴즈 재생/정지 버튼 */}
        <QuizControlSection $tightBottom={!isSetMode}>
          {!isChatOpen ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr auto',
                alignItems: 'center',
                width: '100%',
                gap: 12,
                marginTop: 20,
              }}
            >
                  <div style={{ width: 44, height: 44 }} />
              <div
                style={{
                  justifySelf: 'center',
                  alignSelf: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 12,
                  minWidth: 0,
                  width: '100%',
                }}
              >
                {!isSetMode ? (
                  <>
                  <ChatStartButton
                    type="button"
                    onClick={handleOpenChat}
                    disabled={!currentClass?.classCode}
                  >
                    클래스챗 시작
                  </ChatStartButton>
                    {showSetViewButton && (
                      <BackToSetExitButton
                        type="button"
                        disabled={isQuizPlayActive}
                        onClick={() => {
                          if (isQuizPlayActive) return;
                          setIsSetMode(true);
                        }}
                        aria-label="문제카드 세트로 돌아가기"
                        title={isQuizPlayActive ? '퀴즈 재생 중에는 이동할 수 없습니다' : '문제카드 세트로 돌아가기'}
                      >
                        <span className="back-to-set-exit-icon" aria-hidden="true">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 19V5M5 12l7-7 7 7" />
                          </svg>
                        </span>
                        <span className="back-to-set-exit-label">나가기</span>
                      </BackToSetExitButton>
                    )}
                  </>
                ) : (
                  <ChatStartButton
                    type="button"
                    onClick={() => {
                      handleOpenChat();
                      // 세트 진행도는 필요 시 수동 새로고침
                      void handleRefreshSetProgress();
                    }}
                    disabled={!currentClass?.classCode}
                  >
                    세트 클래스챗 시작
                  </ChatStartButton>
                )}
              </div>
              <div style={{ width: 44, height: 44 }} />
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr auto',
                alignItems: 'center',
                width: '100%',
                gap: 12,
                marginTop: 20,
              }}
            >
              <div style={{ width: 44, height: 44 }} />
              <div
                style={{
                  justifySelf: 'center',
                  alignSelf: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 12,
                  minWidth: 0,
                  width: '100%',
                }}
              >
                <QuizControlWrapper $isSetMode={isSetMode}>
                {!isSetMode && (
                <ControlTooltipWrapper data-tooltip={multiAnswerTooltip}>
                <MultiAnswerToggleButton
                  $isMultiMode={isMultiAnswerMode}
                  $isDisabled={false}
                  onClick={handleToggleMultiAnswerMode}
                  tabIndex={-1}
                  aria-pressed={isMultiAnswerMode}
                  title={multiAnswerTooltip}
                  aria-label={multiAnswerTooltip}
                >
                  {isMultiAnswerMode ? (
                    <MultiAnswerToggleIcon viewBox="0 0 24 24">
                      <rect x="4" y="5" width="13" height="9" rx="1.5" />
                      <rect x="7" y="8" width="13" height="9" rx="1.5" />
                      <rect x="10" y="11" width="13" height="9" rx="1.5" />
                    </MultiAnswerToggleIcon>
                  ) : (
                    <MultiAnswerToggleIcon viewBox="0 0 24 24">
                      <rect x="5" y="5" width="14" height="14" rx="2" />
                    </MultiAnswerToggleIcon>
                  )}
                </MultiAnswerToggleButton>
              </ControlTooltipWrapper>
              )}
              {/* 퀴즈 재생 버튼 (단일 퀴즈 모드일 때만 표시) */}
              {!isSetMode && (
                <ControlTooltipWrapper data-tooltip={isQuizPlayActive ? '퀴즈 중지' : '퀴즈 재생'}>
                  <PlayStopToggleButton 
                    $isPlaying={isQuizPlayActive}
                    onClick={isQuizPlayActive ? handleStopQuiz : handlePlayQuiz}
                    title={isQuizPlayActive ? '퀴즈 중지' : '퀴즈 재생'}
                    aria-label={isQuizPlayActive ? '퀴즈 중지' : '퀴즈 재생'}
                  >
                    {isQuizPlayActive ? (
                      <StopIcon viewBox="0 0 24 24">
                        <rect x="6" y="6" width="12" height="12" rx="1"/>
                      </StopIcon>
                    ) : (
                      <PlayIcon viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </PlayIcon>
                    )}
                  </PlayStopToggleButton>
                </ControlTooltipWrapper>
              )}
              {/* 세트 재생 순서 토글 (순차 재생/랜덤 재생) - 세트 모드일 때만, 재생 버튼 왼쪽 */}
              {isSetMode && (
                <ControlTooltipWrapper data-tooltip={setPlayOrder === 'random' ? '랜덤 재생 (클릭 시 순차 재생)' : '순차 재생 (클릭 시 랜덤 재생)'}>
                  <SetOrderToggleButton
                    type="button"
                    $isRandom={setPlayOrder === 'random'}
                    onClick={() => setSetPlayOrder(prev => prev === 'sequential' ? 'random' : 'sequential')}
                    title={setPlayOrder === 'random' ? '랜덤 재생' : '순차 재생'}
                    aria-label={setPlayOrder === 'random' ? '랜덤 재생 (클릭 시 순차 재생)' : '순차 재생 (클릭 시 랜덤 재생)'}
                  >
                    {setPlayOrder === 'sequential' ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <line x1="8" y1="6" x2="21" y2="6" />
                        <line x1="8" y1="12" x2="21" y2="12" />
                        <line x1="8" y1="18" x2="21" y2="18" />
                        <line x1="3" y1="6" x2="3.01" y2="6" />
                        <line x1="3" y1="12" x2="3.01" y2="12" />
                        <line x1="3" y1="18" x2="3.01" y2="18" />
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M16 3h5v5" />
                        <path d="M4 20L21 3" />
                        <path d="M21 16v5h-5" />
                        <path d="M15 15l6 6" />
                        <path d="M4 4l5 5" />
                      </svg>
                    )}
                  </SetOrderToggleButton>
                </ControlTooltipWrapper>
              )}
              {/* 세트 재생 버튼 (세트 모드일 때만 표시) */}
              {isSetMode && (
                <ControlTooltipWrapper data-tooltip={isSetPlayActive ? '세트 재생 중지' : '세트 재생'}>
                  <PlayStopToggleButton 
                    $isPlaying={isSetPlayActive}
                    onClick={isSetPlayActive ? handleStopQuizSet : handlePlayQuizSet}
                    title={isSetPlayActive ? '세트 재생 중지' : '세트 재생'}
                    aria-label={isSetPlayActive ? '세트 재생 중지' : '세트 재생'}
                  >
                    {isSetPlayActive ? (
                      <StopIcon viewBox="0 0 24 24">
                        <rect x="6" y="6" width="12" height="12" rx="1"/>
                      </StopIcon>
                    ) : (
                      <PlayIcon viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </PlayIcon>
                    )}
                  </PlayStopToggleButton>
                </ControlTooltipWrapper>
              )}
              {isSetMode && (
                <ControlTooltipWrapper data-tooltip={isStudentCreateMode ? '문제 만들기 종료' : '문제 만들기 시작'}>
                  <StudentCreateToggleButton
                    $isActive={isStudentCreateMode}
                    type="button"
                    onClick={isStudentCreateMode ? handleStopStudentCreateMode : handleStartStudentCreateMode}
                    aria-label="문제 만들기 모드 전환"
                  >
                    {isStudentCreateMode ? (
                      <>
                        <StopIcon viewBox="0 0 24 24" style={{ width: 16, height: 16 }}>
                          <rect x="6" y="6" width="12" height="12" rx="1"/>
                        </StopIcon>
                        문제 만들기
                      </>
                    ) : (
                      <>
                        <PlayIcon viewBox="0 0 24 24" style={{ width: 16, height: 16 }}>
                          <path d="M8 5v14l11-7z"/>
                        </PlayIcon>
                        문제 만들기
                      </>
                    )}
                  </StudentCreateToggleButton>
                </ControlTooltipWrapper>
              )}
              {!isSetMode && (
              <ControlTooltipWrapper data-tooltip={publicModeTooltip}>
                <PublicModeToggleButton
                  $isPublicMode={isPublicMode}
                  $isDisabled={false}
                  onClick={handleTogglePublicMode}
                  tabIndex={-1}
                  aria-pressed={isPublicMode}
                  title={publicModeTooltip}
                  aria-label={publicModeTooltip}
                >
                  <VisuallyHidden>{isPublicMode ? '응답 공개' : '응답 비공개'}</VisuallyHidden>
                  <PublicModeToggleIcon
                    src={isPublicMode ? '/eye_open.svg' : '/eye_close.svg'}
                    alt=""
                    aria-hidden="true"
                  />
                </PublicModeToggleButton>
              </ControlTooltipWrapper>
              )}
              <ControlTooltipWrapper data-tooltip="클래스챗 닫기">
                <CloseChatButton
                  type="button"
                  onClick={handleCloseChat}
                  title="클래스챗 닫기"
                  aria-label="클래스챗 닫기"
                >
                  ×
                </CloseChatButton>
              </ControlTooltipWrapper>
            </QuizControlWrapper>
                {showSetViewButton && (
                  <BackToSetExitButton
                    type="button"
                    disabled={isQuizPlayActive}
                    onClick={() => {
                      if (isQuizPlayActive) return;
                      setIsSetMode(true);
                    }}
                    aria-label="문제카드 세트로 돌아가기"
                    title={isQuizPlayActive ? '퀴즈 재생 중에는 이동할 수 없습니다' : '문제카드 세트로 돌아가기'}
                  >
                    <span className="back-to-set-exit-icon" aria-hidden="true">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 19V5M5 12l7-7 7 7" />
                      </svg>
                    </span>
                    <span className="back-to-set-exit-label">나가기</span>
                  </BackToSetExitButton>
                )}
              </div>
              <div style={{ width: 44, height: 44 }} />
            </div>
          )}
        </QuizControlSection>


        {/* 문제카드 / 세트 섹션 */}
        {isSetMode ? (
          <QuizCardSectionContainer>
            <QuizCardSection>
              {quizSets.length === 0 && (
                <div style={{ color: 'var(--color-text-secondary)', fontSize: 14 }}>
                  문제카드 세트가 없습니다. + 버튼으로 세트를 만들어주세요.
                </div>
              )}
              {quizSets.map((set, idx) => {
                const isSelected = idx === selectedSetIndex;
                return (
                  <QuizCardWrapper
                    key={set.id}
                    $index={idx}
                    $displayedIndex={displayedSetIndex}
                    $totalCards={quizSets.length}
                    onClick={async () => {
                      if (isSetPlayActive || isStudentCreateMode) return;
                      if (isSetMode && idx === selectedSetIndex) {
                        setIsSetMode(false);
                        return;
                      }
                      await handleSelectSetIndex(idx);
                    }}
                  >
                    <QuizCard $isSelected={isSelected} $isSetCard>
                      <QuizCardContent>
                        <SetCardTitle>{set.title || '무제 세트'}</SetCardTitle>
                        <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                          문항 {Object.keys(set.quizzes || {}).length}개
                        </div>
                        <SetCardActions>
                            <SetActionButton
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleOpenSetModal(set.id);
                              }}
                              title="문제세트 수정"
                              aria-label="문제세트 수정"
                            >
                              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M4 21h4.586a2 2 0 0 0 1.414-.586l10.414-10.414a2 2 0 0 0 0-2.828l-2.586-2.586a2 2 0 0 0-2.828 0L4 15.999V21z" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M13.5 6.5l4 4" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              문제세트 수정
                            </SetActionButton>
                            <SetActionButton
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleDeleteSet(set.id);
                              }}
                              title="문제세트 삭제"
                              aria-label="문제세트 삭제"
                            >
                              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 6h18" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M10 11v6" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M14 11v6" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              문제세트 삭제
                            </SetActionButton>
                        </SetCardActions>
                      </QuizCardContent>
                    </QuizCard>
                  </QuizCardWrapper>
                );
              })}
            </QuizCardSection>
            <QuizCardNavigationContainer>
              <NavigationButton
                onClick={async () => {
                  if (isSetPlayActive || isStudentCreateMode) return;
                  const next = Math.max(0, displayedSetIndex - 1);
                  await handleSelectSetIndex(next);
                }}
                disabled={quizSets.length === 0 || displayedSetIndex === 0 || isSetPlayActive || isStudentCreateMode}
                title="이전 세트"
                aria-label="이전 세트"
              >
                &lt;
              </NavigationButton>
              <NavigationButton
                onClick={() => {
                  if (isSetPlayActive || isStudentCreateMode) return;
                  void handleOpenSetModal();
                }}
                disabled={isSetPlayActive || isStudentCreateMode}
                title="세트 생성"
                aria-label="세트 생성"
              >
                +
              </NavigationButton>
              <NavigationButton
                onClick={async () => {
                  if (isSetPlayActive || isStudentCreateMode) return;
                  const next = Math.min(displayedSetIndex + 1, Math.max(quizSets.length - 1, 0));
                  await handleSelectSetIndex(next);
                }}
                disabled={displayedSetIndex >= quizSets.length - 1 || quizSets.length === 0 || isSetPlayActive || isStudentCreateMode}
                title="다음 세트"
                aria-label="다음 세트"
              >
                &gt;
              </NavigationButton>
            </QuizCardNavigationContainer>
          </QuizCardSectionContainer>
        ) : (
        <QuizCardSectionContainer>
          <QuizCardSection>
            {(() => {
              // 최대 5개만 표시: 중앙 카드 + 앞뒤 각각 2개씩 (총 5개, 두 번째 뒤 카드까지)
              // 빈 카드도 포함하므로 savedQuizzes.length + 1까지 고려
              const totalCards = savedQuizzes.length;
              const hasEmptyCard = displayedQuizIndex === totalCards; // 빈 카드가 중앙에 있는지
              const visibleStart = Math.max(0, displayedQuizIndex - 2);
              const visibleEnd = Math.min(totalCards + (hasEmptyCard ? 1 : 0), displayedQuizIndex + 3);
              
              const cards: React.ReactElement[] = [];
              
              // savedQuizzes의 카드들 렌더링
              for (let i = visibleStart; i < Math.min(visibleEnd, totalCards); i++) {
                const quiz = savedQuizzes[i];
                const absoluteIndex = i;
                const isCenterCard = absoluteIndex === displayedQuizIndex;
                
                cards.push(
                  <QuizCardWrapper
                    key={quiz.id}
                    $index={absoluteIndex}
                    $displayedIndex={displayedQuizIndex}
                    $totalCards={totalCards + 1}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isSwitchingQuiz) {
                        return;
                      }
                      void handleSelectQuizIndex(absoluteIndex);
                    }}
                  >
                    <QuizCard
                      $isSelected={isCenterCard && (currentQuizIndex === absoluteIndex)}
                      $hasImage={!!(quiz.questionImageUrl && quiz.questionImageUrl.trim())}
                      style={{
                        cursor: 'pointer',
                        pointerEvents: 'auto',
                      }}
                    >
                      <QuizCardContent>
                        <QuizCardQuestion>
                          {quiz.question || '질문이 없습니다'}
                        </QuizCardQuestion>
                        {(() => {
                          if (quiz.type === 'choice' && quiz.choices && quiz.choices.length > 0) {
                            return (
                              <QuizCardChoices>
                                {quiz.choices.map((choice, choiceIndex) => (
                                  <QuizCardChoiceItem key={choiceIndex}>
                                    {choiceIndex + 1}. {choice.text || '(선택지 없음)'}
                                  </QuizCardChoiceItem>
                                ))}
                              </QuizCardChoices>
                            );
                          }
                          return null;
                        })()}
                        {(() => {
                          if (quiz.type === 'essay' && quiz.guide) {
                            return (
                              <QuizCardGuide>
                                {quiz.guide}
                              </QuizCardGuide>
                            );
                          }
                          return null;
                        })()}
                      </QuizCardContent>
                      <QuizCardActionColumn>
                        <QuizCardNumberBadge>{absoluteIndex + 1}</QuizCardNumberBadge>
                        <QuizCardEditButton
                          $isEnabled={isCenterCard}
                          disabled={!isCenterCard || isSwitchingQuiz}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isCenterCard || isSwitchingQuiz) {
                              return;
                            }
                            openQuizModal(quiz.id, { initialIndex: absoluteIndex });
                          }}
                          title="문제 수정"
                        >
                          <svg viewBox="0 0 24 24" fill="none">
                            <path d="M4 21h4.586a2 2 0 0 0 1.414-.586l10.414-10.414a2 2 0 0 0 0-2.828l-2.586-2.586a2 2 0 0 0-2.828 0L4 15.999V21z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M13.5 6.5l4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </QuizCardEditButton>
                        <QuizCardDeleteButton
                          $isEnabled={isCenterCard}
                          disabled={!isCenterCard}
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (!currentClass?.classCode || !isCenterCard) return;

                            if (window.confirm('이 문제를 삭제하시겠습니까?')) {
                              try {
                                await chatQuizService.deleteQuiz(currentClass.classCode, quiz.id, activeQuizSetId || undefined);
                                // activeQuizSetId가 있으면 해당 세트를 다시 로드, 없으면 전체 퀴즈 로드
                                if (activeQuizSetId) {
                                  await loadQuizSets(activeQuizSetId);
                                  // loadQuizSets가 savedQuizzes를 업데이트하므로 추가 작업 불필요
                                } else {
                                const quizzes = await chatQuizService.getQuizzes(currentClass.classCode);
                                setSavedQuizzes(quizzes);
                                if (quizzes.length > 0) {
                                  if (displayedQuizIndex >= quizzes.length) {
                                    setDisplayedQuizIndex(quizzes.length - 1);
                                  }
                                } else {
                                  setDisplayedQuizIndex(0);
                                  }
                                }

                                if (currentQuizIndex === absoluteIndex) {
                                  setCurrentQuizIndex(-1);
                                }
                              } catch (error) {
                                console.error('❌ 퀴즈 삭제 실패:', error);
                                alert('퀴즈 삭제에 실패했습니다.');
                              }
                            }
                          }}
                          title="문제 삭제"
                        >
                          <svg viewBox="0 0 24 24" fill="none">
                            <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M10 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </QuizCardDeleteButton>
                        <QuizCardResetButton
                          $isEnabled={isCenterCard}
                          disabled={!isCenterCard}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isCenterCard) return;
                            handleResetQuizResponses(quiz.id);
                          }}
                          title="응답 초기화"
                        >
                          <QuizCardResetIcon src="/reset.svg" alt="응답 초기화" />
                        </QuizCardResetButton>
                      </QuizCardActionColumn>
                    </QuizCard>
                  </QuizCardWrapper>
                );
              }
              
              // 빈 카드 렌더링 (displayedQuizIndex === totalCards일 때)
              if (hasEmptyCard && visibleEnd > totalCards) {
                const emptyCardIndex = totalCards;
                const isCenterCard = emptyCardIndex === displayedQuizIndex;
                
                cards.push(
                  <QuizCardWrapper
                    key="empty-card"
                    $index={emptyCardIndex}
                    $displayedIndex={displayedQuizIndex}
                    $totalCards={totalCards + 1}
                    $isCreateButton={true}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isSwitchingQuiz || isQuizPlayActive) return;
                      void handleSelectQuizIndex(emptyCardIndex, { rememberPrevious: true, allowNewCard: true });
                    }}
                    style={isQuizPlayActive ? { opacity: 0.5, pointerEvents: 'none' as const } : undefined}
                  >
                    <QuizCard
                      $isSelected={isCenterCard && currentQuizIndex === -1}
                      style={{
                        cursor: isQuizPlayActive ? 'not-allowed' : 'default',
                        pointerEvents: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '12px',
                      }}
                    >
                      <CreateQuizButtonIcon>
                        +
                      </CreateQuizButtonIcon>
                      <CreateQuizButtonText>
                        새 문제 카드 생성
                      </CreateQuizButtonText>
                    </QuizCard>
                  </QuizCardWrapper>
                );
              }
              
              return cards;
            })()}
          </QuizCardSection>
          
          {/* 네비게이션 버튼 (카드 아래) */}
          <QuizCardNavigationContainer>
                  <NavigationButton
              onClick={() => {
                      if (isSetMode) return;
                      if (savedQuizzes.length > 0 && displayedQuizIndex > 0) {
                        void handleSelectQuizIndex(displayedQuizIndex - 1);
                      }
              }}
                    disabled={
                      isSetMode ||
                      savedQuizzes.length === 0 ||
                      displayedQuizIndex === 0 ||
                      isSwitchingQuiz
                    }
              title="이전 문제"
            >
              &lt;
            </NavigationButton>
            
                  <NavigationButton
              onClick={() => {
                      if (isSetMode || isQuizPlayActive) return;
                      void handleSelectQuizIndex(savedQuizzes.length, { rememberPrevious: true, allowNewCard: true });
                    }}
                    title="문제 추가"
                    disabled={isSetMode || isSwitchingQuiz || isQuizPlayActive}
            >
              +
            </NavigationButton>
            
                  <NavigationButton
              onClick={() => {
                      if (isSetMode) return;
                      // 빈 카드까지 포함하므로 savedQuizzes.length까지 이동 가능
                      if (displayedQuizIndex < savedQuizzes.length - 1) {
                        const nextIndex = displayedQuizIndex + 1;
                        void handleSelectQuizIndex(nextIndex);
                      }
              }}
                    disabled={
                      isSetMode ||
                      displayedQuizIndex >= savedQuizzes.length - 1 ||
                      isSwitchingQuiz
                    }
              title="다음 문제"
            >
              &gt;
            </NavigationButton>
          </QuizCardNavigationContainer>
        </QuizCardSectionContainer>
        )}
        {isSetModalOpen && (
          <QuizModalOverlay 
            role="presentation" 
            onClick={(e) => {
              if (!showSetSaveSuccess) {
                e.stopPropagation();
                handleCloseSetModal();
              }
            }}
          >
            <QuizModalContainer
              role="dialog"
              aria-modal="true"
              aria-label="문제카드 세트 생성/수정"
              onClick={(e) => e.stopPropagation()}
            >
              <QuizModalHeader>
                <QuizModalTitle>문제카드 세트 {editingSetId ? '수정' : '생성'}</QuizModalTitle>
                <QuizModalCloseButton onClick={handleCloseSetModal} aria-label="닫기">
                  ×
                </QuizModalCloseButton>
              </QuizModalHeader>
              <QuizModalBody>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <SetModalModeButtons>
                    <SetModalModeButton
                      type="button"
                      $isActive={setModalMode === 'edit'}
                      onClick={() => setSetModalMode('edit')}
                    >
                      문제편집 모드
                    </SetModalModeButton>
                    <SetModalModeButton
                      type="button"
                      $isActive={setModalMode === 'order'}
                      onClick={() => setSetModalMode('order')}
                    >
                      순서정렬 모드
                    </SetModalModeButton>
                  </SetModalModeButtons>
                  <label style={{ fontWeight: 700, fontSize: 18 }}>
                    세트 제목
                    <input
                      type="text"
                      value={setTitle}
                      onChange={(e) => handleSetTitleChange(e.target.value)}
                      style={{
                        width: '100%',
                        marginTop: 6,
                        padding: '8px 10px',
                        border: '1px solid var(--color-border)',
                        borderRadius: 8,
                        fontSize: 18,
                        fontWeight: 700,
                        textAlign: 'center',
                      }}
                      placeholder="예: 단원평가"
                    />
                  </label>

                  {setModalMode === 'edit' ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                      {setSections.map((section, idx) => (
                        <SetSectionCard key={section.id}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ fontWeight: 800, fontSize: 14, color: 'var(--color-text-primary)' }}>
                            문항 {idx + 1}
                          </div>
                          <IconButton
                            type="button"
                            onClick={() => handleRemoveSection(section.id)}
                            title="문항 삭제"
                          >
                            <TrashIcon viewBox="0 0 24 24" strokeWidth="2">
                              <path d="M3 6h18" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M10 11v6" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M14 11v6" strokeLinecap="round" strokeLinejoin="round"/>
                            </TrashIcon>
                          </IconButton>
                        </div>

                        <QuizEditorOptionRow style={{ justifyContent: 'center' }}>
                          <ControlTooltipWrapper data-tooltip="단일/복수 응답">
                            <MultiAnswerToggleButton
                              $isMultiMode={!!section.defaultMultiAnswerMode}
                              $isDisabled={false}
                              onClick={() =>
                                handleSectionChange(section.id, {
                                  defaultMultiAnswerMode: !section.defaultMultiAnswerMode,
                                })
                              }
                              aria-pressed={!!section.defaultMultiAnswerMode}
                              title="단일/복수 응답"
                            >
                              {section.defaultMultiAnswerMode ? (
                                <MultiAnswerToggleIcon viewBox="0 0 24 24">
                                  <rect x="4" y="5" width="13" height="9" rx="1.5" />
                                  <rect x="7" y="8" width="13" height="9" rx="1.5" />
                                  <rect x="10" y="11" width="13" height="9" rx="1.5" />
                                </MultiAnswerToggleIcon>
                              ) : (
                                <MultiAnswerToggleIcon viewBox="0 0 24 24">
                                  <rect x="5" y="5" width="14" height="14" rx="2" />
                                </MultiAnswerToggleIcon>
                              )}
                            </MultiAnswerToggleButton>
                          </ControlTooltipWrapper>

                          <ControlTooltipWrapper data-tooltip="응답 공개/숨김">
                            <PublicModeToggleButton
                              $isPublicMode={!!section.defaultPublicMode}
                              $isDisabled={false}
                              onClick={() =>
                                handleSectionChange(section.id, {
                                  defaultPublicMode: !section.defaultPublicMode,
                                })
                              }
                              aria-pressed={!!section.defaultPublicMode}
                              title="응답 보기/숨기기"
                            >
                              <VisuallyHidden>
                                {section.defaultPublicMode ? '응답 공개' : '응답 숨김'}
                              </VisuallyHidden>
                              <PublicModeToggleIcon
                                src={section.defaultPublicMode ? '/eye_open.svg' : '/eye_close.svg'}
                                alt=""
                                aria-hidden="true"
                              />
                            </PublicModeToggleButton>
                          </ControlTooltipWrapper>
                        </QuizEditorOptionRow>

                        <QuestionTypeButtonsRow>
                          <QuestionTypeButton
                            $isActive={section.type === 'choice'}
                            onClick={() =>
                              handleSectionChange(section.id, {
                                type: 'choice',
                                choices:
                                  section.type !== 'choice' || section.choices.length === 0
                                    ? createDefaultChoices(section.id)
                                    : section.choices,
                                correctAnswers: [],
                              })
                            }
                          >
                            선택형
                          </QuestionTypeButton>
                          <QuestionTypeButton
                            $isActive={section.type === 'short'}
                            onClick={() =>
                              handleSectionChange(section.id, {
                                type: 'short',
                                correctAnswers: [],
                                answers: section.answers && section.answers.length > 0 ? section.answers : [''],
                              })
                            }
                          >
                            단답형
                          </QuestionTypeButton>
                          <QuestionTypeButton
                            $isActive={section.type === 'essay'}
                            onClick={() =>
                              handleSectionChange(section.id, {
                                type: 'essay',
                                correctAnswers: [],
                              })
                            }
                          >
                            서술형
                          </QuestionTypeButton>
                        </QuestionTypeButtonsRow>

                        <LargeTextInput
                          type="text"
                          value={section.question}
                          onChange={(e) => handleSectionChange(section.id, { question: e.target.value })}
                          placeholder="질문을 입력하세요..."
                        />

                        {section.questionImageUrl && section.questionImageUrl.trim() && (
                          <QuestionImageBox>
                            <QuestionImage src={section.questionImageUrl} alt="질문 이미지" />
                          </QuestionImageBox>
                        )}

                        <QuestionMediaToggleRow>
                          <QuestionMediaToggleButton
                            type="button"
                            $isActive={!!(section.questionImageUrl && section.questionImageUrl.trim())}
                            onClick={() => {
                              openSectionImageSourceMenu(section.id);
                            }}
                            aria-pressed={!!(section.questionImageUrl && section.questionImageUrl.trim())}
                            aria-label={section.questionImageUrl ? '질문 이미지 숨기기' : '질문 이미지 추가'}
                            title={section.questionImageUrl ? '이미지 숨기기' : '이미지 추가'}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                              <path d="M4 7a3 3 0 0 1 3-3h2l1-1h4l1 1h2a3 3 0 0 1 3 3v11a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3V7z" />
                              <circle cx="12" cy="13" r="3.5" />
                            </svg>
                          </QuestionMediaToggleButton>
                          {section.questionImageUrl && section.questionImageUrl.trim() && (
                            <QuestionImageDeleteButton
                              type="button"
                              onClick={() => handleDeleteSectionImage(section.id, section.questionImageUrl || '')}
                              aria-label="이미지 삭제"
                              title="이미지 삭제"
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                <path d="M3 6h18" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M10 11v6" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M14 11v6" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </QuestionImageDeleteButton>
                          )}
                        </QuestionMediaToggleRow>

                        {/* 타입별 상세 입력 */}
                        {section.type === 'choice' && (() => {
                          const rawChoices = Array.isArray(section.choices) ? section.choices : [];
                          const normalizedChoices = (rawChoices.length ? rawChoices : createDefaultChoices(section.id)).map(
                            (ch, idx) => ({
                              id: ch?.id ?? `choice_${section.id}_${idx}`,
                              text: ch?.text ?? '',
                            })
                          );
                          const safeCorrect = (Array.isArray(section.correctAnswers) ? section.correctAnswers : []).filter(
                            (v) => Number.isInteger(v) && v >= 0 && v < normalizedChoices.length
                          );

                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ fontWeight: 700 }}>선택지</div>
                              </div>
                              <ChoiceButtonsContainer>
                                <DndContext
                                  sensors={sensors}
                                  collisionDetection={closestCenter}
                                  onDragEnd={(event) => {
                                    const { active, over } = event;
                                    if (!over || active.id === over.id) return;
                                    const activeIndex = normalizedChoices.findIndex((c) => c.id === String(active.id));
                                    const overIndex = normalizedChoices.findIndex((c) => c.id === String(over.id));
                                    if (activeIndex === -1 || overIndex === -1) return;
                                    const updated = arrayMove(normalizedChoices, activeIndex, overIndex);
                                    const adjustCorrect = safeCorrect
                                      .map((v) => (v === activeIndex ? overIndex : v))
                                      .map((v) =>
                                        activeIndex < overIndex && v > activeIndex && v <= overIndex ? v - 1 : v
                                      )
                                      .map((v) =>
                                        activeIndex > overIndex && v < activeIndex && v >= overIndex ? v + 1 : v
                                      );
                                    handleSectionChange(section.id, { choices: updated, correctAnswers: adjustCorrect });
                                  }}
                                >
                                  <SortableContext items={normalizedChoices.map((c) => c.id)}>
                                    {normalizedChoices.map((choice, cIdx) => (
                                    <SortableChoiceOptionRow
                                      key={choice.id}
                                      option={choice}
                                      index={cIdx}
                                      isEditorLocked={false}
                                      correctAnswers={safeCorrect}
                                      totalChoicesCount={normalizedChoices.length}
                                      onChoiceNumberClick={(index) => {
                                        const isCorrect = safeCorrect.includes(index);
                                        const next = isCorrect ? safeCorrect.filter((v) => v !== index) : [...safeCorrect, index];
                                        handleSectionChange(section.id, { correctAnswers: next });
                                      }}
                                      onChoiceOptionChange={(index, value) => {
                                        const nextChoices = normalizedChoices.map((ch, idx) =>
                                          idx === index ? { ...ch, text: value } : ch
                                        );
                                        handleSectionChange(section.id, { choices: nextChoices });
                                      }}
                                      onRefreshChoiceOption={(index) => {
                                        const nextChoices = normalizedChoices.map((ch, idx) =>
                                          idx === index ? { ...ch, text: '' } : ch
                                        );
                                        handleSectionChange(section.id, { choices: nextChoices });
                                      }}
                                      onDeleteChoiceOption={(index) => {
                                        if (normalizedChoices.length <= 2) return;
                                        const nextChoices = normalizedChoices.filter((_, idx) => idx !== index);
                                        const nextCorrect = safeCorrect
                                          .filter((v) => v !== index)
                                          .map((v) => (v > index ? v - 1 : v));
                                        handleSectionChange(section.id, {
                                          choices: nextChoices,
                                          correctAnswers: nextCorrect,
                                        });
                                      }}
                                    />
                                    ))}
                                  </SortableContext>
                                </DndContext>
                              </ChoiceButtonsContainer>
                              <AddChoiceButton
                                type="button"
                                onClick={() =>
                                  handleSectionChange(section.id, {
                                    choices: [...normalizedChoices, { id: `choice_${Date.now()}`, text: '' }],
                                  })
                                }
                              >
                                <AddChoiceButtonIcon>+</AddChoiceButtonIcon>
                                선택지 추가
                              </AddChoiceButton>
                            </div>
                          );
                        })()}

                        {section.type === 'short' && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <ChoiceButtonsContainer>
                              {(section.answers.length > 0 ? section.answers : ['']).map((ans, aIdx) => (
                                <ChoiceOptionRow key={`${section.id}-answer-${aIdx}`}>
                                  <ChoiceInputWrapper>
                                    <ChoiceInputField
                                      type="text"
                                      value={ans}
                                      onChange={(e) => {
                                        const next = section.answers.map((v, idx) =>
                                          idx === aIdx ? e.target.value : v
                                        );
                                        handleSectionChange(section.id, { answers: next });
                                      }}
                                      placeholder="정답을 입력하세요"
                                    />
                                    <ChoiceInputIcons>
                                      <IconButton
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const next = section.answers.map((v, idx) =>
                                            idx === aIdx ? '' : v
                                          );
                                          handleSectionChange(section.id, { answers: next });
                                        }}
                                        title="정답 비우기"
                                        type="button"
                                      >
                                        <RefreshIcon viewBox="0 0 24 24" strokeWidth="2">
                                          <path d="M1 4v6h6" strokeLinecap="round" strokeLinejoin="round" />
                                          <path d="M23 20v-6h-6" strokeLinecap="round" strokeLinejoin="round" />
                                          <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" strokeLinecap="round" strokeLinejoin="round" />
                                        </RefreshIcon>
                                      </IconButton>
                                      <IconButton
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (section.answers.length <= 1) return;
                                          const next = section.answers.filter((_, idx) => idx !== aIdx);
                                          handleSectionChange(section.id, { answers: next });
                                        }}
                                        title="이 정답 삭제"
                                        type="button"
                                      >
                                        <TrashIcon viewBox="0 0 24 24" strokeWidth="2">
                                          <path d="M3 6h18" strokeLinecap="round" strokeLinejoin="round" />
                                          <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" strokeLinecap="round" strokeLinejoin="round" />
                                          <path d="M10 11v6" strokeLinecap="round" strokeLinejoin="round" />
                                          <path d="M14 11v6" strokeLinecap="round" strokeLinejoin="round" />
                                        </TrashIcon>
                                      </IconButton>
                                    </ChoiceInputIcons>
                                  </ChoiceInputWrapper>
                                </ChoiceOptionRow>
                              ))}
                            </ChoiceButtonsContainer>
                            <AddChoiceButton
                              type="button"
                              onClick={() => handleSectionChange(section.id, { answers: [...section.answers, ''] })}
                            >
                              <AddChoiceButtonIcon>+</AddChoiceButtonIcon>
                              정답 추가
                            </AddChoiceButton>
                          </div>
                        )}

                        {section.type === 'essay' && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <textarea
                              value={section.guide}
                              onChange={(e) => handleSectionChange(section.id, { guide: e.target.value })}
                              placeholder="답변 작성 가이드(선택)"
                              style={{
                                width: '100%',
                                minHeight: 80,
                                padding: 10,
                                border: '2px solid var(--color-border)',
                                borderRadius: 10,
                                fontSize: 14,
                                background: 'var(--color-category-input-bg)',
                              }}
                            />
                          </div>
                        )}
                        </SetSectionCard>
                      ))}
                      <AddSectionButton type="button" onClick={handleAddSection}>
                        + 문항 추가
                      </AddSectionButton>
                      
                      <input
                        ref={setSectionFileInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          e.target.value = '';
                          if (!file || !editingSectionId) return;
                          await handleSelectSectionImageFile(file, editingSectionId);
                        }}
                      />
                    </div>
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={(event: DragEndEvent) => {
                        const { active, over } = event;
                        if (!over || active.id === over.id) return;
                        const activeIndex = setSections.findIndex((s) => s.id === String(active.id));
                        const overIndex = setSections.findIndex((s) => s.id === String(over.id));
                        if (activeIndex === -1 || overIndex === -1) return;
                        setSetSections((prev) => arrayMove(prev, activeIndex, overIndex));
                      }}
                    >
                      <SortableContext items={setSections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                        <OrderModeGrid>
                          {setSections.map((section, idx) => (
                            <SortableOrderCard key={section.id} section={section as any} index={idx} />
                          ))}
                        </OrderModeGrid>
                      </SortableContext>
                    </DndContext>
                  )}
                </div>
              </QuizModalBody>
              <QuizModalFooter>
                <QuizModalButton onClick={handleCloseSetModal}>취소</QuizModalButton>
                <QuizModalButtonPrimary 
                  onClick={handleSaveSet}
                  disabled={!hasSetChanges()}
                >
                  저장
                </QuizModalButtonPrimary>
              </QuizModalFooter>
            </QuizModalContainer>
            {showFloatingSaveButton && !showSetSaveSuccess && (
              <FloatingSaveButton
                type="button"
                $isDisabled={!hasSetChanges()}
                onClick={(e) => {
                  e.stopPropagation();
                  if (hasSetChanges()) {
                    handleSaveSet();
                  }
                }}
                title="저장"
                aria-label="저장"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3H16L21 8V19C21 20.1046 20.1046 21 19 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M17 21V13H7V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7 3V8H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </FloatingSaveButton>
            )}
            {showSetSaveSuccess && (
              <SaveSuccessMessage onClick={(e) => e.stopPropagation()}>
                <SaveSuccessText>저장되었습니다.</SaveSuccessText>
                <SaveSuccessButtons>
                  <SaveSuccessButton onClick={(e) => {
                    e.stopPropagation();
                    setShowSetSaveSuccess(false);
                  }}>
                    편집계속
                  </SaveSuccessButton>
                  <SaveSuccessButton onClick={(e) => {
                    e.stopPropagation();
                    handleCloseSetModal();
                  }}>
                    종료하기
                  </SaveSuccessButton>
                </SaveSuccessButtons>
              </SaveSuccessMessage>
            )}
          </QuizModalOverlay>
        )}

        {isQuizModalOpen && (
          <QuizModalOverlay
            role="presentation"
            onClick={(e) => {
              e.stopPropagation();
              handleCancelQuizModal();
            }}
          >
            <QuizModalContainer
              role="dialog"
              aria-modal="true"
              aria-labelledby="quiz-modal-title"
              onClick={(e) => e.stopPropagation()}
            >
              <QuizModalHeader>
                <QuizModalTitle id="quiz-modal-title">
                  {editingQuizId ? '문제 수정' : '새 문제 생성'}
                </QuizModalTitle>
                <QuizModalCloseButton
                  type="button"
                  onClick={handleCancelQuizModal}
                  aria-label="닫기"
                >
                  ×
                </QuizModalCloseButton>
              </QuizModalHeader>
              <QuizModalBody>
                <QuizEditorContent $isLocked={false}>
                  <FirstRowContainer>
                    <FirstRowFirstCol>
                      <QuizEditorOptionRow>
                        <MultiAnswerToggleButton
                          $isMultiMode={editorMultiAnswerSetting}
                          $isDisabled={isEditorLocked}
                          onClick={() => {
                            if (isEditorLocked) return;
                            setEditorMultiAnswerSetting((prev) => !prev);
                          }}
                          aria-pressed={editorMultiAnswerSetting}
                          title={editorMultiAnswerSetting ? '멀티 응답 저장' : '단일 응답 저장'}
                        >
                          {editorMultiAnswerSetting ? (
                            <MultiAnswerToggleIcon viewBox="0 0 24 24">
                              <rect x="4" y="5" width="13" height="9" rx="1.5" />
                              <rect x="7" y="8" width="13" height="9" rx="1.5" />
                              <rect x="10" y="11" width="13" height="9" rx="1.5" />
                            </MultiAnswerToggleIcon>
                          ) : (
                            <MultiAnswerToggleIcon viewBox="0 0 24 24">
                              <rect x="5" y="5" width="14" height="14" rx="2" />
                            </MultiAnswerToggleIcon>
                          )}
                        </MultiAnswerToggleButton>
                        <PublicModeToggleButton
                          $isPublicMode={editorPublicModeSetting}
                          $isDisabled={isEditorLocked}
                          onClick={() => {
                            if (isEditorLocked) return;
                            setEditorPublicModeSetting((prev) => !prev);
                          }}
                          aria-pressed={editorPublicModeSetting}
                          title={editorPublicModeSetting ? '응답 공개 저장' : '응답 비공개 저장'}
                        >
                          <VisuallyHidden>
                            {editorPublicModeSetting ? '응답 공개 저장' : '응답 비공개 저장'}
                          </VisuallyHidden>
                          <PublicModeToggleIcon
                            src={editorPublicModeSetting ? '/eye_open.svg' : '/eye_close.svg'}
                            alt=""
                            aria-hidden="true"
                          />
                        </PublicModeToggleButton>
                      </QuizEditorOptionRow>
                      <QuestionTypeButtonsRow>
                        <QuestionTypeButton
                          $isActive={questionType === 'choice'}
                          disabled={isEditorLocked}
                          onClick={() => setQuestionType('choice')}
                        >
                          선택형
                        </QuestionTypeButton>
                        <QuestionTypeButton
                          $isActive={questionType === 'short'}
                          disabled={isEditorLocked}
                          onClick={() => setQuestionType('short')}
                        >
                          단답형
                        </QuestionTypeButton>
                        <QuestionTypeButton
                          $isActive={questionType === 'essay'}
                          disabled={isEditorLocked}
                          onClick={() => setQuestionType('essay')}
                        >
                          서술형
                        </QuestionTypeButton>
                      </QuestionTypeButtonsRow>
                    </FirstRowFirstCol>
                    <FirstRowSecondCol>
                      <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '12px' }}>
                        <LargeTextInput
                          type="text"
                          value={messageText}
                          onChange={(e) => {
                            if (isEditorLocked) return;
                            setMessageText(e.target.value);
                          }}
                          placeholder="질문을 입력하세요..."
                          readOnly={isEditorLocked}
                          tabIndex={1}
                          onKeyDown={(e) => {
                            if (e.key === 'Tab') {
                              console.log('🔍 [LargeTextInput] Tab key down:', {
                                shiftKey: e.shiftKey,
                                activeElement: document.activeElement?.tagName
                              });
                            }
                          }}
                          onFocus={(e) => {
                            console.log('👁️ [LargeTextInput] Focus:', { tabIndex: e.target.tabIndex });
                          }}
                        />

                        {isQuestionImageMode && (
                          questionImageUrl ? (
                            <QuestionImageBox>
                              <QuestionImage src={questionImageUrl} alt="질문 이미지" />
                            </QuestionImageBox>
                          ) : (
                            <QuestionImagePlaceholder>이미지 영역 (16:9)</QuestionImagePlaceholder>
                          )
                        )}

                        <QuestionMediaToggleRow>
                          <QuestionMediaToggleButton
                            type="button"
                            $isActive={isQuestionImageMode}
                            onClick={() => {
                              openImageSourceMenu();
                            }}
                            aria-pressed={isQuestionImageMode}
                            aria-label={isQuestionImageMode ? '질문 이미지 숨기기' : '질문 이미지 추가'}
                            title={isQuestionImageMode ? '이미지 숨기기' : '이미지 추가'}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                              <path d="M4 7a3 3 0 0 1 3-3h2l1-1h4l1 1h2a3 3 0 0 1 3 3v11a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3V7z" />
                              <circle cx="12" cy="13" r="3.5" />
                            </svg>
                          </QuestionMediaToggleButton>
                          {questionImageUrl && questionImageUrl.trim().length > 0 && (
                            <QuestionImageDeleteButton
                              type="button"
                              onClick={handleDeleteQuestionImage}
                              disabled={isEditorLocked}
                              aria-label="이미지 삭제"
                              title="이미지 삭제"
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                <path d="M3 6h18" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M10 11v6" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M14 11v6" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </QuestionImageDeleteButton>
                          )}
                        </QuestionMediaToggleRow>

                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            e.target.value = '';
                            if (!file) return;
                            await handleSelectImageFile(file);
                          }}
                        />
                      </div>
                    </FirstRowSecondCol>
                    <FirstRowThirdCol>
                      {questionType === 'choice' && (
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={handleChoiceDragEnd}
                        >
                          <SortableContext items={choiceOptions.map(opt => opt.id)} strategy={verticalListSortingStrategy}>
                            <ChoiceButtonsContainer>
                              {choiceOptions.map((option, index) => (
                                <SortableChoiceOptionRow
                                  key={option.id}
                                  option={option}
                                  index={index}
                                  isEditorLocked={isEditorLocked}
                                  correctAnswers={correctAnswers}
                                  totalChoicesCount={choiceOptions.length}
                                  onChoiceNumberClick={handleChoiceNumberClick}
                                  onChoiceOptionChange={handleChoiceOptionChange}
                                  onRefreshChoiceOption={handleRefreshChoiceOption}
                                  onDeleteChoiceOption={handleDeleteChoiceOption}
                                />
                              ))}
                              {choiceOptions.length < 6 && (
                                <AddChoiceButton
                                  onClick={handleAddChoiceOption}
                                  disabled={isEditorLocked}
                                  tabIndex={-1}
                                >
                                  <AddChoiceButtonIcon>+</AddChoiceButtonIcon>
                                </AddChoiceButton>
                              )}
                            </ChoiceButtonsContainer>
                          </SortableContext>
                        </DndContext>
                      )}
                      {questionType === 'short' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
                          <ChoiceButtonsContainer>
                            {(answerBlocks.length > 0 ? answerBlocks : ['']).map((ans, aIdx) => (
                              <ChoiceOptionRow key={`short-${aIdx}`}>
                                <ChoiceInputWrapper>
                                  <ChoiceInputField
                              type="text"
                                    value={ans}
                                    onChange={(e) => {
                                      if (isEditorLocked) return;
                                      const next = (answerBlocks.length > 0 ? answerBlocks : ['']).map((v, idx) =>
                                        idx === aIdx ? e.target.value : v
                                      );
                                      setAnswerBlocks(next);
                                    }}
                                    placeholder="정답을 입력하세요"
                              readOnly={isEditorLocked}
                                  />
                                  <ChoiceInputIcons>
                                    <IconButton
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (isEditorLocked) return;
                                        const next = (answerBlocks.length > 0 ? answerBlocks : ['']).map((v, idx) =>
                                          idx === aIdx ? '' : v
                                        );
                                        setAnswerBlocks(next);
                                      }}
                                      title="정답 비우기"
                                      type="button"
                                      disabled={isEditorLocked}
                                    >
                                      <RefreshIcon viewBox="0 0 24 24" strokeWidth="2">
                                        <path d="M1 4v6h6" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M23 20v-6h-6" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" strokeLinecap="round" strokeLinejoin="round" />
                                      </RefreshIcon>
                                    </IconButton>
                                    <IconButton
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (isEditorLocked) return;
                                        const base = answerBlocks.length > 0 ? answerBlocks : [''];
                                        if (base.length <= 1) {
                                          setAnswerBlocks(['']);
                                          return;
                                        }
                                        const next = base.filter((_, idx) => idx !== aIdx);
                                        setAnswerBlocks(next.length > 0 ? next : ['']);
                                      }}
                                      title="이 정답 삭제"
                                      type="button"
                                      disabled={isEditorLocked}
                                    >
                                      <TrashIcon viewBox="0 0 24 24" strokeWidth="2">
                                        <path d="M3 6h18" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M10 11v6" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M14 11v6" strokeLinecap="round" strokeLinejoin="round" />
                                      </TrashIcon>
                                    </IconButton>
                                  </ChoiceInputIcons>
                                </ChoiceInputWrapper>
                              </ChoiceOptionRow>
                            ))}
                          </ChoiceButtonsContainer>
                          <AddChoiceButton
                            onClick={() => {
                              if (isEditorLocked) return;
                              const base = answerBlocks.length > 0 ? answerBlocks : [''];
                              setAnswerBlocks([...base, '']);
                            }}
                            disabled={isEditorLocked}
                            tabIndex={-1}
                          >
                            <AddChoiceButtonIcon>+</AddChoiceButtonIcon>
                            정답 추가
                          </AddChoiceButton>
                        </div>
                      )}
                      {questionType === 'essay' && (
                        <GuideMessageContainer>
                          <GuideMessageInput
                            value={guideMessage}
                            onChange={(e) => {
                              if (isEditorLocked) return;
                              setGuideMessage(e.target.value);
                            }}
                            placeholder="구체적인 안내 메시지를 입력하세요..."
                            rows={4}
                            disabled={isEditorLocked}
                            tabIndex={2}
                          />
                        </GuideMessageContainer>
                      )}
                    </FirstRowThirdCol>
                  </FirstRowContainer>
                  <BottomButtonRow>
                    {!editingQuizId && (
                      <CancelButton data-variant="modal-secondary" disabled={isEditorLocked} onClick={handleCancelQuizModal}>
                        취소하기
                      </CancelButton>
                    )}
                    <SaveButton disabled={isEditorLocked || !hasQuizChanges()} onClick={handleSaveQuiz}>
                      {editingQuizId ? '수정하기' : '저장하기'}
                    </SaveButton>
                  </BottomButtonRow>
                </QuizEditorContent>
              </QuizModalBody>
            </QuizModalContainer>
          </QuizModalOverlay>
        )}

        {isImageSourceMenuOpen && (
          <MediaModalOverlay role="presentation" onClick={() => {
            setIsImageSourceMenuOpen(false);
            setEditingSectionId(null);
          }}>
            <MediaModalContainer role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
              <MediaModalTitle>이미지 추가</MediaModalTitle>
              <MediaModalActions>
                <MediaSecondaryButton type="button" onClick={() => {
                  setIsImageSourceMenuOpen(false);
                  setEditingSectionId(null);
                }}>
                  취소
                </MediaSecondaryButton>
                <MediaSecondaryButton 
                  type="button" 
                  onClick={() => {
                    if (editingSectionId) {
                      handleStartSectionFileUpload(editingSectionId);
                    } else {
                      handleStartFileUpload();
                    }
                  }}
                  disabled={isEditorLocked}
                >
                  파일 업로드
                </MediaSecondaryButton>
                <MediaPrimaryButton 
                  type="button" 
                  onClick={() => {
                    if (editingSectionId) {
                      handleStartSectionCamera(editingSectionId);
                    } else {
                      handleStartCamera();
                    }
                  }}
                  disabled={isEditorLocked}
                >
                  사진찍기
                </MediaPrimaryButton>
              </MediaModalActions>
            </MediaModalContainer>
          </MediaModalOverlay>
        )}

        {isCameraModalOpen && (
          <MediaModalOverlay role="presentation" onClick={() => setIsCameraModalOpen(false)}>
            <MediaModalContainer role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
              <MediaModalTitle>사진찍기</MediaModalTitle>
              <div style={{ width: '100%', aspectRatio: '4 / 3', background: '#000', borderRadius: 12, overflow: 'hidden' }}>
                <video ref={videoRef} style={{ width: '100%', height: '100%', objectFit: 'cover' }} playsInline muted />
              </div>
              <MediaModalActions>
                <MediaSecondaryButton type="button" onClick={() => setIsCameraModalOpen(false)}>
                  취소
                </MediaSecondaryButton>
                <MediaPrimaryButton type="button" onClick={handleCaptureFromCamera}>
                  촬영
                </MediaPrimaryButton>
              </MediaModalActions>
            </MediaModalContainer>
          </MediaModalOverlay>
        )}

        {isCropModalOpen && (
          <MediaModalOverlay role="presentation" onClick={() => (isUploadingQuestionImage ? null : setIsCropModalOpen(false))}>
            <MediaModalContainer role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
              <MediaModalTitle>이미지 크롭 (16:9)</MediaModalTitle>
              <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', background: '#111', borderRadius: 12, overflow: 'hidden' }}>
                <Cropper
                  image={rawImageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={16 / 9}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={(_, croppedAreaPixels) => {
                    cropPixelsRef.current = croppedAreaPixels as CropAreaPixels;
                  }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', fontWeight: 700 }}>확대</div>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.01}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  style={{ flex: 1 }}
                  disabled={isUploadingQuestionImage}
                />
              </div>
              <MediaModalActions>
                <MediaSecondaryButton
                  type="button"
                  onClick={() => setIsCropModalOpen(false)}
                  disabled={isUploadingQuestionImage}
                >
                  취소
                </MediaSecondaryButton>
                <MediaPrimaryButton
                  type="button"
                  onClick={handleUploadCroppedImage}
                  disabled={isUploadingQuestionImage}
                >
                  {isUploadingQuestionImage ? '업로드 중...' : '확인'}
                </MediaPrimaryButton>
              </MediaModalActions>
            </MediaModalContainer>
          </MediaModalOverlay>
        )}

        {/* 두 번째 줄: 번호/자리 토글 */}
        <SecondRowContainer $isSeatMode={viewMode === 'seat'}>
          <SectionTitleContainer>
            <SectionTitleNavigationButton
              onClick={async () => {
                if (isStudentCreateMode || isSetPlayActive) {
                  showToastMessage('재생 중에는 세트/문제를 이동할 수 없습니다');
                  return;
                }
                if (isSetMode) {
                  if (quizSets.length > 0 && displayedSetIndex > 0) {
                    await handleSelectSetIndex(displayedSetIndex - 1);
                  }
                } else {
                  if (savedQuizzes.length > 0 && displayedQuizIndex > 0) {
                    void handleSelectQuizIndex(displayedQuizIndex - 1);
                  }
                }
              }}
              disabled={
                isSetMode
                  ? quizSets.length === 0 || displayedSetIndex === 0 || isSetPlayActive || isStudentCreateMode
                  : savedQuizzes.length === 0 || displayedQuizIndex === 0 || isSwitchingQuiz
              }
              title="이전 문제"
            >
              &lt;
            </SectionTitleNavigationButton>
            <SectionTitle>모니터링</SectionTitle>
            <SectionTitleNavigationButton
              onClick={async () => {
                if (isStudentCreateMode || isSetPlayActive) {
                  showToastMessage('재생 중에는 세트/문제를 이동할 수 없습니다');
                  return;
                }
                if (isSetMode) {
                  const hasSets = quizSets.length > 0;
                  if (hasSets && displayedSetIndex < quizSets.length - 1) {
                    await handleSelectSetIndex(displayedSetIndex + 1);
                  }
                } else {
                  // 마지막 문제카드까지만 이동 가능
                  if (displayedQuizIndex < savedQuizzes.length - 1) {
                    const nextIndex = displayedQuizIndex + 1;
                    void handleSelectQuizIndex(nextIndex);
                  }
                }
              }}
              disabled={
                isSetMode
                  ? quizSets.length === 0 || displayedSetIndex >= quizSets.length - 1 || isSetPlayActive || isStudentCreateMode
                  : displayedQuizIndex >= savedQuizzes.length - 1 || isSwitchingQuiz
              }
              title="다음 문제"
            >
              &gt;
            </SectionTitleNavigationButton>
          </SectionTitleContainer>
        <MonitoringControlsRow>
          {/* 1행: 번호/자리 토글 */}
          <MonitoringControlsFirstRow>
            <ViewModeToggleButton 
              $isSeatMode={viewMode === 'seat'}
              $isDisabled={false}
              $isInitialLoad={!isSettingsRestored}
              onClick={() => {
                if (viewMode === 'number') {
                  setViewMode('seat');
                } else {
                  setViewMode('number');
                }
              }}
              tabIndex={-1}
            >
              <ViewModeToggleOption $isActive={viewMode === 'number'} $isDisabled={false}>
                번호
              </ViewModeToggleOption>
              <ViewModeToggleOption $isActive={viewMode === 'seat'} $isDisabled={false}>
                자리
              </ViewModeToggleOption>
            </ViewModeToggleButton>
          </MonitoringControlsFirstRow>
          
          {/* 2행: 정답자선택, 전체선택, 선택취소 */}
          <MonitoringControlsSecondRow>
            <CompactButton onClick={handleSelectCorrectStudents} disabled={!hasCorrectAnswers} tabIndex={-1}>
              정답자선택
            </CompactButton>
            <CompactButton onClick={handleSelectAll} tabIndex={-1}>
              전체선택
            </CompactButton>
            <CompactButton onClick={handleDeselectAll} disabled={selectedStudents.size === 0} tabIndex={-1}>
              선택취소
            </CompactButton>
          </MonitoringControlsSecondRow>
          
          {/* 3행: 진행 새로고침, 프레젠테이션 */}
          <MonitoringControlsThirdRow>
            {isSetMode && (
              <ResetResponsesButton
                type="button"
                onClick={handleRefreshSetProgress}
                title="세트 진행 새로고침"
              >
                진행 새로고침
              </ResetResponsesButton>
            )}
            {!isSetMode && (
              <ResetResponsesButton
                disabled={!canResetResponses}
                onClick={() => {
                  if (!selectedQuizForReset) return;
                  handleResetQuizResponses(selectedQuizForReset.id);
                }}
                title="현재 문제 응답 초기화"
              >
                <QuizCardResetIcon src="/reset.svg" alt="응답 초기화" />
                응답 초기화
              </ResetResponsesButton>
            )}
            <PresentationButton
              type="button"
              disabled={!canStartPresentation}
              onClick={handleOpenPresentationModal}
              title="프레젠테이션"
            >
              프레젠테이션
            </PresentationButton>
          </MonitoringControlsThirdRow>
        </MonitoringControlsRow>
        </SecondRowContainer>

        {/* 멤버 블럭 영역 */}
        {viewMode === 'number' ? (
          <Content>
            <StudentsContainer $alignment={gridAlignment}>
              {isLoadingStudents ? (
                <div>로딩 중...</div>
              ) : students.length > 0 ? (
                students.map((student) => {
                  const response = displayedResponses[student.id];
                  const isArray = Array.isArray(response);
                  const currentIndex = studentAnswerIndices[student.id] ?? (isArray ? response.length - 1 : 0);
                  
                  const isActiveStudent = isStudentActive(student.id);
                  
                  // 멀티 모드일 때 배열에서 답변 가져오기
                  let currentResponse: StudentQuizResponse | null = null;
                  if (isArray && response.length > 0) {
                    // 배열인 경우: 현재 인덱스의 답변 가져오기
                    const validIndex = Math.max(0, Math.min(currentIndex, response.length - 1));
                    currentResponse = response[validIndex] || null;
                  } else if (!isArray && response) {
                    // 단일 답변인 경우
                    currentResponse = response;
                  }
                  
                  const isConnected = isChatOpen ? connectedStudentIds.includes(student.id) : true;
                  const hasStoredResponse = isArray ? (Array.isArray(response) && response.length > 0) : !!response;
                  const isWaitingForResponse = shouldShowMemberQuizTypingWait({
                    isQuizPlaying,
                    isConnected,
                    isStudentCreateMode,
                    isSetMode,
                    isMultiAnswerMode,
                    activeQuizType: activeLiveQuizForCoordinatorUi?.type,
                    rawResponse: response,
                  });
                  const hasResponseData = hasStoredResponse || isWaitingForResponse;
                  const selectedSet =
                    isSetMode && selectedSetIndex >= 0 && selectedSetIndex < quizSets.length
                      ? quizSets[selectedSetIndex]
                      : null;
                  const totalSetQuizzes = selectedSet ? Object.keys(selectedSet.quizzes || {}).length : 0;
                  const answeredCorrectForSet = setProgress[student.id]?.correct ?? 0;
                  const answeredEssayForSet = setProgress[student.id]?.essay ?? 0;
                  const answeredAnyForSet = setProgress[student.id]?.answered ?? 0;
                  const totalForStudent = setProgress[student.id]?.total ?? totalSetQuizzes;
                  
                  const responseLength = isArray && Array.isArray(response) ? response.length : 0;
                  const removalIndex = isArray && responseLength > 0
                    ? Math.max(0, Math.min(currentIndex, responseLength - 1))
                    : undefined;
                  
                  return (
                  <StudentBlock
                    key={student.id}
                    $isActive={isActiveStudent}
                    $isSelected={selectedStudents.has(student.id)}
                    onClick={() => handleStudentSelection(student.id)}
                  >
                    <StudentResetButton
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleResetStudentResponse(student.id, removalIndex);
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      disabled={!hasResponseData}
                      title="멤버 응답 삭제"
                      aria-label={`${student.name || '멤버'} 응답 삭제`}
                    >
                      <TrashIcon viewBox="0 0 24 24" strokeWidth="2">
                        <path d="M3 6h18" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M10 11v6" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M14 11v6" strokeLinecap="round" strokeLinejoin="round" />
                      </TrashIcon>
                    </StudentResetButton>
                    <MessageBubble>
                      {isStudentCreateMode ? (
                        <MessageContent $isWaiting={false}>
                          {studentCreateSubmittedIds.has(student.id) ? (
                            <StudentCreateSubmittedBlock>
                              <StudentCreateSubmittedIcon viewBox="0 0 24 24" aria-hidden="true">
                                <circle cx="12" cy="12" r="9" />
                                <path d="M9 12l2 2 4-4" />
                              </StudentCreateSubmittedIcon>
                              <StudentCreateSubmittedLabel>제출완료</StudentCreateSubmittedLabel>
                            </StudentCreateSubmittedBlock>
                          ) : (
                            <MessageText>{'\u00A0'}</MessageText>
                          )}
                        </MessageContent>
                      ) : isSetMode && selectedSet ? (
                        (() => {
                          const total = totalForStudent || 0;
                          const answered = answeredAnyForSet;
                          const correct = answeredCorrectForSet;
                          const essay = answeredEssayForSet;
                          const pctAnswered = total > 0 ? Math.min(100, (answered / total) * 100) : 0;
                          const pctCorrect = total > 0 ? Math.min(100, (correct / total) * 100) : 0;
                          const pctEssay = total > 0 ? Math.min(100, (essay / total) * 100) : 0;
                          return (
                            <>
                              <QuizSetBarWrap>
                                <QuizSetBar>
                                  {pctAnswered > 0 && <QuizSetBarResponse $widthPct={pctAnswered} $leftPct={0} />}
                                  {pctCorrect > 0 && <QuizSetBarCorrect $widthPct={pctCorrect} $leftPct={0} />}
                                  {pctEssay > 0 && <QuizSetBarEssay $widthPct={pctEssay} $leftPct={pctCorrect} />}
                                </QuizSetBar>
                                <QuizSetBarTextRow>
                                  <QuizSetTextCorrect>{correct}</QuizSetTextCorrect>
                                  {essay > 0 && <QuizSetTextEssay>+{essay}</QuizSetTextEssay>}
                                  <QuizSetTextAnswered> / {answered}</QuizSetTextAnswered>
                                  <QuizSetTextTotal> / {total}</QuizSetTextTotal>
                                </QuizSetBarTextRow>
                              </QuizSetBarWrap>
                            </>
                          );
                        })()
                      ) : (
                      <>
                      {/* 퀴즈 응답 표시 */}
                      {(() => {
                        if (!currentResponse) {
                          return null;
                        }
                        
                        // submittedAt이 있거나 answer가 있으면 표시
                        // 멀티 모드에서는 answer가 있으면 표시 (submittedAt이 없어도 타이핑 중일 수 있음)
                        const hasSubmittedAt = !!currentResponse.submittedAt;
                        const hasAnswer = currentResponse.answer !== undefined && 
                                         currentResponse.answer !== null &&
                                         (typeof currentResponse.answer === 'string' ? currentResponse.answer.trim().length > 0 : 
                                          Array.isArray(currentResponse.answer) ? currentResponse.answer.length > 0 : 
                                          true);
                        
                        // 답변이 있으면 표시
                        if (hasSubmittedAt || hasAnswer) {
                          // 제출된 응답 표시
                          if (typeof currentResponse.answer === 'string') {
                            const answerText = currentResponse.answer.trim();
                            if (answerText) {
                              return (
                                <MessageContent $isWaiting={isWaitingForResponse}>
                                  <MessageText>{answerText}</MessageText>
                                </MessageContent>
                              );
                            } else {
                              return null;
                            }
                          } else if (Array.isArray(currentResponse.answer)) {
                            // 선택형: 네모 도형에 숫자 표시 (정렬 및 레벨 색상 적용)
                            if (currentResponse.answer.length > 0) {
                              const sortedAnswers = [...currentResponse.answer].sort((a, b) => a - b);
                              return (
                                <MessageContent $isWaiting={isWaitingForResponse}>
                                  <ChoiceBadgesContainer>
                                    {sortedAnswers.map((index) => {
                                      // 선택 버튼 번호는 1번부터 시작하므로, index + 1을 levelIndex로 사용
                                      const levelIndex = index < 6 ? index + 1 : undefined;
                                      return (
                                        <ChoiceBadge key={index} $levelIndex={levelIndex}>
                                          {index + 1}
                                        </ChoiceBadge>
                                      );
                                    })}
                                  </ChoiceBadgesContainer>
                                </MessageContent>
                              );
                            } else {
                              return null;
                            }
                          } else {
                            return null;
                          }
                        }
                        
                        return null;
                      })()}
                      
                      {/* 대기 중인 멤버 표시 (접속했지만 아직 답변 없음) */}
                      {isWaitingForResponse && (
                        <TypingIndicator $isOverlay={true}>
                          <span />
                          <span />
                          <span />
                        </TypingIndicator>
                      )}
                      
                      {/* 여러 답변 네비게이션 (멀티답변 모드이고 답변이 여러 개인 경우) */}
                      {isArray && response.length > 1 && (
                        <AnswerNavigationContainer>
                          <AnswerNavigationButton
                            disabled={currentIndex === 0}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              setStudentAnswerIndices((prev) => ({
                                ...prev,
                                [student.id]: Math.max(0, (prev[student.id] || 0) - 1)
                              }));
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            &lt;
                          </AnswerNavigationButton>
                          <AnswerNavigationText>
                            {currentIndex + 1}/{response.length}
                          </AnswerNavigationText>
                          <AnswerNavigationButton
                            disabled={currentIndex >= response.length - 1}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              setStudentAnswerIndices((prev) => ({
                                ...prev,
                                [student.id]: Math.min(response.length - 1, (prev[student.id] || 0) + 1)
                              }));
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            &gt;
                          </AnswerNavigationButton>
                        </AnswerNavigationContainer>
                      )}
                      
                      {/* 정답 뱃지 */}
                      {(() => {
                        if (!currentResponse || !currentResponse.submittedAt) {
                          return null;
                        }
                        
                        const hasAnswer =
                          currentResponse.answer !== undefined &&
                          currentResponse.answer !== null &&
                          (typeof currentResponse.answer === 'string'
                            ? currentResponse.answer.trim().length > 0
                            : Array.isArray(currentResponse.answer)
                              ? currentResponse.answer.length > 0
                              : true);
                        const badgeStatus =
                          currentResponse.isCorrect === true
                            ? 'correct'
                            : currentResponse.isCorrect === false
                              ? 'wrong'
                              : hasAnswer
                                ? 'neutral'
                                : 'wrong';

                        return (
                          <ResultBadge $status={badgeStatus}>
                            {badgeStatus === 'correct' ? (
                              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                                <circle cx="12" cy="12" r="9" stroke="currentColor" fill="none" />
                              </svg>
                            ) : badgeStatus === 'wrong' ? (
                              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                              </svg>
                            ) : (
                              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                                <circle cx="12" cy="12" r="9" stroke="currentColor" fill="none" />
                              </svg>
                            )}
                          </ResultBadge>
                        );
                      })()}
                      </>
                      )}
                    </MessageBubble>
                    
                      <ProfileImageContainer>
                        <ProfileTextContainer>
                          <ProfileNumber>
                            {student.number || ''}
                          </ProfileNumber>
                          <ProfileName>{student.name || ''}</ProfileName>
                        </ProfileTextContainer>
                      <ProfileImageWrapper>
                        <CachedProfileImage
                          imageId={resolveMemberProfileSource(student, memberProfileDisplayMode)}
                          alt={student.name || '프로필'}
                        />
                        {selectedStudents.has(student.id) && (
                          <SelectionIndicator>
                            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="16" cy="16" r="16" fill="var(--color-accent-strong)"/>
                              <path d="M10 16L14 20L22 12" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </SelectionIndicator>
                        )}
                      </ProfileImageWrapper>
                    </ProfileImageContainer>
                  </StudentBlock>
                  );
                })
              ) : (
                <div>멤버가 없습니다.</div>
              )}
            </StudentsContainer>
            
            {/* 그리드 정렬 토글 */}
            <GridAlignmentContainer>
              <GridAlignmentToggle 
                onClick={() => setGridAlignment(prev => prev === 'left' ? 'center' : 'left')}
                tabIndex={-1}
                title={gridAlignment === 'left' ? '중앙 정렬로 변경' : '왼쪽 정렬로 변경'}
              >
                <GridAlignmentOption $isActive={gridAlignment === 'left'}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 6h18M3 12h12M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </GridAlignmentOption>
                <GridAlignmentOption $isActive={gridAlignment === 'center'}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 6h18M6 12h12M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </GridAlignmentOption>
              </GridAlignmentToggle>
            </GridAlignmentContainer>

            {guestSection}
          </Content>
        ) : (
          <>
            {seatArrangementError ? (
              <SeatModeContainer>
                <div style={{ color: '#666', fontSize: '14px', padding: '20px' }}>{seatArrangementError}</div>
                {guestSection}
              </SeatModeContainer>
            ) : seatArrangementData && seatArrangementData.length > 0 ? (
              (() => {
                // 자리 모드 그리드: 상/하단의 "완전히 비어있는 행"만 trim (중간 빈 행은 유지)
                // 180도 회전 적용 후 좌표를 기준으로 trim 해야 함
                const GRID_TRACKS = 16; // 0..15
                const ROTATE_MAX_INDEX = 14; // 기존 로직 유지 (PresentationPage 방식)

                const usedRows = new Set<number>();
                const usedCols = new Set<number>();
                let minUsedRow = Number.POSITIVE_INFINITY;
                let maxUsedRow = Number.NEGATIVE_INFINITY;

                seatArrangementData.forEach((seatStudent: any) => {
                  const seatRow = typeof seatStudent?.seatRow === 'number' ? seatStudent.seatRow : 0;
                  const seatCol = typeof seatStudent?.seatCol === 'number' ? seatStudent.seatCol : 0;

                  const rotatedRow = isSeatRotated ? (ROTATE_MAX_INDEX - seatRow) : seatRow;
                  const rotatedCol = isSeatRotated ? (ROTATE_MAX_INDEX - seatCol) : seatCol;

                  // 학생 블럭은 2x2 (span 2)
                  const occupiedRows = [rotatedRow, rotatedRow + 1];
                  const occupiedCols = [rotatedCol, rotatedCol + 1];

                  occupiedRows.forEach((r) => {
                    if (r < 0 || r >= GRID_TRACKS) return;
                    usedRows.add(r);
                    minUsedRow = Math.min(minUsedRow, r);
                    maxUsedRow = Math.max(maxUsedRow, r);
                  });

                  occupiedCols.forEach((c) => {
                    if (c < 0 || c >= GRID_TRACKS) return;
                    usedCols.add(c);
                  });
                });

                const effectiveMinRow = Number.isFinite(minUsedRow) ? minUsedRow : 0;
                const effectiveMaxRow = Number.isFinite(maxUsedRow) ? maxUsedRow : (GRID_TRACKS - 1);

                const trimmedStartRow = Math.max(0, Math.min(effectiveMinRow, GRID_TRACKS - 1));
                const trimmedEndRow = Math.max(0, Math.min(effectiveMaxRow, GRID_TRACKS - 1));
                const rowOffset = trimmedStartRow;

                const gridTemplateRows = Array.from(
                  { length: Math.max(1, trimmedEndRow - trimmedStartRow + 1) },
                  (_, idx) => {
                    const rowIndex = trimmedStartRow + idx;
                    return usedRows.has(rowIndex) ? '1fr' : '0.5fr';
                  }
                ).join(' ');

                const gridTemplateColumns = Array.from(
                  { length: GRID_TRACKS },
                  (_, i) => (usedCols.has(i) ? '1fr' : '0.5fr')
                ).join(' ');

                return (
                  <SeatModeContainer>
                    <SeatModeScrollArea>
                      <SeatModeGridWrapper>
                        <SeatGridContent
                          style={{
                            gridTemplateColumns,
                            gridTemplateRows,
                          }}
                        >
                      {seatArrangementData.map((seatStudent: any, index: number) => {
                        const isActiveSeatStudent = isStudentActive(seatStudent.studentId);
                        
                        // 180도 회전 좌표 계산 (PresentationPage와 동일한 방식)
                        // 그리드가 16x16이고 템플릿이 역순이면, 인덱스 0은 15가 되고, 인덱스 n은 15-n이 됨
                        // 블럭이 2x2를 차지하므로 (seatRow, seatCol)에서 시작하는 블럭은 회전 후 (14-seatRow, 14-seatCol)에서 시작
                        // 0-based로 계산 후 1-based로 변환
                        const rotatedRow = isSeatRotated 
                          ? 14 - seatStudent.seatRow
                          : seatStudent.seatRow;
                        const rotatedCol = isSeatRotated 
                          ? 14 - seatStudent.seatCol
                          : seatStudent.seatCol;
                        
                        // gridRow/gridColumn은 1-based이므로 +1
                        const rotatedGridRow = (rotatedRow - rowOffset) + 1;
                        const rotatedGridCol = rotatedCol + 1;
                        
                        // 디버깅: 첫 번째와 마지막 블록만 로그 출력
                        if (isSeatRotated && (index === 0 || index === seatArrangementData.length - 1)) {
                          const originalGridRow = seatStudent.seatRow + 1; // 원본 (1-based)
                          const originalGridCol = seatStudent.seatCol + 1; // 원본 (1-based)
                          
                          console.log(`🔍 [블록 ${index}] ${seatStudent.name || seatStudent.studentId}:`);
                          console.log(`  - 원본 좌표 (0-based): seatRow=${seatStudent.seatRow}, seatCol=${seatStudent.seatCol}`);
                          console.log(`  - 원본 gridRow=${originalGridRow}, gridCol=${originalGridCol} (1-based)`);
                          console.log(`  - 회전 계산 (0-based): rotatedRow=${rotatedRow}, rotatedCol=${rotatedCol}`);
                          console.log(`  - 회전 gridRow=${rotatedGridRow}, gridCol=${rotatedGridCol} (1-based)`);
                          console.log(`  - 실제 적용: gridRow=${rotatedGridRow} / span 2, gridCol=${rotatedGridCol} / span 2`);
                          console.log(`  - PresentationPage 방식: 14 - ${seatStudent.seatRow} = ${rotatedRow} (0-based) => ${rotatedGridRow} (1-based)`);
                        }
                        
                        return (
                        <SeatStudentBlockWrapper
                          key={seatStudent.studentId}
                          $isActive={isActiveSeatStudent}
                          $isSelected={selectedStudents.has(seatStudent.studentId)}
                          style={{
                            gridRow: `${rotatedGridRow} / span 2`,
                            gridColumn: `${rotatedGridCol} / span 2`,
                          }}
                          onClick={() => handleStudentSelection(seatStudent.studentId)}
                        >
                          {(() => {
                            const rawResponse = displayedResponses[seatStudent.studentId];
                            const isArray = Array.isArray(rawResponse);
                            const responseArray = isArray ? (rawResponse as StudentQuizResponse[]) : [];
                            const currentIndex = studentAnswerIndices[seatStudent.studentId] ?? (Array.isArray(responseArray) ? responseArray.length - 1 : 0);

                            let currentResponse: StudentQuizResponse | null = null;

                            if (isArray && responseArray.length > 0) {
                              const validIndex = Math.max(0, Math.min(currentIndex, responseArray.length - 1));
                              currentResponse = responseArray[validIndex] || null;
                            } else if (!isArray && rawResponse) {
                              currentResponse = rawResponse as StudentQuizResponse;
                            }

                            const isSeatConnected = isChatOpen ? connectedStudentIds.includes(seatStudent.studentId) : true;
                            const hasStoredResponse = isArray ? responseArray.length > 0 : !!rawResponse;
                            const isSeatWaitingForResponse = shouldShowMemberQuizTypingWait({
                              isQuizPlaying,
                              isConnected: isSeatConnected,
                              isStudentCreateMode,
                              isSetMode,
                              isMultiAnswerMode,
                              activeQuizType: activeLiveQuizForCoordinatorUi?.type,
                              rawResponse: rawResponse,
                            });
                            const hasResponseData = hasStoredResponse || isSeatWaitingForResponse;
                            const removalIndex = isArray && responseArray.length > 0
                              ? Math.max(0, Math.min(currentIndex, responseArray.length - 1))
                              : undefined;

                            return (
                              <>
                                <StudentResetButton
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handleResetStudentResponse(seatStudent.studentId, removalIndex);
                                  }}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  disabled={!hasResponseData}
                                  title="멤버 응답 삭제"
                                  aria-label={`${seatStudent.name || '멤버'} 응답 삭제`}
                                >
                                  <TrashIcon viewBox="0 0 24 24" strokeWidth="2">
                                    <path d="M3 6h18" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M10 11v6" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M14 11v6" strokeLinecap="round" strokeLinejoin="round" />
                                  </TrashIcon>
                                </StudentResetButton>
                                <MessageBubble>
                                  {isStudentCreateMode ? (
                                    <MessageContent $isWaiting={false}>
                                      {studentCreateSubmittedIds.has(seatStudent.studentId) ? (
                                        <StudentCreateSubmittedBlock>
                                          <StudentCreateSubmittedIcon viewBox="0 0 24 24" aria-hidden="true">
                                            <circle cx="12" cy="12" r="9" />
                                            <path d="M9 12l2 2 4-4" />
                                          </StudentCreateSubmittedIcon>
                                          <StudentCreateSubmittedLabel>제출완료</StudentCreateSubmittedLabel>
                                        </StudentCreateSubmittedBlock>
                                      ) : (
                                        <MessageText>{'\u00A0'}</MessageText>
                                      )}
                                    </MessageContent>
                                  ) : (() => {
                                    const selectedSetForSeat =
                                      isSetMode && selectedSetIndex >= 0 && selectedSetIndex < quizSets.length
                                        ? quizSets[selectedSetIndex]
                                        : null;
                                    
                                    return isSetMode && selectedSetForSeat ? (
                                      (() => {
                                      const totalSetQuizzesForSeat = selectedSetForSeat ? Object.keys(selectedSetForSeat.quizzes || {}).length : 0;
                                      const correctSeat = setProgress[seatStudent.studentId]?.correct ?? 0;
                                      const essaySeat = setProgress[seatStudent.studentId]?.essay ?? 0;
                                      const answeredSeat = setProgress[seatStudent.studentId]?.answered ?? 0;
                                      const totalSeat = setProgress[seatStudent.studentId]?.total ?? totalSetQuizzesForSeat;
                                      const total = totalSeat || 0;
                                      const pctAnswered = total > 0 ? Math.min(100, (answeredSeat / total) * 100) : 0;
                                      const pctCorrect = total > 0 ? Math.min(100, (correctSeat / total) * 100) : 0;
                                      const pctEssay = total > 0 ? Math.min(100, (essaySeat / total) * 100) : 0;
                                      return (
                                        <>
                                          <QuizSetBarWrap>
                                            <QuizSetBar>
                                              {pctAnswered > 0 && <QuizSetBarResponse $widthPct={pctAnswered} $leftPct={0} />}
                                              {pctCorrect > 0 && <QuizSetBarCorrect $widthPct={pctCorrect} $leftPct={0} />}
                                              {pctEssay > 0 && <QuizSetBarEssay $widthPct={pctEssay} $leftPct={pctCorrect} />}
                                            </QuizSetBar>
                                            <QuizSetBarTextRow>
                                              <QuizSetTextCorrect>{correctSeat}</QuizSetTextCorrect>
                                              {essaySeat > 0 && <QuizSetTextEssay>+{essaySeat}</QuizSetTextEssay>}
                                              <QuizSetTextAnswered> / {answeredSeat}</QuizSetTextAnswered>
                                              <QuizSetTextTotal> / {total}</QuizSetTextTotal>
                                            </QuizSetBarTextRow>
                                          </QuizSetBarWrap>
                                        </>
                                      );
                                    })()
                                    ) : (
                                      <>
                                  {(() => {
                                    if (!currentResponse) {
                                      return null;
                                    }

                                    const hasSubmittedAt = !!currentResponse.submittedAt;
                                    const hasAnswer =
                                      currentResponse.answer !== undefined &&
                                      currentResponse.answer !== null &&
                                      (typeof currentResponse.answer === 'string'
                                        ? currentResponse.answer.trim().length > 0
                                        : Array.isArray(currentResponse.answer)
                                          ? currentResponse.answer.length > 0
                                          : true);

                                    if (hasSubmittedAt || hasAnswer) {
                                      if (typeof currentResponse.answer === 'string') {
                                        const answerText = currentResponse.answer.trim();
                                        if (answerText) {
                                          return (
                                          <MessageContent $isWaiting={isSeatWaitingForResponse}>
                                            <MessageText>{answerText}</MessageText>
                                          </MessageContent>
                                          );
                                        }
                                      } else if (Array.isArray(currentResponse.answer) && currentResponse.answer.length > 0) {
                                        const sortedAnswers = [...currentResponse.answer].sort((a, b) => a - b);
                                        return (
                                          <MessageContent $isWaiting={isSeatWaitingForResponse}>
                                            <ChoiceBadgesContainer>
                                              {sortedAnswers.map((index) => {
                                                const levelIndex = index < 6 ? index + 1 : undefined;
                                                return (
                                                  <ChoiceBadge key={index} $levelIndex={levelIndex}>
                                                    {index + 1}
                                                  </ChoiceBadge>
                                                );
                                              })}
                                            </ChoiceBadgesContainer>
                                          </MessageContent>
                                        );
                                      }
                                    }

                                    return null;
                                  })()}

                                  {isSeatWaitingForResponse && (
                                    <TypingIndicator $isOverlay={true}>
                                      <span />
                                      <span />
                                      <span />
                                    </TypingIndicator>
                                  )}

                                  {isArray && responseArray.length > 1 && (
                                    <AnswerNavigationContainer>
                                      <AnswerNavigationButton
                                        disabled={currentIndex === 0}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          setStudentAnswerIndices((prev) => ({
                                            ...prev,
                                            [seatStudent.studentId]: Math.max(0, (prev[seatStudent.studentId] || 0) - 1)
                                          }));
                                        }}
                                        onMouseDown={(e) => e.stopPropagation()}
                                      >
                                        &lt;
                                      </AnswerNavigationButton>
                                      <AnswerNavigationText>
                                        {currentIndex + 1}/{responseArray.length}
                                      </AnswerNavigationText>
                                      <AnswerNavigationButton
                                        disabled={currentIndex >= responseArray.length - 1}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          setStudentAnswerIndices((prev) => ({
                                            ...prev,
                                            [seatStudent.studentId]: Math.min(
                                              responseArray.length - 1,
                                              (prev[seatStudent.studentId] || 0) + 1
                                            )
                                          }));
                                        }}
                                        onMouseDown={(e) => e.stopPropagation()}
                                      >
                                        &gt;
                                      </AnswerNavigationButton>
                                    </AnswerNavigationContainer>
                                  )}

                                  {(() => {
                                    if (!currentResponse || !currentResponse.submittedAt) {
                                      return null;
                                    }

                                    const hasAnswer =
                                      currentResponse.answer !== undefined &&
                                      currentResponse.answer !== null &&
                                      (typeof currentResponse.answer === 'string'
                                        ? currentResponse.answer.trim().length > 0
                                        : Array.isArray(currentResponse.answer)
                                          ? currentResponse.answer.length > 0
                                          : true);
                                    const badgeStatus =
                                      currentResponse.isCorrect === true
                                        ? 'correct'
                                        : currentResponse.isCorrect === false
                                          ? 'wrong'
                                          : hasAnswer
                                            ? 'neutral'
                                            : 'wrong';

                                    return (
                                      <ResultBadge $status={badgeStatus}>
                                        {badgeStatus === 'correct' ? (
                                          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                                            <circle cx="12" cy="12" r="9" stroke="currentColor" fill="none" />
                                          </svg>
                                        ) : badgeStatus === 'wrong' ? (
                                          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                                            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                          </svg>
                                        ) : (
                                          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                                            <circle cx="12" cy="12" r="9" stroke="currentColor" fill="none" />
                                          </svg>
                                        )}
                                      </ResultBadge>
                                    );
                                      })()}
                                      </>
                                    );
                                  })()}
                                </MessageBubble>

                                <ProfileImageContainer>
                                  <ProfileTextContainer>
                                    <ProfileNumber>
                                      {seatStudent.number || ''}
                                    </ProfileNumber>
                                    <ProfileName>{seatStudent.name || ''}</ProfileName>
                                  </ProfileTextContainer>
                                  <ProfileImageWrapper>
                                    <CachedProfileImage
                                      imageId={resolveMemberProfileSource(seatStudent, memberProfileDisplayMode)}
                                      alt={seatStudent.name || '프로필'}
                                    />
                                    {selectedStudents.has(seatStudent.studentId) && (
                                      <SelectionIndicator>
                                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                          <circle cx="16" cy="16" r="16" fill="var(--color-accent-strong)"/>
                                          <path d="M10 16L14 20L22 12" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                      </SelectionIndicator>
                                    )}
                                  </ProfileImageWrapper>
                                </ProfileImageContainer>
                              </>
                            );
                          })()}
                        </SeatStudentBlockWrapper>
                        );
                      })}
                        </SeatGridContent>
                      </SeatModeGridWrapper>
                    </SeatModeScrollArea>
                    <RotateButtonContainer>
                      <RotateButton onClick={() => setIsSeatRotated(!isSeatRotated)} title="180도 회전">
                        <RotateIconWrapper>
                          <RotateArrowIcon>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M12 4v16M12 4l-4 4M12 4l4 4M12 20l-4-4M12 20l4-4" />
                            </svg>
                          </RotateArrowIcon>
                          <RotateNumberIcon>180</RotateNumberIcon>
                        </RotateIconWrapper>
                      </RotateButton>
                    </RotateButtonContainer>
                    {guestSection}
                  </SeatModeContainer>
                );
              })()
            ) : (
              <SeatModeContainer>
                <div style={{ color: '#666', fontSize: '14px', padding: '20px' }}>자리배치를 먼저 해주세요</div>
                {guestSection}
              </SeatModeContainer>
            )}
          </>
        )}
      </Container>
      {isPresentationModalOpen && (
        <PresentationModalOverlay>
          <PresentationModalContainer role="dialog" aria-modal="true">
            <PresentationModalTitle>
              {isSetMode ? '문제 세트 프레젠테이션 옵션' : '프레젠테이션 옵션'}
            </PresentationModalTitle>

            {isSetMode ? (
              <>
                <PresentationOptionRow>
                  <PresentationOptionLabel>이름 표시</PresentationOptionLabel>
                  <PresentationToggleGroup
                    $isActive={presentationOptions.isNameVisible}
                    onClick={() =>
                      setPresentationOptions((prev) => ({ ...prev, isNameVisible: !prev.isNameVisible }))
                    }
                  >
                    <PresentationToggleButton $isActive={!presentationOptions.isNameVisible}>숨김</PresentationToggleButton>
                    <PresentationToggleButton $isActive={presentationOptions.isNameVisible}>표시</PresentationToggleButton>
                  </PresentationToggleGroup>
                </PresentationOptionRow>

                <PresentationOptionRow>
                  <PresentationOptionLabel>정답률 표시</PresentationOptionLabel>
                  <PresentationToggleGroup
                    $isActive={presentationOptions.showCorrectRate}
                    onClick={() =>
                      setPresentationOptions((prev) => ({ ...prev, showCorrectRate: !prev.showCorrectRate }))
                    }
                  >
                    <PresentationToggleButton $isActive={!presentationOptions.showCorrectRate}>숨김</PresentationToggleButton>
                    <PresentationToggleButton $isActive={presentationOptions.showCorrectRate}>표시</PresentationToggleButton>
                  </PresentationToggleGroup>
                </PresentationOptionRow>
                <PresentationOptionRow>
                  <PresentationOptionLabel>점수 표시</PresentationOptionLabel>
                  <PresentationToggleGroup
                    $isActive={presentationOptions.showScore}
                    onClick={() =>
                      setPresentationOptions((prev) => ({ ...prev, showScore: !prev.showScore }))
                    }
                  >
                    <PresentationToggleButton $isActive={!presentationOptions.showScore}>숨김</PresentationToggleButton>
                    <PresentationToggleButton $isActive={presentationOptions.showScore}>표시</PresentationToggleButton>
                  </PresentationToggleGroup>
                </PresentationOptionRow>
              </>
            ) : (
              <>
            <PresentationOptionRow>
              <PresentationOptionLabel>응답 표시</PresentationOptionLabel>
              <PresentationToggleGroup
                $isActive={presentationOptions.isResponsePublic}
                onClick={() =>
                  setPresentationOptions((prev) => ({ ...prev, isResponsePublic: !prev.isResponsePublic }))
                }
              >
                    <PresentationToggleButton $isActive={!presentationOptions.isResponsePublic}>숨김</PresentationToggleButton>
                    <PresentationToggleButton $isActive={presentationOptions.isResponsePublic}>표시</PresentationToggleButton>
              </PresentationToggleGroup>
            </PresentationOptionRow>
            <PresentationOptionRow>
              <PresentationOptionLabel>이름 표시</PresentationOptionLabel>
              <PresentationToggleGroup
                $isActive={presentationOptions.isNameVisible}
                onClick={() =>
                  setPresentationOptions((prev) => ({ ...prev, isNameVisible: !prev.isNameVisible }))
                }
              >
                    <PresentationToggleButton $isActive={!presentationOptions.isNameVisible}>숨김</PresentationToggleButton>
                    <PresentationToggleButton $isActive={presentationOptions.isNameVisible}>표시</PresentationToggleButton>
              </PresentationToggleGroup>
            </PresentationOptionRow>
          <PresentationOptionRow>
            <PresentationOptionLabel>정답 표시</PresentationOptionLabel>
            <PresentationToggleGroup
              $isActive={presentationOptions.isAnswerVisible}
              onClick={() =>
                setPresentationOptions((prev) => ({ ...prev, isAnswerVisible: !prev.isAnswerVisible }))
              }
            >
                    <PresentationToggleButton $isActive={!presentationOptions.isAnswerVisible}>숨김</PresentationToggleButton>
                    <PresentationToggleButton $isActive={presentationOptions.isAnswerVisible}>표시</PresentationToggleButton>
            </PresentationToggleGroup>
          </PresentationOptionRow>
              </>
            )}

          <PresentationModalActions>
            <PresentationSecondaryButton data-variant="modal-secondary" type="button" onClick={handleClosePresentationModal}>
                취소
              </PresentationSecondaryButton>
              <PresentationPrimaryButton type="button" onClick={handleStartPresentation}>
                프레젠테이션 하기
              </PresentationPrimaryButton>
            </PresentationModalActions>
          </PresentationModalContainer>
        </PresentationModalOverlay>
      )}
      
      {/* 토스트 알림 */}
      {showToast && (
        <ToastOverlay>
          <ToastMessage>
            {toastMessage}
          </ToastMessage>
        </ToastOverlay>
      )}
      
      {/* 문제카드 세트 모달 닫기 확인 팝업 */}
      <ConfirmModal
        isOpen={isCloseSetModalConfirmOpen}
        onClose={handleCancelCloseSetModal}
        onConfirm={handleConfirmCloseSetModal}
        title={['변경된 데이터가 있습니다.', '팝업을 닫으시겠습니까?']}
        confirmText="닫기"
        cancelText="취소"
        icon="!"
        iconBackground="var(--status-pending)"
      />
      
      {/* 문제카드 생성/수정 모달 닫기 확인 팝업 */}
      <ConfirmModal
        isOpen={isCloseQuizModalConfirmOpen}
        onClose={handleCancelCloseQuizModal}
        onConfirm={handleConfirmCloseQuizModal}
        title={['변경된 데이터가 있습니다.', '팝업을 닫으시겠습니까?']}
        confirmText="닫기"
        cancelText="취소"
        icon="!"
        iconBackground="var(--status-pending)"
      />
      
      {/* 라이센스 제한 모달 */}
      <LicenseLimitModal
        isOpen={showLicenseLimitModal}
        message={licenseLimitMessage}
        onClose={() => setShowLicenseLimitModal(false)}
        onUpgrade={() => {
          setShowLicenseLimitModal(false);
          onLicenseClick?.();
        }}
      />
    </>
  );
};

export default ChatPage;

// 토스트 알림 스타일
const ToastOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.2s ease-in-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ToastMessage = styled.div`
  background: #ffffff !important;
  color: #000000 !important;
  padding: 20px 32px;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  box-shadow: var(--modal-shadow-large);
  animation: slideUp 0.3s ease-out;
  
  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

